/**
 * XR Lesson Player V3 - Minimal Immersive WebXR Implementation
 * 
 * STEP 1: Just load the skybox GLB in immersive mode
 * 
 * Key differences from VRLessonPlayer:
 * - Uses WebXR API for true immersive-vr mode on Quest
 * - renderer.xr.enabled = true
 * - navigator.xr.requestSession('immersive-vr')
 * 
 * Key differences from XRLessonPlayerV2:
 * - Simplified - only skybox for now
 * - No three-mesh-ui (avoiding font issues)
 * - Minimal dependencies
 * 
 * Skybox source: stored_glb_url from skyboxes collection
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { extractMcqOptions, resolveCorrectAnswerIndex } from '../lib/mcq/answerIndex';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { DRACO_DECODER_PATH } from '../lib/three/dracoDecoder';
import { VRButton } from 'three/examples/jsm/webxr/VRButton.js';
import { XRControllerModelFactory } from 'three/examples/jsm/webxr/XRControllerModelFactory.js';
// Hands come from the bundled three, not a CDN: a second Three.js instance would
// not recognise objects made by this one. VRButton already asks for
// 'hand-tracking' as an optional feature, so nothing else has to change to get
// hands, and controller-only headsets are unaffected.
import { OculusHandModel } from 'three/examples/jsm/webxr/OculusHandModel.js';
// three's own hand pointer: the tapered ray and cursor Quest users know from
// system UI, with pinch-to-select built in. Replaces a hand-rolled line, and is
// the reason there is now exactly one pointer per hand.
import { OculusHandPointerModel } from 'three/examples/jsm/webxr/OculusHandPointerModel.js';
import {
  createHandGestureTracker,
  type HandGestureState,
  type HandGestureTracker,
} from '../lib/three/handGestures';
import { ProfessionalLayoutSystem, PlacedAsset } from '../utils/webxr/professionalLayoutSystem';
import { VRLessonExperience } from '../utils/webxr/vrLessonExperience';
import { StableLayoutSystem } from '../utils/webxr/stableLayoutSystem';
import { SceneLayoutSystem, PlacementStrategy, AssetPlacement } from '../utils/webxr/sceneLayoutSystem';
import { db } from '../config/firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { ArrowLeft, Loader2, AlertTriangle, Glasses, Award, Home, Play, Hand, SkipForward, LogOut } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { useEnforcedPlayerRoute } from '../hooks/useEnforcedPlayerRoute';
import { useComfortBreak } from '../hooks/useComfortBreak';
import { type ReportSessionQuizPayload } from '../services/classSessionService';
// Scores have to land in student_scores as well as on the class session: the
// session progress doc is live teaching data and is thrown away with the class,
// while student_scores is the durable record the dashboards and reports read.
import {
  saveQuizScore,
  lastQuizScoreRefusal,
  trackLessonLaunch,
  updateLessonLaunch,
} from '../services/lessonTrackingService';

// Live-class support. The classroom logic is shared with the krpano player so the
// two cannot drift; this player only supplies its phase vocabulary and camera I/O.
import { useClassroomSession, type ClassroomViewAdapter } from '../hooks/useClassroomSession';
// The lesson panel is drawn by the same renderer the krpano player uses, so the
// two players show an identical UI rather than two hand-maintained versions.
import {
  actionAtUv,
  drawLessonPanel,
  ensureLessonPanelFont,
  parseLessonUiAction,
  EMPTY_LESSON_UI_STATE,
  PANEL_H,
  PANEL_W,
} from '../lib/lessonUi';
import type { ButtonRegion, LessonUiState } from '../lib/lessonUi';
import { createLookControls, type LookControls } from '../lib/three/lookControls';
// A headset draws two eyes on a mobile GPU; the flat view draws one on a desktop
// GPU. Same scene, very different budget — see lib/three/renderBudget.
import {
  applyRenderBudget,
  requestShadowRefresh,
  FLAT_BUDGET,
  IMMERSIVE_BUDGET,
} from '../lib/three/renderBudget';
// In a headset the device owns the camera pose, so a teacher's Direct has to
// move the reference space instead of the camera.
import { faceXrViewerTowards, resetXrReorientation } from '../lib/three/xrReorient';
// Teleport rather than smooth steering: the comfort-safe default, with a
// vignette across the cut. Also moves the reference space rather than the scene
// graph, so asset placement cannot be disturbed.
import { createTeleport, resetTeleport, type TeleportController } from '../lib/three/teleport';
// Explode / isolate / section, lifted out of the krpano plugin so both players
// share one implementation. This is also what makes "label the part" possible.
import {
  createModelTools,
  DEFAULT_HIGHLIGHT_COLOR,
  MAX_HIGHLIGHTED_PARTS,
  type ClipAxis,
  type ModelSummary,
  type ModelTools,
  type PickedPart,
} from '../lib/three/modelTools';
// Teacher part highlight: the words a part is called by, and the floating tags
// that carry them to every viewer, headset included.
import { resolvePartLabel } from '../lib/three/partLabels';
import { createPartLabelLayer, type PartLabel, type PartLabelLayer } from '../lib/three/partHighlightLayer';
// Desk and panel heights derived from the viewer's own eye height, so a
// seated student and a standing teacher each get a layout that fits them.
import { layoutForViewer, DEFAULT_EYE_HEIGHT, type ViewerLayout } from '../lib/three/ergonomics';
// One anchor for the dock, the models and the panel, so they finally agree with
// each other about which way the viewer is facing.
import {
  captureAnchor,
  flatPanelLayout,
  type WorkspaceAnchor,
  type WorkspaceFrame,
} from '../lib/three/workspace';
// The room around the desk, themed by subject and grade.
import { classNumberOf, planLessonRoom, themeFor } from '../lib/three/roomTheme';
import { placeRoomProps } from '../lib/three/propMeshes';
// The authored layout (preset, sizes, main model) and overlap-free placement.
import {
  DEFAULT_LESSON_LAYOUT,
  panelSideOf,
  planWorkspace,
  resolveLessonLayout,
  type LessonLayout,
  type PlannedFrame,
} from '../lib/three/lessonLayout';
// Moving and resizing the lesson panel, remembered per viewer relative to the
// anchor so the placement survives a recentre and the next session.
import {
  beginMove,
  beginResize,
  loadPref,
  poseFromPref,
  prefFromPose,
  savePref,
  updateMove,
  updateResize,
  type MoveDrag,
  type PrefStorage,
  type ResizeDrag,
  type ViewMode,
} from '../lib/three/panelPlacement';

/**
 * Two presses on the grab bar this close together put the panel back where the
 * workspace frame wants it. Long enough for a deliberate double tap with a
 * trigger or a pinch; short enough that grabbing, moving and grabbing again to
 * adjust never reads as one.
 */
const PANEL_RESET_DOUBLE_PRESS_MS = 400;

/** A move or resize of the lesson panel in progress. */
interface PanelDrag {
  /**
   * Which input started it — and so the only one that may steer or end it. A
   * second controller, or the other hand, pointing at the panel mid-drag must not
   * take it over or drop it.
   */
  source: 'controller' | 'hand' | 'pointer';
  /** The controller group, the hand slot index, or the DOM pointerId. */
  owner: THREE.Object3D | number;
  /** A headset move: the grab point rides the ray at the distance it was grabbed. */
  move?: MoveDrag;
  /**
   * A flat-screen move: across a plane through the grab point facing the camera,
   * the same rule a mouse drag of a model uses, so the two feel alike.
   */
  plane?: { plane: THREE.Plane; offset: THREE.Vector3 };
  resize?: ResizeDrag;
  /**
   * The view the drag began in, and so the preference it saves to. Read at the
   * press rather than the release: a headset drag still held when the session
   * ends is released in flat mode, and saving its headset pose as the FLAT
   * placement would park the laptop panel off-screen on every reload.
   */
  mode: ViewMode;
  /** Where the panel was at the press, so a press that never moved saves nothing. */
  startPosition: THREE.Vector3;
  startScale: number;
}

/**
 * localStorage, or null where even touching it throws (blocked site data, some
 * sandboxed frames). loadPref/savePref already swallow errors from the calls
 * themselves; this covers the property access they are handed.
 */
function panelPrefStorage(preset: LessonLayout['preset'] = 'lecture'): PrefStorage | null {
  let storage: Storage | null;
  try {
    storage = typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
  // Lecture keeps the original keys, so placements saved before layouts existed
  // still apply. Other presets remember their own: a panel dragged clear of a
  // lab bench on the right means nothing in a room with the panel on the left.
  if (!storage || preset === 'lecture') return storage;
  const s = storage;
  return {
    getItem: (key: string) => s.getItem(`${key}:${preset}`),
    setItem: (key: string, value: string) => s.setItem(`${key}:${preset}`, value),
    removeItem: (key: string) => s.removeItem(`${key}:${preset}`),
  } as PrefStorage;
}

/**
 * The scale note at the front edge of the desk, or none.
 *
 * A lesson set to true scale says so; when the readable floor had to enlarge a
 * small model, it says the sizes are not to scale instead, so a class never
 * takes a lifted size as the real ratio.
 */
function placeScaleNote(scene: THREE.Scene, frame: PlannedFrame): void {
  const existing = scene.getObjectByName('scaleNote') as THREE.Sprite | undefined;
  if (existing) {
    scene.remove(existing);
    existing.material.map?.dispose();
    existing.material.dispose();
  }
  if (!frame.layout.realScale || frame.slots.length < 2) return;
  const text = frame.notToScale ? 'Sizes not to scale' : 'Shown to true scale';
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 96;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = 'rgba(10, 25, 41, 0.85)';
  ctx.beginPath();
  ctx.roundRect(4, 4, 504, 88, 44);
  ctx.fill();
  ctx.fillStyle = frame.notToScale ? '#fbbf24' : '#5eead4';
  ctx.font = '600 40px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 256, 50);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, toneMapped: false }));
  sprite.name = 'scaleNote';
  sprite.scale.set(0.24, 0.045, 1);
  // Front edge of the desk, just above it: under the models, never in front of them.
  const toViewer = frame.anchor.position.clone().sub(frame.dock.center).setY(0).normalize();
  sprite.position.copy(frame.dock.center).addScaledVector(toViewer, frame.dock.depth / 2 + 0.02);
  sprite.position.y = frame.deskY + 0.03;
  scene.add(sprite);
}

/** A controller's pointing ray in world space, unit length. */
function controllerRay(controller: THREE.Object3D): THREE.Ray {
  const rotation = new THREE.Matrix4().extractRotation(controller.matrixWorld);
  return new THREE.Ray(
    new THREE.Vector3().setFromMatrixPosition(controller.matrixWorld),
    new THREE.Vector3(0, 0, -1).applyMatrix4(rotation).normalize()
  );
}

/** A fist this soon after the same hand's pinch began was a fist all along (see updateHands). */
const FIST_BEATS_PINCH_MS = 350;
/** How near the two fists a model has to be for pulling them apart to explode it, in metres. */
const FIST_REACH_M = 0.6;
/** How near a controller a model has to be for a grip that points at nothing to take it. */
const GRIP_REACH_M = 0.6;
/** Radians per second a held model turns with its controller's stick fully over. */
const HELD_TURN_RATE = 3;
/** Size change per second (as a log ratio) of a held model with the stick fully over. */
const HELD_SCALE_RATE = 1.2;
/** Radians per pixel of a Shift+drag on a flat screen. */
const SHIFT_DRAG_TURN_PER_PX = 0.01;
/** Size change (as a log ratio) per pixel of wheel travel over a model. */
const WHEEL_SCALE_PER_PX = 0.0015;

/**
 * Scratch for the per-frame gesture code: the loop runs 72-90 times a second
 * in a headset, and a fresh Vector3 per frame is garbage the collector
 * eventually stops a frame to clear — a visible hitch in a headset.
 */
const gestureScratch = {
  a: new THREE.Vector3(),
  b: new THREE.Vector3(),
  mid: new THREE.Vector3(),
  box: new THREE.Box3(),
  stick0: { x: 0, y: 0 },
  stick1: { x: 0, y: 0 },
};

/**
 * What every model gesture can do to one asset, by key. Each input — grips and
 * sticks, hands, fists, touch, the wheel, Shift+drag and the My view popover —
 * goes through these, so they all keep the same limits and the same
 * teacher/student rules. Built in the component; see modelGesturesRef.
 */
interface ModelGestures {
  keyOf(object: THREE.Object3D | null | undefined): string | null;
  /** The model a ray points at, or null. */
  keyAlongRay(ray: THREE.Ray): string | null;
  /** The model nearest `point` within `maxDistance` metres, or null. */
  nearestKey(point: THREE.Vector3, maxDistance: number): string | null;
  /** Multiplier on the dock scale; 1 when never resized. */
  scaleOf(key: string): number;
  /** Radians past the dock's facing; 0 when never turned. */
  yawOf(key: string): number;
  explodeOf(key: string): number;
  setScale(key: string, multiplier: number): void;
  setYaw(key: string, yaw: number): void;
  setExplode(key: string, amount: number): void;
}

const NO_MODEL_GESTURES: ModelGestures = {
  keyOf: () => null,
  keyAlongRay: () => null,
  nearestKey: () => null,
  scaleOf: () => 1,
  yawOf: () => 0,
  explodeOf: () => 0,
  setScale: () => {},
  setYaw: () => {},
  setExplode: () => {},
};
import { groundedOffset } from '../lib/three/groundedOffset';
// Releasing one topic's content so the next can replace it without the renderer,
// and therefore without the XR session, being torn down.
import { disposeLessonContent, disposeObject3D } from '../lib/lesson/lessonSwap';
import { attachAssetsGroup, discardAssetsGroup } from '../lib/three/attachAssetsGroup';
// The same builder ClassLaunchRouter uses, so a teacher advancing their class is
// served exactly like a student rather than not at all.
import { buildActiveLesson, type ActiveLessonImage } from '../lib/lesson/buildActiveLesson';
import { chapterGroupOf, sequenceTopics } from '../lib/lesson/chapterSequence';
import { subscribeSessionResults } from '../services/performanceService';
import { launchLesson } from '../services/classSessionService';
import {
  mergeLessonAssets,
  unresolvedAssetIds,
  type ResolvedAssetDoc,
} from '../lib/lesson/mergeLessonAssets';
import { shouldLoadAssets, shouldRetryDeferredLoad } from '../lib/lesson/assetLoadGate';
import { cameraRotationToHV } from '../lib/classroom/viewSync';
import { evaluateQuizGate } from '../lib/classroom/quizGate';
import {
  changedTeacherFields,
  clearForTeacher,
  emptyLocalOverrides,
  scopedActiveImageId,
  type LocalModelOverrides,
  type TeacherStateView,
} from '../lib/classroom/modelPrecedence';
// A viewer's own model gestures: shrink, expand, turn and explode their copy.
import {
  beginTwoPoint,
  sampleFromPoints,
  updateTwoPoint,
  STUDENT_SCALE_LIMITS,
  type TwoPointSample,
  type TwoPointState,
} from '../lib/three/twoPointGesture';
import {
  beginExplode,
  stickExplodeStep,
  updateExplode,
  type ExplodeGestureState,
} from '../lib/three/explodeGesture';
import {
  applyAssetLocalScale,
  applyAssetLocalYaw,
  applyAssetOverrides,
  readStick,
} from '../lib/three/localModelTransform';
import { resolveStudentDisplayName } from '../utils/displayName';
import { createNarrationController, type NarrationController } from '../lib/lesson/narration';
// The teacher marker draws real geometry rather than an SVG overlay, so a
// student wearing a headset sees the ink too.
import { createInkLayer, type InkLayer } from '../lib/annotations/inkLayer';
import {
  annotationNow,
  capPoints,
  MAX_INK_STROKES,
  simplifyStroke,
  STROKE_TTL_MS,
} from '../lib/annotations/sphereGeometry';
import { MARKER_COLORS } from '../Components/player/MarkerToolbar';
import { appendStroke, publishAnnotations } from '../services/classSessionService';
import type {
  AnnotationModelMark,
  AnnotationPoint,
  AnnotationStroke,
  LaunchedLesson,
  SessionLessonPhase,
  TeacherAnnotations,
  TeacherContentState,
} from '../types/lms';
import { LiveClassHostOverlay } from '../Components/classSession/LiveClassHostOverlay';
import { PlayerChrome } from '../Components/player/PlayerChrome';
import { LessonImageViewer } from '../Components/player/LessonImagePanel';
import { onLessonImageReady } from '../lib/lessonUi/imageCache';
import { PlayerTopBar } from '../Components/player/PlayerTopBar';
import { TopicPicker } from '../Components/player/TopicPicker';
import { PlayerBottomBar } from '../Components/player/PlayerBottomBar';
import { ForceAdvanceDialog } from '../Components/player/ForceAdvanceDialog';
import { usePlayerViewport } from '../hooks/usePlayerViewport';

// WebXR Utilities
import {
  LayoutEngine,
  createLayoutEngine,
  DEBUG_CATEGORIES,
} from '../utils/webxr';

// ============================================================================
// Types
// ============================================================================

interface LessonData {
  chapter: {
    chapter_id: string;
    chapter_name: string;
    curriculum: string;
    class_name: string;
    subject: string;
    mcq_ids?: string[];
    tts_ids?: string[];
    meshy_asset_ids?: string[];
  };
  topic: {
    topic_id: string;
    topic_name: string;
    skybox_id?: string | number;
    skybox_remix_id?: string | number;
    skybox_url?: string;
    skybox_glb_url?: string;
    asset_urls?: string[];
    meshy_asset_ids?: string[];
    mcq_ids?: string[];
    tts_ids?: string[];
    avatar_intro?: string;
    avatar_explanation?: string;
    avatar_outro?: string;
  };
  image3dasset?: string | null;
}

interface TTSData {
  id: string;
  section: string;
  audioUrl: string;
  text?: string;
}

interface MCQData {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation?: string;
}

interface MeshyAsset {
  id: string;
  glbUrl: string;
  name?: string;
  thumbnailUrl?: string;
}

type LoadingState = 'loading' | 'ready' | 'error' | 'no-vr' | 'in-vr';
/**
 * The lesson phase vocabulary is now the SAME one the session, Firestore and
 * the krpano player use. This player used to have its own names (waiting /
 * content / mcq / complete) with an adapter translating on every read and
 * write; that translation layer is gone.
 */
type LessonPhase = Extract<
  SessionLessonPhase,
  'idle' | 'intro' | 'explanation' | 'outro' | 'quiz' | 'completed'
>;

// ============================================================================
// Error Boundary Component
// ============================================================================

class XRPlayerErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[XRLessonPlayerV3] Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-card rounded-lg border border-destructive/50 p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Something went wrong</h2>
            <p className="text-slate-400 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// Component
// ============================================================================

const XRLessonPlayerV3: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // profile carries the school/class context saveQuizScore attributes on.
  const { user, profile } = useAuth();

  // If the class was launched into the other player, move there rather than
  // splitting the class across two.
  useEnforcedPlayerRoute('xr_v3');

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const vrButtonRef = useRef<HTMLElement | null>(null);
  /**
   * Narration is owned by a controller, not a bare Audio element. See
   * lib/lesson/narration.ts: dropping an element with `pause(); ref = null` did
   * not reliably stop it, which is how intro and explanation ended up playing
   * over each other.
   */
  const narrationRef = useRef<NarrationController | null>(null);
  /** Pending "advance after a silent phase" timer, so it can be cancelled. */
  const phaseAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Read inside advancePhase, which must not re-create itself when quiz data loads. */
  const mcqDataRef = useRef<MCQData[]>([]);
  const assetsGroupRef = useRef<THREE.Group | null>(null);
  const primaryAssetRef = useRef<THREE.Group | null>(null);
  const groundPlaneRef = useRef<THREE.Mesh | null>(null);
  const teleportRef = useRef<TeleportController | null>(null);
  const modelToolsRef = useRef<ModelTools | null>(null);
  /**
   * What this viewer has done to their own copy of each model — explode, size
   * and turn, by asset key. Never published. Cleared by a topic swap, by the
   * teacher's Reset and by Reset my view; lib/classroom/modelPrecedence decides
   * which of it any other teacher publish overrides.
   */
  const localModelRef = useRef<LocalModelOverrides>(emptyLocalOverrides());
  /** The teacher state the student-apply effect last acted on, and the tools version it acted on. */
  const appliedTeacherStateRef = useRef<TeacherStateView | null>(null);
  const appliedToolsVersionRef = useRef(-1);
  /** Two-input model gestures in progress. Read and written per frame. */
  const modelGestureRef = useRef<{
    twoGrip: { key: string; state: TwoPointState } | null;
    twoHand: { key: string; state: TwoPointState } | null;
    fists: { key: string; state: ExplodeGestureState } | null;
  }>({ twoGrip: null, twoHand: null, fists: null });
  /* Re-pointed every render, for the same reason as selectPickedPartRef below:
     the input handlers that call these are installed long before they exist. */
  const modelGesturesRef = useRef<ModelGestures>(NO_MODEL_GESTURES);
  /** The model under a screen point, for the wheel; installed with the canvas handlers. */
  const modelKeyAtPointerRef = useRef<(event: MouseEvent) => string | null>(() => null);
  /*
    Set up long before the callback it points at exists.

    The canvas pointer handlers are installed inside the scene-setup effect,
    which runs above the model handlers in this file. Capturing the callback
    directly there would freeze the first version of it, closed over an empty
    asset list — the same stale-closure trap that once made the quiz
    unanswerable. The ref is re-pointed on every render instead.
  */
  const selectPickedPartRef = useRef<(key: string, partId: string, name: string) => void>(
    () => {}
  );
  /* Teacher highlight mode, read by the same early-installed pick handlers —
     a ref for the mode and a re-pointed ref for the toggle, as above. */
  const highlightModeRef = useRef(false);
  const toggleHighlightRef = useRef<(picked: PickedPart) => void>(() => {});
  /** Floating name tags over highlighted parts. Lives as long as the scene. */
  const partLabelsRef = useRef<PartLabelLayer | null>(null);
  /**
   * The tag text for each highlighted part, by part id. The host resolves it
   * from the mesh it picked and publishes it; students show the published text,
   * so everyone reads the same name even where their copies' naming differs.
   */
  const partLabelTextRef = useRef<Map<string, string>>(new Map());
  /**
   * Hang a tag over every part that is glowing, and only those.
   *
   * Driven from the tools' own list rather than the published one: the tools
   * drop ids past the cap and keep ids for models still loading, and a tag
   * over a part that is not glowing — or none over one that is — would
   * contradict the scene. Refs only, so the loader and the effects can call it.
   */
  const syncPartLabels = useCallback(() => {
    const layer = partLabelsRef.current;
    if (!layer) return;
    const tools = modelToolsRef.current;
    if (!tools) {
      layer.clear();
      return;
    }
    const labels: PartLabel[] = [];
    for (const { partId } of tools.highlighted()) {
      const anchor = tools.partAnchor(partId);
      if (!anchor) continue;
      // The number after '#' is the part's index in its model (modelTools ids).
      const index = Number(partId.slice(partId.lastIndexOf('#') + 1));
      const text = partLabelTextRef.current.get(partId) || resolvePartLabel(anchor.mesh, index);
      labels.push({ partId, text, mesh: anchor.mesh });
    }
    layer.set(labels);
  }, []);
  /** Read by the animation loop, which must not close over React state. */
  const modelExplodeRef = useRef(0);
  /* Called from the XR session handlers, which are installed before these
     callbacks exist — same reason as selectPickedPartRef above. */
  const applyViewerErgonomicsRef = useRef<() => void>(() => {});
  /** Where the workspace was built. Recaptured on recentre, never per frame. */
  const workspaceAnchorRef = useRef<WorkspaceAnchor | null>(null);
  // This lesson's resolved layout (preset, sizes, main model), read by frameFor
  // on every re-layout, and the viewer heights the room was last built for.
  const lessonLayoutRef = useRef<LessonLayout>(DEFAULT_LESSON_LAYOUT);
  // What the room around the desk is themed on (subject, grade) and seeded by (the lesson).
  const roomContextRef = useRef<{
    subject: string;
    classNumber: number | null;
    chapterId: string;
    topicId: string;
    topicIndex: number;
  }>({
    subject: '',
    classNumber: null,
    chapterId: '',
    topicId: '',
    topicIndex: 0,
  });
  const lastViewRef = useRef<ViewerLayout | null>(null);
  /** The lesson panel move or resize in progress, if any. Read per frame. */
  const panelDragRef = useRef<PanelDrag | null>(null);
  /** When the grab bar was last pressed, for the double-press reset. */
  const lastPanelGrabPressRef = useRef(0);
  /* Called from the asset loader, which sits above the layout callbacks in this
     file — the same forward-reference the other refs here solve. */
  const applyWorkspaceLayoutRef = useRef<() => void>(() => {});
  const restoreDeskHeightRef = useRef<() => void>(() => {});
  /** True while the thumbstick is pushed forward, so release can commit the aim. */
  const teleportArmedRef = useRef(false);
  
  // Professional Layout System - handles zones, collision, and placement
  const professionalLayoutRef = useRef<ProfessionalLayoutSystem | null>(null);
  const placedAssetsRef = useRef<PlacedAsset[]>([]);
  
  // VR Lesson Experience - world-class VR EdTech experience
  const vrExperienceRef = useRef<VRLessonExperience | null>(null);
  
  // Stable Layout System - crash-safe, deterministic asset staging
  const stableLayoutRef = useRef<StableLayoutSystem | null>(null);
  
  // Interaction guard - prevent runaway loops
  const interactionGuardRef = useRef<{
    lastInteractionTime: number;
    interactionCount: number;
    isProcessing: boolean;
  }>({ lastInteractionTime: 0, interactionCount: 0, isProcessing: false });
  
  // Ground plane constants
  const GROUND_LEVEL = 0;        // Y coordinate of the ground plane
  const TABLE_HEIGHT = 1.0;      // Height for placing objects (1m above ground)
  
  // Normalized asset sizing - ALL assets scaled to this size
  const NORMALIZED_SIZE = 1.0;   // All assets fit within 1.0m bounding box for better viewing
  
  // XR Controller refs
  const controller1Ref = useRef<THREE.Group | null>(null);
  const controller2Ref = useRef<THREE.Group | null>(null);
  const raycasterRef = useRef<THREE.Raycaster | null>(null);
  const controllerModelFactoryRef = useRef<XRControllerModelFactory | null>(null);
  const reticleRef = useRef<THREE.Mesh | null>(null);
  /**
   * Tracked hands, when the headset reports them.
   *
   * `grabProxy` is an empty Object3D parked at the pinch point each frame. The
   * layout system's startGrab/updateGrab measure from whatever object they are
   * given, so handing them the proxy makes an object follow the FINGERS rather
   * than the wrist — without touching the asset system itself.
   */
  const handsRef = useRef<
    Array<{
      hand: THREE.Group;
      tracker: HandGestureTracker;
      pointer: OculusHandPointerModel;
      /** The controller's own ray, hidden whenever this hand is tracked. */
      controllerRay: THREE.Object3D | null;
      grabProxy: THREE.Object3D;
      grabbing: boolean;
      /** When this hand's current pinch began, so a fist that follows can win. */
      pinchedAt: number;
      /** This frame's reading, for the two-hand gestures that need both hands. */
      last: HandGestureState | null;
    }>
  >([]);
  const hoveredObjectRef = useRef<THREE.Object3D | null>(null);
  const lastGrabTimeRef = useRef<Map<string, number>>(new Map());
  const controllersSetupRef = useRef<Set<number>>(new Set());
  const inputSourcesRef = useRef<(XRInputSource | null)[]>([null, null]); // Store input sources for haptic feedback
  
  // VR UI refs
  /**
   * The one lesson panel. Replaces the separate script / MCQ / start / class
   * meshes; the shared renderer draws all of those states onto this surface.
   */
  const lessonPanelRef = useRef<{
    mesh: THREE.Mesh;
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    texture: THREE.CanvasTexture;
    bornAt: number;
  } | null>(null);
  /** Clickable regions from the last draw, in 2048x1280 canvas space. */
  const lessonPanelRegionsRef = useRef<ButtonRegion[]>([]);
  /** Latest panel state, read by the draw call outside React's render. */
  const lessonUiStateRef = useRef<LessonUiState>(EMPTY_LESSON_UI_STATE);
  const lessonPanelFontRef = useRef<string | undefined>(undefined);
  const lessonPanelHoverRef = useRef<string | null>(null);
  /**
   * Panel click handler, reached through a ref.
   *
   * The two call sites (the canvas pointer handler and the VR controller ray)
   * are created inside the scene-init effect, which runs once when the skybox
   * resolves. Calling the useCallback directly from there captured the FIRST
   * version of it — closed over an empty mcqData — so every quiz tap hit the
   * `currentMcqIndex >= mcqData.length` guard and returned silently. That is
   * why the quiz looked unresponsive rather than hidden.
   */
  const lessonPanelUvRef = useRef<(u: number, v: number) => boolean>(() => false);
  /** When the learner actually began, for the score's time-taken field. */
  const lessonStartTimeRef = useRef<number | null>(null);
  /**
   * The lesson_launches record for this sitting.
   *
   * All three dashboards count launches from that collection, and this player
   * never wrote one — only krpano did. With this player as the default, every
   * "lessons launched" and "completed" figure was under-reporting.
   */
  const launchIdRef = useRef<string | null>(null);

  /** Teacher marker: the 3D ink layer, plus the stroke being drawn right now. */
  const inkLayerRef = useRef<InkLayer | null>(null);
  const activeStrokeRef = useRef<AnnotationStroke | null>(null);
  const markerActiveRef = useRef(false);
  const markerColorRef = useRef<string>(MARKER_COLORS[0]);
  /**
   * Places a mark on a 3D model. Reached through a ref because the desktop
   * pointer handler is created in the scene-init effect, long before this
   * exists — the same stale-closure trap the panel handler fell into.
   */
  const addModelMarkRef = useRef<
    (model: THREE.Object3D, mesh: THREE.Mesh, raycaster: THREE.Raycaster) => void
  >(() => {});
  const lastProgressPercentRef = useRef<number>(-1);
  
  // WebXR Systems refs
  const layoutEngineRef = useRef<LayoutEngine | null>(null);
  
  // State
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lessonData, setLessonData] = useState<LessonData | null>(null);
  // The layout existing lessons inherit (app_settings/lesson_layout), set in Studio.
  const [globalLayoutDefault, setGlobalLayoutDefault] = useState<unknown>(null);
  useEffect(() => {
    let live = true;
    getDoc(doc(db, 'app_settings', 'lesson_layout'))
      .then((snap) => {
        if (live && snap.exists()) setGlobalLayoutDefault(snap.data());
      })
      // No default stored, or not readable: the built-in Lecture room stands.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  const [skyboxUrl, setSkyboxUrl] = useState<string | null>(null);
  const [fallbackImageUrl, setFallbackImageUrl] = useState<string | null>(null);
  const [isVRSupported, setIsVRSupported] = useState<boolean | null>(null);
  const [isSceneReady, setIsSceneReady] = useState(false);
  
  // Lesson content state
  const [ttsData, setTtsData] = useState<TTSData[]>([]);
  const [mcqData, setMcqData] = useState<MCQData[]>([]);
  /**
   * The topic whose questions `mcqData` holds, once they have finished loading.
   * Until it matches the topic on screen, nobody can say whether that topic has
   * a quiz — an empty list might just not have arrived yet — and the quiz gate
   * treats it as having one.
   */
  const [mcqLoadedTopicId, setMcqLoadedTopicId] = useState<string | null>(null);
  const [meshyAssets, setMeshyAssets] = useState<MeshyAsset[]>([]);
  const [lessonPhase, setLessonPhase] = useState<LessonPhase>('idle');
  const [lessonStarted, setLessonStarted] = useState(false);
  const [currentTtsIndex, setCurrentTtsIndex] = useState(0);
  const [currentMcqIndex, setCurrentMcqIndex] = useState(0);
  const [, setIsAudioPlaying] = useState(false);
  const [assetsLoaded, setAssetsLoaded] = useState(0);
  const [selectedMcqOption, setSelectedMcqOption] = useState<number | null>(null);
  const [mcqAnswered, setMcqAnswered] = useState(false);
  const [mcqScore, setMcqScore] = useState(0);
  const mcqAnswerHistoryRef = useRef<Array<{ questionIndex: number; correct: boolean; selectedOptionIndex: number }>>([]);
  const pendingQuizReportRef = useRef<ReportSessionQuizPayload | null>(null);
  /**
   * True once this topic's quiz has been handed in by answering the last
   * question. It is what stops a swap handing the same quiz in again as
   * unfinished — `lessonPhase === 'completed'` used to, but the phase moves on
   * (the teacher can take the class back to the intro) while the hand-in stands.
   * Cleared on a new topic and whenever the quiz is started again.
   */
  const quizHandedInRef = useRef(false);

  // TTS State Machine
  type TTSState = 'idle' | 'playing' | 'paused' | 'ended';
  const [ttsState, setTtsState] = useState<TTSState>('idle');
  
  
  // Asset references map for dock control
  const assetRefs = useRef<Map<string, THREE.Object3D>>(new Map());
  
  // Scene Layout System (production-grade, scalable layout)
  const sceneLayoutRef = useRef<SceneLayoutSystem | null>(null);
  /** Drives the scene layout; rotated at lesson start. */
  const [placementStrategy] = useState<PlacementStrategy>('curved-arc');
  const assetPlacementsRef = useRef<AssetPlacement[]>([]);
  const animationMixersRef = useRef<THREE.AnimationMixer[]>([]);
  const lastAnimationTimeRef = useRef<number>(0);

  // --- Live class -----------------------------------------------------------
  // Drag-to-look for flat screens. The camera used to be immovable outside a
  // headset, which also left view sync with nothing to read or write.
  const lookControlsRef = useRef<LookControls | null>(null);
  /** Subscribers to local view changes (host broadcast, student view reporting). */
  const viewListenersRef = useRef<Set<(h: number, v: number, fov: number) => void>>(new Set());
  /** Set by the classroom hook below so effects defined earlier can reach it. */
  const classroomRef = useRef<{
    blockStudentPhaseControl: (label: string) => boolean;
    markStudentLooking: () => void;
    showImmersiveUiForThisViewer: boolean;
    directClassToCurrentView: () => Promise<boolean>;
    /** Class the lesson is being taught in, for score attribution. */
    classId: string | null;
    /** Live session the student joined, so a result links to it. */
    sessionId: string | null;
    isClassHost: boolean;
    publishModelState: (patch: Partial<TeacherContentState>, sceneKey: string) => void;
    /** The synced content state, read by the topic swap after its awaits. */
    teacherContentState: TeacherContentState | null;
  }>({
    blockStudentPhaseControl: () => false,
    markStudentLooking: () => {},
    showImmersiveUiForThisViewer: true,
    directClassToCurrentView: async () => false,
    classId: null,
    sessionId: null,
    isClassHost: false,
    publishModelState: () => {},
    teacherContentState: null,
  });
  /**
   * Gates the phase autoplay effect. Under lockstep the teacher owns playback, so
   * a student's audio must not start on its own.
   */
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  /** Last phase the autoplay effect actually played, so it fires once per phase. */
  const lastPlayedPhaseRef = useRef<LessonPhase | null>(null);
  const [hostDrawer, setHostDrawer] = useState<null | 'roster' | 'approvals' | 'preview'>(null);
  /**
   * Comfort break. Students only — a teacher driving the class does not need
   * their own lesson interrupted, and they can see the prompt land for the room.
   */
  const [isPresentingXR, setIsPresentingXR] = useState(false);
  /**
   * Model manipulation state.
   *
   * The bottom bar has always had these controls; it hid them because this
   * player reported no separable parts. They mirror TeacherContentState, so a
   * teacher's manipulation reaches students with no new session fields.
   */
  const [modelPartCount, setModelPartCount] = useState(0);
  const [modelExplode, setModelExplode] = useState(0);
  const [modelIsolated, setModelIsolated] = useState(false);
  /** Every asset in the scene, so the teacher can choose which one to work on. */
  const [modelAssets, setModelAssets] = useState<ModelSummary[]>([]);
  const [modelSelectedAssetKey, setModelSelectedAssetKey] = useState<string | null>(null);
  /** Addressed by id, not name: mesh names are often blank or repeated. */
  const [modelSelectedPartId, setModelSelectedPartId] = useState<string | null>(null);
  const [modelSelectedPartName, setModelSelectedPartName] = useState<string | null>(null);
  const [modelClip, setModelClip] = useState<{ axis: ClipAxis; offset: number } | null>(null);
  /** Host only: a tap on a part toggles its highlight instead of just selecting it. */
  const [modelHighlightMode, setModelHighlightMode] = useState(false);
  /** Parts glowing right now, for the toolbar's "Clear (n)" and the dock dot. */
  const [modelHighlightCount, setModelHighlightCount] = useState(0);
  /**
   * Bumped each time the model tools re-collect the scene (an asset finished
   * loading). The student-apply effect keys on it: freshly collected models
   * need the teacher's state and the student's overrides again even when no
   * new publish arrived, and a publish that landed before any model existed
   * found nothing to apply to.
   */
  const [modelToolsVersion, setModelToolsVersion] = useState(0);
  /** Bumped (throttled) when this viewer's own overrides change, so My view re-reads them. */
  const [localModelVersion, setLocalModelVersion] = useState(0);
  /** The model the My view popover acts on; null follows the first one. */
  const [myViewAssetKey, setMyViewAssetKey] = useState<string | null>(null);
  /* No setters: Highlight replaced the marker on V3's dock, so nothing turns it
     on any more. The ink code that reads these is removed in a follow-up. */
  const [markerActive, setMarkerActive] = useState(false);
  const [markerColor, setMarkerColor] = useState<string>(MARKER_COLORS[0]);
  const [endSessionConfirming, setEndSessionConfirming] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const playerViewport = usePlayerViewport();
  
  // Debug logger with category support - enhanced with timestamps and structured output
  const addDebug = useCallback((msg: string, category?: keyof typeof DEBUG_CATEGORIES) => {
    const prefix = category ? DEBUG_CATEGORIES[category] : '[V3]';
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const fullMsg = `${prefix} ${msg}`;
    // Console only. This used to also feed an on-screen panel that was
    // rendered unconditionally, so it shipped to every class.
    console.log(`[${timestamp}] ${fullMsg}`);
  }, []);
  
  // Structured debug helpers with enhanced context
  const debugXR = useCallback((msg: string) => {
    addDebug(`🥽 ${msg}`, 'XR');
  }, [addDebug]);
  
  const debugLayout = useCallback((msg: string) => {
    addDebug(`📐 ${msg}`, 'LAYOUT');
  }, [addDebug]);
  
  const debugUI = useCallback((msg: string) => {
    addDebug(`🖼️ ${msg}`, 'UI');
  }, [addDebug]);
  
  const debugAsset = useCallback((msg: string) => {
    addDebug(`📦 ${msg}`, 'ASSET');
  }, [addDebug]);
  
  const debugInteraction = useCallback((msg: string) => {
    addDebug(`👆 ${msg}`, 'INTERACTION');
  }, [addDebug]);
  
  const debugTTS = useCallback((msg: string) => {
    addDebug(`🔊 ${msg}`, 'TTS');
  }, [addDebug]);
  
  const debugQuiz = useCallback((msg: string) => {
    addDebug(`❓ ${msg}`, 'QUIZ');
  }, [addDebug]);
  
  // Comprehensive state logger - logs current state summary
  
  // ============================================================================
  // Load Lesson Data from SessionStorage
  // ============================================================================
  
  useEffect(() => {
    const loadLessonData = () => {
      addDebug('Loading lesson data from sessionStorage...');
      try {
        const stored = sessionStorage.getItem('activeLesson');
        if (stored) {
          const data = JSON.parse(stored);
          addDebug(`Lesson loaded: ${data.topic?.topic_name || 'unknown'}`);
          addDebug(`Skybox ID: ${data.topic?.skybox_id || 'none'}`);
          setLessonData(data);
        } else {
          addDebug('ERROR: No lesson data in sessionStorage');
          setErrorMessage('No lesson data found. Please select a lesson first.');
          setLoadingState('error');
        }
      } catch (err) {
        addDebug(`ERROR: Failed to parse: ${err}`);
        setErrorMessage('Failed to load lesson data');
        setLoadingState('error');
      }
    };
    
    loadLessonData();
  }, [addDebug]);


  // Reset quiz answer history when entering MCQ phase
  useEffect(() => {
    if (lessonPhase !== 'quiz') return;
    mcqAnswerHistoryRef.current = [];
    quizHandedInRef.current = false;
  }, [lessonPhase]);
  
  // ============================================================================
  // Fetch Skybox GLB URL from Firestore
  // ============================================================================
  
  useEffect(() => {
    const fetchSkyboxUrl = async () => {
      if (!lessonData?.topic) {
        addDebug('Waiting for lesson data...');
        return;
      }
      
      addDebug('Fetching skybox URL...');
      setLoadingMessage('Fetching skybox...');
      const topic = lessonData.topic;
      
      // Priority 1: Direct skybox_glb_url on topic
      if (topic.skybox_glb_url) {
        addDebug(`Direct skybox_glb_url: ${topic.skybox_glb_url.substring(0, 50)}...`);
        setSkyboxUrl(topic.skybox_glb_url);
        return;
      }
      
      // Priority 2: Fetch from skyboxes collection using skybox_id
      // IMPORTANT: Convert to string as Firestore doc() requires string IDs
      const rawSkyboxId = topic.skybox_id || topic.skybox_remix_id;
      const skyboxId = rawSkyboxId ? String(rawSkyboxId) : null;
      addDebug(`Skybox ID from topic: ${skyboxId || 'NONE'} (type: ${typeof rawSkyboxId})`);
      
      if (skyboxId) {
        try {
          addDebug(`Fetching from Firestore: skyboxes/${skyboxId}`);
          const skyboxDoc = await getDoc(doc(db, 'skyboxes', skyboxId));
          
          if (skyboxDoc.exists()) {
            const skyboxData = skyboxDoc.data();
            addDebug(`Skybox doc found! Fields: ${Object.keys(skyboxData).join(', ')}`);
            
            // Always store the image URL as fallback
            const imageUrl = skyboxData.fileUrl || skyboxData.imageUrl;
            if (imageUrl) {
              addDebug(`Setting fallback image: ${String(imageUrl).substring(0, 50)}...`);
              setFallbackImageUrl(String(imageUrl));
            }
            
            // Use stored_glb_url (Firebase Storage) - this is the GLB file
            if (skyboxData.stored_glb_url) {
              addDebug(`Using stored_glb_url: ${skyboxData.stored_glb_url.substring(0, 60)}...`);
              setSkyboxUrl(skyboxData.stored_glb_url);
              return;
            }
            
            // Fallback to image URLs if no GLB
            if (imageUrl) {
              addDebug(`No GLB, using image: ${String(imageUrl).substring(0, 60)}...`);
              setSkyboxUrl(String(imageUrl));
              return;
            }
            
            addDebug('ERROR: Skybox doc has no URL fields!');
          } else {
            addDebug(`ERROR: Skybox doc ${skyboxId} does not exist!`);
          }
        } catch (err) {
          addDebug(`ERROR fetching skybox: ${err}`);
        }
      }
      
      // Priority 3: Use skybox_url as final fallback
      if (topic.skybox_url) {
        addDebug(`Using topic.skybox_url: ${topic.skybox_url.substring(0, 60)}...`);
        setSkyboxUrl(topic.skybox_url);
        return;
      }
      
      addDebug('ERROR: No skybox URL found anywhere!');
      setErrorMessage('No skybox found for this lesson');
      setLoadingState('error');
    };
    
    fetchSkyboxUrl();
  }, [lessonData, addDebug]);
  
  // ============================================================================
  // Fetch TTS Audio Data
  // ============================================================================
  
  useEffect(() => {
    const fetchTTSData = async () => {
      if (!lessonData) return;
      
      // CRITICAL: Language is in topic.language, not lessonData.language
      const lessonLanguage = (lessonData as any).topic?.language || (lessonData as any).language || 'en';
      console.log('[TTS FETCH] Detected lesson language:', lessonLanguage);
      
      // Priority 1: Check if TTS audio is already in lessonData (from bundle)
      const ttsAudioFromStorage = (lessonData as any).ttsAudio;
      if (ttsAudioFromStorage && Array.isArray(ttsAudioFromStorage)) {
        // Filter by language (strict match)
        const languageFilteredTTS = ttsAudioFromStorage.filter((tts: any) => {
          const ttsLang = (tts.language || 'en').toLowerCase().trim();
          const targetLang = lessonLanguage.toLowerCase().trim();
          return ttsLang === targetLang;
        });
        
        if (languageFilteredTTS.length > 0) {
          // VERSION MARKER - v3.0 - Camera-relative asset positioning + TTS fix
          console.log('[TTS v3.0] ════════════════════════════════════════');
          console.log('[TTS v3.0] Processing', languageFilteredTTS.length, 'TTS entries');
          addDebug(`[v3.0] Processing ${languageFilteredTTS.length} TTS for ${lessonLanguage}`);
          
          const convertedTTS: TTSData[] = languageFilteredTTS.map((tts: any, index: number) => {
            const rawId = tts.id || '';
            const rawScriptType = tts.script_type;
            
            console.log(`[TTS v3.0] #${index + 1}: script_type="${rawScriptType}", id contains: intro=${rawId.includes('intro')}, expl=${rawId.includes('explanation')}, outro=${rawId.includes('outro')}`);
            
            // FORCE section extraction - ALWAYS extract from script_type or ID
            let sectionType: string;
            
            // Priority 1: Use script_type directly if valid
            if (rawScriptType === 'intro' || rawScriptType === 'introduction') {
              sectionType = 'intro';
              console.log(`[TTS v3.0]   → script_type match: intro`);
            } else if (rawScriptType === 'explanation' || rawScriptType === 'content') {
              sectionType = 'explanation';
              console.log(`[TTS v3.0]   → script_type match: explanation`);
            } else if (rawScriptType === 'outro' || rawScriptType === 'conclusion' || rawScriptType === 'summary') {
              sectionType = 'outro';
              console.log(`[TTS v3.0]   → script_type match: outro`);
            }
            // Priority 2: Extract from ID
            else if (rawId.toLowerCase().includes('intro')) {
              sectionType = 'intro';
              console.log(`[TTS v3.0]   → ID match: intro`);
            } else if (rawId.toLowerCase().includes('explanation')) {
              sectionType = 'explanation';
              console.log(`[TTS v3.0]   → ID match: explanation`);
            } else if (rawId.toLowerCase().includes('outro')) {
              sectionType = 'outro';
              console.log(`[TTS v3.0]   → ID match: outro`);
            }
            // Priority 3: Position in array
            else {
              if (index === 0) sectionType = 'intro';
              else if (index === languageFilteredTTS.length - 1) sectionType = 'outro';
              else sectionType = 'explanation';
              console.log(`[TTS v3.0]   → Position fallback: ${sectionType}`);
            }
            
            console.log(`[TTS v3.0]   FINAL: "${sectionType}"`);
            
            return {
              id: rawId,
              section: sectionType,
              audioUrl: tts.audio_url || tts.audioUrl || tts.url || '',
              text: tts.text || tts.script_text || '',
            };
          });
          
          console.log('[TTS v3.0] ════════════════════════════════════════');
          console.log('[TTS v3.0] RESULTS:');
          convertedTTS.forEach((t, i) => {
            console.log(`[TTS v3.0]   #${i + 1}: section="${t.section}"`);
          });
          
          setTtsData(convertedTTS);
          addDebug(`[v3.0] TTS: ${convertedTTS.map(t => t.section).join(', ')}`);
          return;
        } else {
          addDebug(`⚠️ No TTS found in bundle for language ${lessonLanguage}`, {
            totalTTS: ttsAudioFromStorage.length,
            sampleLanguages: ttsAudioFromStorage.slice(0, 3).map((t: any) => t.language || 'none'),
          });
        }
      }
      
      // Priority 2: Fetch from Firestore using IDs
      const ttsIds = lessonData.topic?.tts_ids || lessonData.chapter?.tts_ids || [];
      if (ttsIds.length === 0) {
        addDebug('No TTS IDs found');
        return;
      }
      
      addDebug(`Fetching ${ttsIds.length} TTS entries for language: ${lessonLanguage}...`);
      const ttsResults: TTSData[] = [];
      
      // Filter IDs by language (check if ID contains language indicator)
      const languageTtsIds = ttsIds.filter(id => {
        if (lessonLanguage === 'hi') {
          return id.includes('_hi') || id.includes('_hindi');
        } else {
          return !id.includes('_hi') && !id.includes('_hindi');
        }
      });
      
      for (const ttsId of languageTtsIds.slice(0, 3)) { // Max 3 for intro/explanation/outro
        try {
          const ttsDoc = await getDoc(doc(db, 'chapter_tts', ttsId));
          if (ttsDoc.exists()) {
            const data = ttsDoc.data();
            const ttsLang = data.language || 'en';
            
            // Only include if language matches
            if (ttsLang === lessonLanguage && (data.audio_url || data.audioUrl)) {
              // Extract section from ID (more reliable than data.section)
              let section = 'content';
              const idLower = ttsId.toLowerCase();
              if (idLower.includes('intro')) section = 'intro';
              else if (idLower.includes('explanation')) section = 'explanation';
              else if (idLower.includes('outro')) section = 'outro';
              
              ttsResults.push({
                id: ttsId,
                section: section,
                audioUrl: data.audio_url || data.audioUrl,
                text: data.text || data.content || '',
              });
              addDebug(`TTS loaded: ${section} (${ttsLang})`);
            }
          }
        } catch (err) {
          addDebug(`TTS error for ${ttsId}: ${err}`);
        }
      }
      
      setTtsData(ttsResults);
      
      // Comprehensive TTS debug logging
      console.log('[TTS DEBUG] ========================================');
      console.log('[TTS DEBUG] Language:', lessonLanguage);
      console.log('[TTS DEBUG] Total TTS loaded:', ttsResults.length);
      ttsResults.forEach((tts, idx) => {
        console.log(`[TTS DEBUG] TTS #${idx + 1}:`, {
          id: tts.id,
          section: tts.section,
          audioUrl: tts.audioUrl?.substring(0, 80) + '...',
          hasText: !!tts.text,
        });
      });
      console.log('[TTS DEBUG] ========================================');
      
      addDebug(`✅ Loaded ${ttsResults.length} TTS entries (language: ${lessonLanguage})`);
    };
    
    fetchTTSData();
  }, [lessonData, addDebug]);
  
  // ============================================================================
  // Fetch MCQ Data
  // ============================================================================
  
  useEffect(() => {
    // Only the Firestore branch awaits; the others finish before this effect
    // returns, so the one check after the fetch loop is all a stale run needs.
    let cancelled = false;
    const fetchMCQData = async () => {
      if (!lessonData) return;
      // Recorded on every way out, empty or not: "this topic has no quiz" is an
      // answer too, and the quiz gate waits for one.
      const loadingTopicId = String((lessonData as any).topic?.topic_id ?? '');
      
      // CRITICAL: Language is in topic.language, not lessonData.language
      const lessonLanguage = (lessonData as any).topic?.language || (lessonData as any).language || 'en';
      console.log('[MCQ FETCH] Detected lesson language:', lessonLanguage);
      
      // Priority 1: Check if MCQs are already in lessonData (from bundle)
      if ((lessonData as any).topic?.mcqs && Array.isArray((lessonData as any).topic.mcqs)) {
        const mcqs = (lessonData as any).topic.mcqs;
        if (mcqs.length > 0) {
          // Convert to MCQData format with 1-based to 0-based index conversion
          const convertedMCQs: MCQData[] = mcqs.map((mcq: any) => {
            const options = extractMcqOptions(mcq);
            // Resolved in one place (src/lib/mcq/answerIndex.ts) — the index the backend
            // stores is already 0-based, so the -1 this replaces scored B as A.
            return {
              id: mcq.id || '',
              question: mcq.question || '',
              options: options,
              correctAnswer: resolveCorrectAnswerIndex(mcq, options, 'xr-quiz'),
              explanation: mcq.explanation || '',
            };
          });
          
          // Comprehensive MCQ debug logging for bundle data
          console.log('[MCQ DEBUG FROM BUNDLE] ========================================');
          console.log('[MCQ DEBUG] Language:', lessonLanguage);
          console.log('[MCQ DEBUG] Total MCQs from bundle:', convertedMCQs.length);
          convertedMCQs.forEach((mcq, idx) => {
            console.log(`[MCQ DEBUG] MCQ #${idx + 1}:`, {
              id: mcq.id,
              question: mcq.question?.substring(0, 50) + '...',
              optionsCount: mcq.options?.length,
              options: mcq.options,
              correctAnswer: mcq.correctAnswer,
              correctOptionText: mcq.options?.[mcq.correctAnswer] || 'N/A',
            });
          });
          console.log('[MCQ DEBUG] ========================================');
          
          setMcqData(convertedMCQs);
          setMcqLoadedTopicId(loadingTopicId);
          addDebug(`✅ Loaded ${convertedMCQs.length} MCQs from bundle (language: ${lessonLanguage})`);
          return;
        }
      }
      
      // Priority 2: Fetch from Firestore using IDs
      const mcqIds = lessonData.topic?.mcq_ids || lessonData.chapter?.mcq_ids || [];
      if (mcqIds.length === 0) {
        addDebug('No MCQ IDs found');
        // An earlier topic's list may still be here, which would make this
        // quiz-less topic look gated.
        setMcqData([]);
        setMcqLoadedTopicId(loadingTopicId);
        return;
      }
      
      addDebug(`Fetching ${mcqIds.length} MCQ entries for language: ${lessonLanguage}...`);
      const mcqResults: MCQData[] = [];
      
      // Filter IDs by language (check if ID contains language indicator)
      const languageMcqIds = mcqIds.filter(id => {
        if (lessonLanguage === 'hi') {
          return id.includes('_hi') || id.includes('_hindi');
        } else {
          return !id.includes('_hi') && !id.includes('_hindi');
        }
      });
      
      for (const mcqId of languageMcqIds.slice(0, 5)) { // Max 5 questions
        try {
          const mcqDoc = await getDoc(doc(db, 'chapter_mcqs', mcqId));
          if (mcqDoc.exists()) {
            const data = mcqDoc.data();
            const mcqLang = data.language || 'en';
            
            // Only include if language matches
            if (mcqLang === lessonLanguage) {
              const options = extractMcqOptions(data);
              mcqResults.push({
                id: mcqId,
                question: data.question || data.question_text || '',
                options: options,
                correctAnswer: resolveCorrectAnswerIndex(data, options, 'xr-quiz'),
                explanation: data.explanation || '',
              });
              addDebug(`MCQ loaded: ${mcqId} (${mcqLang})`);
            }
          }
        } catch (err) {
          addDebug(`MCQ error for ${mcqId}: ${err}`);
        }
      }
      
      // The topic changed while these were loading. Landing now would put the
      // old topic's questions over the new one's and mark the OLD topic loaded,
      // leaving the new topic's gate 'unknown' — read as closed — throughout.
      if (cancelled) return;
      setMcqData(mcqResults);
      setMcqLoadedTopicId(loadingTopicId);
      
      // Comprehensive MCQ debug logging
      console.log('[MCQ DEBUG] ========================================');
      console.log('[MCQ DEBUG] Language:', lessonLanguage);
      console.log('[MCQ DEBUG] Total MCQs loaded:', mcqResults.length);
      mcqResults.forEach((mcq, idx) => {
        console.log(`[MCQ DEBUG] MCQ #${idx + 1}:`, {
          id: mcq.id,
          question: mcq.question?.substring(0, 50) + '...',
          optionsCount: mcq.options?.length,
          options: mcq.options,
          correctAnswer: mcq.correctAnswer,
          correctOptionText: mcq.options?.[mcq.correctAnswer] || 'N/A',
        });
      });
      console.log('[MCQ DEBUG] ========================================');
      
      addDebug(`✅ Loaded ${mcqResults.length} MCQs (language: ${lessonLanguage})`);
    };
    
    fetchMCQData();
    return () => {
      cancelled = true;
    };
  }, [lessonData, addDebug]);
  
  // ============================================================================
  // Fetch 3D Assets (Meshy)
  // ============================================================================
  
  useEffect(() => {
    const fetchMeshyAssets = async () => {
      if (!lessonData) return;

      /*
        Every source the lesson describes its models in, merged into one list
        with each model appearing exactly once.

        Identity is the asset id, not the URL. The bundle and the topic's ids
        point at the SAME Firestore documents, and the bundle has normalised
        their URLs while a raw re-read has not — de-duplicating on the URL let
        one asset through twice, which is why the same model appeared several
        times in the scene with only one of them answering the teacher's
        controls. See mergeLessonAssets for the full reasoning.
      */
      const pending = unresolvedAssetIds(lessonData);
      const resolvedDocs: ResolvedAssetDoc[] = [];
      if (pending.length > 0) {
        addDebug(`Resolving ${pending.length} 3D asset id(s) from Firestore...`);
        for (const assetId of pending) {
          try {
            const assetDoc = await getDoc(doc(db, 'meshy_assets', assetId));
            if (assetDoc.exists()) resolvedDocs.push({ id: assetId, data: assetDoc.data() });
          } catch (err) {
            addDebug(`3D Asset error for ${assetId}: ${err}`);
          }
        }
      }

      const merged = mergeLessonAssets({
        bundleAssets: (lessonData as any).assets3d,
        assetUrls: lessonData.topic?.asset_urls,
        assetIds: (lessonData.topic as any)?.asset_ids,
        image3d: (lessonData as any).image3dasset,
        resolvedDocs,
      });

      setMeshyAssets(merged);
      addDebug(`Loaded ${merged.length} 3D asset(s) from all sources`);
    };

    fetchMeshyAssets();
  }, [lessonData, addDebug]);
  
  // ============================================================================
  // Check WebXR Support
  // ============================================================================
  
  useEffect(() => {
    const checkVRSupport = async () => {
      try {
        // Check WebGL support first
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) {
          console.warn('[XRLessonPlayerV3] WebGL not available');
          setIsVRSupported(false);
          setErrorMessage('WebGL is not supported in this browser. Please use a modern browser with WebGL support.');
          setLoadingState('error');
          return;
        }
        
        if (!navigator.xr) {
          console.log('[XRLessonPlayerV3] WebXR not available (WebGL is OK)');
          setIsVRSupported(false);
          return;
        }
        
        try {
          const supported = await navigator.xr.isSessionSupported('immersive-vr');
          console.log('[XRLessonPlayerV3] immersive-vr supported:', supported);
          setIsVRSupported(supported);
        } catch (err) {
          console.error('[XRLessonPlayerV3] VR support check failed:', err);
          setIsVRSupported(false);
        }
      } catch (err: any) {
        console.error('[XRLessonPlayerV3] VR/WebGL check error:', err);
        setIsVRSupported(false);
      }
    };
    
    checkVRSupport();
  }, []);
  
  // ============================================================================
  // Initialize Three.js Scene with WebXR
  // ============================================================================
  
  useEffect(() => {
    try {
      addDebug(`Scene init check: container=${!!containerRef.current}, vrSupport=${isVRSupported}`);
      
      if (!containerRef.current) {
        addDebug('Waiting for container ref...');
        return;
      }
      if (isVRSupported === null) {
        addDebug('Waiting for VR support check...');
        return;
      }
      
      addDebug('All conditions met, initializing scene...');
      setLoadingMessage('Setting up VR scene...');
      setIsSceneReady(false);
      
      // Create scene
      const scene = new THREE.Scene();
      scene.background = null; // Will be filled by skybox
      sceneRef.current = scene;
      
      // Create camera at origin (center of skybox)
      const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
      camera.position.set(0, 1.6, 0); // Eye height
      cameraRef.current = camera;
      
      // Verify scene and camera were created
      if (!scene || !camera) {
        throw new Error('Failed to create scene or camera');
      }
      
      // Create WebGL renderer with XR enabled (with error handling)
      let renderer: THREE.WebGLRenderer;
      try {
        // Check WebGL support first
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) {
          throw new Error('WebGL is not supported in this browser');
        }
        
        renderer = new THREE.WebGLRenderer({
          antialias: true,
          alpha: false,
          powerPreference: 'high-performance',
          failIfMajorPerformanceCaveat: false, // Don't fail on performance issues
        });
        
        // Verify renderer was created successfully
        if (!renderer || !renderer.domElement) {
          throw new Error('Failed to create WebGL renderer');
        }
        
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;
        
        // STATE-OF-ART RENDERING: Enable high-quality shadows
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Soft shadows
        renderer.shadowMap.autoUpdate = true;
        
        // CRITICAL: Enable XR
        renderer.xr.enabled = true;
        
        rendererRef.current = renderer;
        
        if (containerRef.current) {
          containerRef.current.appendChild(renderer.domElement);
        }
        
        addDebug('✅ WebGL renderer created successfully');
      } catch (webglErr: any) {
        console.error('[XRLessonPlayerV3] WebGL creation error:', webglErr);
        addDebug(`WebGL error: ${webglErr?.message || webglErr}`);
        
        // Try fallback: create renderer without some features
        try {
          addDebug('Attempting fallback WebGL renderer...');
          renderer = new THREE.WebGLRenderer({
            antialias: false,
            alpha: false,
            powerPreference: 'default',
            failIfMajorPerformanceCaveat: false,
          });
          
          if (!renderer || !renderer.domElement) {
            throw new Error('Fallback renderer also failed');
          }
          
          renderer.setSize(window.innerWidth, window.innerHeight);
          renderer.setPixelRatio(1); // Lower pixel ratio for compatibility
          renderer.outputColorSpace = THREE.SRGBColorSpace;
          renderer.xr.enabled = true;
          
          rendererRef.current = renderer;
          
          if (containerRef.current) {
            containerRef.current.appendChild(renderer.domElement);
          }
          
          addDebug('✅ Fallback WebGL renderer created');
        } catch (fallbackErr: any) {
          console.error('[XRLessonPlayerV3] Fallback renderer also failed:', fallbackErr);
          throw new Error(`WebGL context creation failed: ${webglErr?.message || webglErr}. Fallback also failed: ${fallbackErr?.message || fallbackErr}`);
        }
      }
    
      // Add VR Button if supported (only if renderer was created successfully)
      // BUT: Hide until asset calculations are complete
      if (isVRSupported && containerRef.current && rendererRef.current) {
        try {
          const vrButton = VRButton.createButton(rendererRef.current);
          vrButton.style.position = 'absolute';
          // Clear of the bottom bar. three.js defaults to bottom:20px, which is
          // inside the --hud-bottom band, so ENTER VR landed on top of the
          // teacher's controls. Reading the same variable the chrome publishes
          // means the two cannot collide again whatever the bar's height.
          vrButton.style.bottom = 'calc(var(--hud-bottom, 4rem) + 1rem)';
          vrButton.style.left = '50%';
          vrButton.style.transform = 'translateX(-50%)';
          vrButton.style.zIndex = '100';
          vrButton.style.display = 'none'; // Hidden until calculations done
          containerRef.current.appendChild(vrButton);
          vrButtonRef.current = vrButton;
          
          // Listen for session start/end
          rendererRef.current.xr.addEventListener('sessionstart', () => {
            console.log(`${DEBUG_CATEGORIES.XR} VR session started`);
            setIsPresentingXR(true);
            // A mouse drag of the panel cannot carry into the headset.
            panelDragRef.current = null;
            resetXrReorientation();
            resetTeleport();
            // Cut the render budget for the headset. Without this a lesson with
            // more than one 3D asset drops frames badly: the shadow pass is
            // re-rendered every frame at 2048 across two eyes, and its cost
            // scales with the number of casters.
            if (rendererRef.current && sceneRef.current) {
              applyRenderBudget(rendererRef.current, sceneRef.current, IMMERSIVE_BUDGET);
            }
            
            // Store input sources for haptic feedback
            const session = rendererRef.current.xr.getSession();
            if (session && session.inputSources) {
              session.inputSources.forEach((inputSource, index) => {
                if (index < 2) {
                  inputSourcesRef.current[index] = inputSource;
                }
              });
              console.log(`[HAPTIC] Stored ${session.inputSources.length} input sources for haptic feedback`);
              addDebug(`Input sources stored: ${session.inputSources.length}`);
            }
            setLoadingState('in-vr');
            
            // Initialize layout engine and compute anchor from current head pose
            if (!layoutEngineRef.current) {
              layoutEngineRef.current = createLayoutEngine();
            }
            
            // CRITICAL: Must call initialize() before computeAnchor() for isReady() to return true
            const xrSession = rendererRef.current?.xr.getSession();
            if (xrSession) {
              layoutEngineRef.current.initialize(xrSession);
              console.log(`${DEBUG_CATEGORIES.LAYOUT} Layout engine initialized with XR session`);
            } else {
              layoutEngineRef.current.initialize();
              console.log(`${DEBUG_CATEGORIES.LAYOUT} Layout engine initialized without XR session`);
            }
            
            // Compute layout anchor after a brief delay for head tracking to stabilize
            setTimeout(() => {
              if (layoutEngineRef.current && cameraRef.current) {
                console.log(`\n🎯 [VR START] ════════════════════════════════════════`);
                console.log(`🎯 [VR START] Computing initial anchor on VR session start`);
                
                // Compute anchor
                layoutEngineRef.current.computeAnchor(cameraRef.current);
                
                // Get camera position for asset repositioning
                const cameraPos = new THREE.Vector3();
                cameraRef.current.getWorldPosition(cameraPos);
                console.log(`🎯 [VR START] Camera position: (${cameraPos.x.toFixed(3)}, ${cameraPos.y.toFixed(3)}, ${cameraPos.z.toFixed(3)})`);
                
                addDebug(`VR Session Started`);
                addDebug(`Camera Y: ${cameraPos.y.toFixed(2)}m`);
                
                // ═══════════════════════════════════════════════════════════════════
                // PROFESSIONAL LAYOUT: Update user pose and reposition assets
                // Uses collision-aware placement with zone management
                // ═══════════════════════════════════════════════════════════════════
                addDebug(`═══ VR SESSION STARTED ═══`);
                addDebug(`Camera Y: ${cameraPos.y.toFixed(2)}m`);
                
                // Update the professional layout system with current user pose
                if (professionalLayoutRef.current && cameraRef.current) {
                  professionalLayoutRef.current.updateUserPose(cameraRef.current, GROUND_LEVEL);
                  console.log(`[LayoutSystem] User pose updated for VR session`);
                  addDebug(`Layout System: User pose updated`);
                }
                
                // Update VR Lesson Experience with user pose
                if (vrExperienceRef.current && cameraRef.current) {
                  vrExperienceRef.current.updateUserPose(cameraRef.current, GROUND_LEVEL);
                  console.log(`[VRExperience] User pose updated for VR session`);
                  addDebug(`VR Experience: Stage positioned`);
                }
                
                addDebug(`Ground Level: ${GROUND_LEVEL}m`);
                addDebug(`Table Height: ${TABLE_HEIGHT}m`);
                addDebug(`Target Asset Y: ${(GROUND_LEVEL + TABLE_HEIGHT).toFixed(2)}m`);

                // ═══════════════════════════════════════════════════════════
                // FIT THE ROOM TO THE VIEWER
                //
                // Everything below the headset was laid out from standing-adult
                // constants: a dock 0.9m up and a panel at 1.6m. Students take
                // these lessons SITTING at classroom desks, where the eye line
                // is nearer 1.2m — so the model sat at chest height and the
                // lesson panel floated above the top of their view.
                //
                // WebXR's local-floor reference space puts Y=0 on the real
                // floor, so the headset's height IS the viewer's eye height.
                // Measured here, once the head pose has settled, rather than
                // assuming a posture or asking the student to pick one.
                // ═══════════════════════════════════════════════════════════
                applyViewerErgonomicsRef.current();
                
                // ═══════════════════════════════════════════════════════════════════
                // STABLE LAYOUT ON VR SESSION START
                // Recompute anchors for VR mode and re-stage if needed
                // ═══════════════════════════════════════════════════════════════════
                const assetsGroup = sceneRef.current?.getObjectByName('assetsGroup');
                if (assetsGroup && assetsGroup.children.length > 0) {
                  console.log(`🎯 [VR START] ═══════════════════════════════════════`);
                  console.log(`🎯 [VR START] STABLE LAYOUT for ${assetsGroup.children.length} assets`);
                  
                  addDebug(`🔄 Stable Layout for ${assetsGroup.children.length} asset(s)`);
                  
                  // Initialize or update Stable Layout System
                  if (!stableLayoutRef.current) {
                    stableLayoutRef.current = new StableLayoutSystem({
                      stageDistance: 2.5,
                      stageWidth: 4.0,
                      stageDepth: 2.5,
                      horizontalOffset: 0.8,
                      floorHeight: GROUND_LEVEL,
                      modelSpacing: 0.5,
                      normalizedSize: 0.8,
                      environmentThreshold: 10.0,
                    });
                  }
                  
                  // Initialize or recompute anchors for VR
                  if (!stableLayoutRef.current.isReady()) {
                    stableLayoutRef.current.initialize(cameraRef.current, GROUND_LEVEL);
                  } else {
                    // Unlock and restage for VR mode
                    stableLayoutRef.current.unlockLayout();
                    stableLayoutRef.current.recomputeAnchors(cameraRef.current, GROUND_LEVEL);
                  }
                  
                  // Stage all models
                  const modelsToStage = assetsGroup.children as THREE.Object3D[];
                  const stagedModels = stableLayoutRef.current.stageModels(modelsToStage);
                  
                  addDebug(`Layout Engine Ready: ${stableLayoutRef.current.isReady()}`);
                  addDebug(`Models staged: ${stagedModels.length}`);
                  
                  // Log final positions
                  stagedModels.forEach((staged, index) => {
                    const pos = staged.model.position;
                    console.log(`🎯 [VR START] Asset ${index + 1}: (${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)})`);
                    addDebug(`Asset ${index + 1}: (${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)})`);
                  });
                  
                  console.log(`🎯 [VR START] ═══════════════════════════════════════`);
                } else {
                  console.log(`🎯 [VR START] No assets group found or empty`);
                  addDebug(`No assets to layout`);
                }
                
                console.log(`🎯 [VR START] ════════════════════════════════════════\n`);
                
                // The lesson panel shows the start state itself.
                ensureLessonPanel();
              }
            }, 500);
          });
          
          rendererRef.current.xr.addEventListener('sessionend', () => {
            console.log(`${DEBUG_CATEGORIES.XR} VR session ended`);
            setIsPresentingXR(false);
            resetXrReorientation();
            resetTeleport();
            // Back to full fidelity on the flat screen.
            if (rendererRef.current && sceneRef.current) {
              applyRenderBudget(rendererRef.current, sceneRef.current, FLAT_BUDGET);
            }
            // A panel drag still held when the headset came off is dropped, not
            // saved: its owner (a controller or a hand) will never release it
            // now, and while it is set placeLessonPanel skips the re-layout
            // below, leaving the panel where the headset left it.
            panelDragRef.current = null;
            // Hand the flat view back its standing-height layout: the dock was
            // lowered to suit whoever was wearing the headset, and leaving it
            // there would shrink the desktop scene for the next person.
            restoreDeskHeightRef.current();
            setLoadingState('ready');
            
          });
        } catch (vrErr: any) {
          console.error('[XRLessonPlayerV3] VR button creation error:', vrErr);
          addDebug(`VR button error: ${vrErr?.message || vrErr}`);
        }
      }
    
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      setIsSceneReady(true);

      // Screen input for the lesson panel.
      //
      // This used to be inside `if (!isVRSupported)`, so on a desktop browser
      // that reports immersive-vr support but is not presenting, NO handler was
      // attached at all and the panel was completely unclickable. Whether the
      // device could enter VR has nothing to do with whether a mouse is being
      // used, so it is now always attached — the raycast simply finds nothing
      // while a headset session owns the view.
      try {
        setLoadingState('ready');
        ensureLessonPanel();

        const canvas = rendererRef.current.domElement;
        // The page must not pan or pinch-zoom under a finger on the scene: a
        // two-finger pinch on a model sizes the model (below), and the browser
        // taking the same gesture for page zoom would fire pointercancel mid-way.
        canvas.style.touchAction = 'none';
        const handlePointerUp = (event: PointerEvent) => {
          try {
            if (event.button !== 0) return;
            if (rendererRef.current?.xr?.isPresenting) return;
            // A drag is a look-around, not a click. lookControls already applies
            // a 3px threshold, so this is the same gesture distinction the
            // controls use rather than a second, competing one.
            if (lookControlsRef.current?.isDragging()) return;
            if (!rendererRef.current || !sceneRef.current || !cameraRef.current || !raycasterRef.current) return;

            const rect = canvas.getBoundingClientRect();
            const mouse = new THREE.Vector2(
              ((event.clientX - rect.left) / rect.width) * 2 - 1,
              -((event.clientY - rect.top) / rect.height) * 2 + 1
            );
            raycasterRef.current.setFromCamera(mouse, cameraRef.current);

            const panelMesh = lessonPanelRef.current?.mesh;
            if (!panelMesh) return;
            const uv = raycasterRef.current.intersectObject(panelMesh, false)[0]?.uv;
            if (!uv) return;
            // Regions come back from the shared renderer in canvas space, so the
            // UV is all this needs — no per-panel size guessing.
            lessonPanelUvRef.current(uv.x, uv.y);
          } catch (err: any) {
            console.error('[XRLessonPlayerV3] Panel pointer handler error:', err);
          }
        };

        // pointerup, not pointerdown: a click is only a click once we know the
        // gesture did not turn into a drag.
        /*
          Asset interaction on a flat screen.

          Everything above this point only raycast the lesson panel, so on a
          desktop or a phone the 3D assets could not be touched at all — a
          teacher preparing a lesson, or driving one from a laptop, had no way to
          move a model or pick a part. Controllers and hands had this; a mouse
          did not.

          Drag moves the asset across a plane facing the camera; a click without
          a drag selects the part under the cursor, which is what Isolate acts on
          and what a model mark attaches to.
        */
        let assetDrag: {
          model: THREE.Object3D;
          plane: THREE.Plane;
          offset: THREE.Vector3;
          moved: boolean;
          /** Shift was held at the press: the drag turns the model instead of moving it. */
          turn?: { key: string; startX: number; startYaw: number };
        } | null = null;
        /** The pointer holding the panel's grab bar or resize corner, if any. */
        let panelPointerId: number | null = null;

        /*
          Two fingers on a model: spread to size it, twist to turn it.

          Starts when a second finger lands while the first is already dragging
          a model, so a pinch on empty scene is still left to the look controls.
          Lifting either finger ends it, and whichever finger is still down is
          then swallowed until it lifts too — otherwise it would turn back into
          a drag, or land as a tap on the panel.
        */
        const touches = new Map<number, { x: number; y: number }>();
        let touchPinch: { key: string; state: TwoPointState } | null = null;
        const swallowedTouches = new Set<number>();
        const touchSample = (): TwoPointSample => {
          const [first, second] = Array.from(touches.values());
          const dx = second.x - first.x;
          const dy = second.y - first.y;
          // Screen y runs down, so a clockwise twist raises atan2; negated, it
          // turns the model clockwise seen from above — the way the fingers turn
          // when the desk is seen from above it, as the camera sees it.
          return { distance: Math.hypot(dx, dy), angle: -Math.atan2(dy, dx) };
        };

        const pointerToNdc = (event: MouseEvent) => {
          const rect = canvas.getBoundingClientRect();
          return new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
          );
        };

        /** The model under a screen point, or null; the lesson panel in front hides what is behind it. */
        const modelKeyAt = (event: MouseEvent): string | null => {
          const camera = cameraRef.current;
          const raycaster = raycasterRef.current;
          if (!camera || !raycaster || rendererRef.current?.xr?.isPresenting) return null;
          raycaster.setFromCamera(pointerToNdc(event), camera);
          const panelMesh = lessonPanelRef.current?.mesh;
          if (panelMesh?.visible && raycaster.intersectObject(panelMesh, false).length > 0) return null;
          const meshes = stableLayoutRef.current?.getAllInteractableMeshes?.() ?? [];
          const hit = raycaster.intersectObjects(meshes, false)[0];
          if (!hit) return null;
          const model = stableLayoutRef.current?.findRootModel?.(hit.object) ?? hit.object;
          return modelGesturesRef.current.keyOf(model);
        };
        modelKeyAtPointerRef.current = modelKeyAt;

        /*
          The wheel over a model sizes that model; anywhere else it still zooms
          the view (lookControls asks modelKeyAtPointerRef first and stands
          down). A trackpad pinch arrives as a ctrl+wheel, so it lands here too,
          which is what makes pinching a model on a laptop resize it.
        */
        const handleAssetWheel = (event: WheelEvent) => {
          const key = modelKeyAt(event);
          if (!key) return;
          event.preventDefault();
          // Line-mode wheels (Firefox) report a handful of lines, not pixels.
          const pixels = event.deltaY * (event.deltaMode === 1 ? 16 : 1);
          // Clamped per event, so one flick of a free-spinning wheel cannot
          // throw the model straight to a limit.
          const step = Math.max(-0.25, Math.min(0.25, -pixels * WHEEL_SCALE_PER_PX));
          const gestures = modelGesturesRef.current;
          gestures.setScale(key, gestures.scaleOf(key) * Math.exp(step));
        };

        const handleAssetPointerDown = (event: PointerEvent) => {
          if (event.button !== 0) return;
          if (rendererRef.current?.xr?.isPresenting) return;
          if (event.pointerType === 'touch') {
            touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
            const key = touches.size === 2 && assetDrag ? modelGesturesRef.current.keyOf(assetDrag.model) : null;
            if (key && !touchPinch) {
              const gestures = modelGesturesRef.current;
              touchPinch = {
                key,
                state: beginTwoPoint(touchSample(), { scale: gestures.scaleOf(key), yaw: gestures.yawOf(key) }),
              };
              assetDrag = null;
              try {
                canvas.setPointerCapture(event.pointerId);
              } catch {
                /* capture is best-effort */
              }
              return;
            }
          }
          const camera = cameraRef.current;
          const raycaster = raycasterRef.current;
          if (!camera || !raycaster) return;

          raycaster.setFromCamera(pointerToNdc(event), camera);

          // The panel wins: a click on a quiz option must not drag a model that
          // happens to sit behind it.
          const panelMesh = lessonPanelRef.current?.mesh;
          const panelHit = panelMesh?.visible ? raycaster.intersectObject(panelMesh, false)[0] : undefined;
          if (panelHit) {
            // The grab bar and resize corner are handled here, on the way down,
            // because a drag has to start on press; everything else on the panel
            // stays a click, resolved on pointerup by the panel handler.
            const handle = panelHit.uv ? actionAtUv(lessonPanelRegionsRef.current, panelHit.uv.x, panelHit.uv.y) : null;
            if (pressPanelHandle(handle, raycaster.ray.clone(), panelHit.point, 'pointer', event.pointerId)) {
              panelPointerId = event.pointerId;
              // Dragging the panel must not also swing the view.
              lookControlsRef.current?.setEnabled(false);
              try {
                canvas.setPointerCapture(event.pointerId);
              } catch {
                /* capture is best-effort */
              }
            }
            return;
          }

          const meshes = stableLayoutRef.current?.getAllInteractableMeshes?.() ?? [];
          const hit = raycaster.intersectObjects(meshes, false)[0];
          if (!hit) return;

          const model = stableLayoutRef.current?.findRootModel?.(hit.object) ?? hit.object;
          // Drag across a plane through the object that faces the camera, so the
          // model tracks the cursor at its own depth rather than sliding away.
          const normal = camera.getWorldDirection(new THREE.Vector3()).negate();
          const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, hit.point);
          const turnKey = event.shiftKey ? modelGesturesRef.current.keyOf(model) : null;
          assetDrag = {
            model,
            plane,
            offset: model.position.clone().sub(hit.point),
            moved: false,
            turn: turnKey
              ? { key: turnKey, startX: event.clientX, startYaw: modelGesturesRef.current.yawOf(turnKey) }
              : undefined,
          };
          // Suppress look-controls so dragging a model does not also swing the view.
          lookControlsRef.current?.setEnabled(false);
          canvas.setPointerCapture(event.pointerId);
        };

        const handleAssetPointerMove = (event: PointerEvent) => {
          const touch = event.pointerType === 'touch' ? touches.get(event.pointerId) : undefined;
          if (touch) {
            touch.x = event.clientX;
            touch.y = event.clientY;
            if (touchPinch && touches.size === 2) {
              const { scale, yaw } = updateTwoPoint(touchPinch.state, touchSample(), STUDENT_SCALE_LIMITS);
              modelGesturesRef.current.setScale(touchPinch.key, scale);
              modelGesturesRef.current.setYaw(touchPinch.key, yaw);
              return;
            }
          }
          if (panelPointerId !== null && panelPointerId === event.pointerId) {
            const camera = cameraRef.current;
            const raycaster = raycasterRef.current;
            if (!camera || !raycaster || panelDragRef.current?.source !== 'pointer') return;
            raycaster.setFromCamera(pointerToNdc(event), camera);
            updatePanelDrag(raycaster.ray);
            return;
          }
          if (!assetDrag) return;
          if (assetDrag.turn) {
            // Shift+drag: sideways travel turns the model about the vertical.
            // Right turns the near side right, like spinning it on a lazy Susan.
            const dx = event.clientX - assetDrag.turn.startX;
            if (!assetDrag.moved && Math.abs(dx) < 3) return;
            assetDrag.moved = true;
            modelGesturesRef.current.setYaw(assetDrag.turn.key, assetDrag.turn.startYaw + dx * SHIFT_DRAG_TURN_PER_PX);
            return;
          }
          const camera = cameraRef.current;
          const raycaster = raycasterRef.current;
          if (!camera || !raycaster) return;

          raycaster.setFromCamera(pointerToNdc(event), camera);
          const point = new THREE.Vector3();
          if (!raycaster.ray.intersectPlane(assetDrag.plane, point)) return;
          assetDrag.moved = true;
          assetDrag.model.position.copy(point).add(assetDrag.offset);
        };

        const handleAssetPointerUp = (event: PointerEvent) => {
          if (event.pointerType === 'touch') {
            touches.delete(event.pointerId);
            if (swallowedTouches.delete(event.pointerId)) {
              event.stopImmediatePropagation();
              return;
            }
            if (touchPinch) {
              touchPinch = null;
              for (const id of touches.keys()) swallowedTouches.add(id);
              try {
                canvas.releasePointerCapture(event.pointerId);
              } catch {
                /* capture is best-effort */
              }
              lookControlsRef.current?.setEnabled(!markerActiveRef.current);
              event.stopImmediatePropagation();
              return;
            }
          }
          if (panelPointerId !== null && panelPointerId === event.pointerId) {
            panelPointerId = null;
            if (panelDragRef.current?.source === 'pointer') endPanelDrag();
            try {
              canvas.releasePointerCapture(event.pointerId);
            } catch {
              /* capture is best-effort */
            }
            lookControlsRef.current?.setEnabled(!markerActiveRef.current);
            // Not a click: without this the panel handler registered after this
            // one would press whatever button ended up under the released pointer.
            event.stopImmediatePropagation();
            return;
          }
          if (!assetDrag) return;
          const { moved } = assetDrag;
          const model = assetDrag.model;
          assetDrag = null;
          try {
            canvas.releasePointerCapture(event.pointerId);
          } catch {
            /* capture is best-effort */
          }
          lookControlsRef.current?.setEnabled(!markerActiveRef.current);

          if (moved) return;

          // A click, not a drag: select the part, or mark it while the marker is on.
          const camera = cameraRef.current;
          const raycaster = raycasterRef.current;
          if (!camera || !raycaster) return;
          raycaster.setFromCamera(pointerToNdc(event), camera);
          const picked = modelToolsRef.current?.pick(raycaster);
          if (!picked) return;

          if (highlightModeRef.current && classroomRef.current.isClassHost) {
            toggleHighlightRef.current(picked);
            return;
          }
          if (markerActiveRef.current && classroomRef.current.isClassHost) {
            addModelMarkRef.current(model, picked.mesh, raycaster);
            return;
          }
          // Clicking a part targets its asset too, so the very next Explode or
          // Section acts on the model the teacher just pointed at.
          selectPickedPartRef.current(picked.key, picked.partId, picked.name);
        };

        (rendererRef as any)._assetPointerDown = handleAssetPointerDown;
        (rendererRef as any)._assetPointerMove = handleAssetPointerMove;
        (rendererRef as any)._assetPointerUp = handleAssetPointerUp;
        canvas.addEventListener('pointerdown', handleAssetPointerDown);
        canvas.addEventListener('pointermove', handleAssetPointerMove);
        canvas.addEventListener('pointerup', handleAssetPointerUp);
        canvas.addEventListener('pointercancel', handleAssetPointerUp);
        (rendererRef as any)._assetWheel = handleAssetWheel;
        canvas.addEventListener('wheel', handleAssetWheel, { passive: false });

        (rendererRef as any)._panelPointerUp = handlePointerUp;
        canvas.addEventListener('pointerup', handlePointerUp);
      } catch (err: any) {
        console.error('[XRLessonPlayerV3] Panel input init error:', err);
      }
    }

    // Setup lighting
    // High ambient light since skybox uses MeshBasicMaterial (self-illuminating)
    // These lights are mainly for future 3D assets inside the skybox
    // STATE-OF-ART LIGHTING: High-quality lighting for asset rendering
    // Ambient light for base illumination
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);
    
    // Main directional light with shadows
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
    directionalLight.position.set(5, 10, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 50;
    // Only the desk and its models cast or receive shadows, and they sit within
    // a metre or two of the viewer. A ±3 m box spends the shadow map there —
    // about three times the detail of ±10 m, which smeared a model's shadow on
    // the desk top into a faint blur instead of grounding it.
    directionalLight.shadow.camera.left = -3;
    directionalLight.shadow.camera.right = 3;
    directionalLight.shadow.camera.top = 3;
    directionalLight.shadow.camera.bottom = -3;
    directionalLight.shadow.bias = -0.0001;
    scene.add(directionalLight);
    
    // Fill light from opposite side
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
    fillLight.position.set(-5, 5, -5);
    scene.add(fillLight);
    
    // Rim light for edge definition
    const rimLight = new THREE.DirectionalLight(0x88ccff, 0.3);
    rimLight.position.set(0, 5, -10);
    scene.add(rimLight);
    
    // Add hemisphere light for natural lighting (sky/ground gradient)
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.6);
    hemiLight.position.set(0, 20, 0);
    scene.add(hemiLight);
    
    console.log('[XRLessonPlayerV3] Lights added: Ambient, Directional, Hemisphere');
    
    // ═══════════════════════════════════════════════════════════════════════════
    // GROUND PLANE - Invisible reference surface for consistent asset placement
    // This provides a fixed Y=0 reference for all 3D assets
    // ═══════════════════════════════════════════════════════════════════════════
    const GROUND_Y = 0; // Ground level at Y=0
    const groundGeometry = new THREE.PlaneGeometry(50, 50); // 50m x 50m plane
    const groundMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0,           // Fully transparent - won't block skybox
      side: THREE.DoubleSide,
      depthWrite: false,    // Don't write to depth buffer
    });
    const groundPlane = new THREE.Mesh(groundGeometry, groundMaterial);
    groundPlane.rotation.x = -Math.PI / 2; // Rotate to horizontal (XZ plane)
    groundPlane.position.y = GROUND_Y;
    groundPlane.name = 'groundPlane';
    groundPlane.userData.isGround = true;
    groundPlane.userData.groundY = GROUND_Y;
    groundPlane.renderOrder = -1; // Render first (behind everything)
    scene.add(groundPlane);
    
    // Store ground reference for asset positioning
    groundPlaneRef.current = groundPlane;

    /*
      Locomotion. Landing is restricted to the ground plane and blocked near any
      interactable, so a student cannot teleport inside a model they are meant to
      be looking at.
    */
    teleportRef.current?.dispose();
    teleportRef.current = createTeleport({
      scene,
      floors: [groundPlane],
      obstacles: () => stableLayoutRef.current?.getAllInteractableMeshes?.() ?? [],
    });
    
    // Also add a subtle grid helper for development (semi-transparent)
    // This helps visualize the ground plane during testing
    const gridHelper = new THREE.GridHelper(20, 20, 0x444444, 0x222222);
    gridHelper.position.y = GROUND_Y + 0.001; // Slightly above ground to prevent z-fighting
    gridHelper.material.transparent = true;
    gridHelper.material.opacity = 0.15; // Very subtle
    gridHelper.name = 'groundGrid';
    gridHelper.visible = false; // Hidden by default - can enable for debugging
    scene.add(gridHelper);
    
    console.log(`[XRLessonPlayerV3] Ground plane added at Y=${GROUND_Y}`);
    
    // ═══════════════════════════════════════════════════════════════════════
    // PROFESSIONAL LAYOUT SYSTEM INITIALIZATION
    // Handles zone management, collision detection, and asset placement
    // ═══════════════════════════════════════════════════════════════════════
    const layoutSystem = new ProfessionalLayoutSystem({
      uiZone: {
        distance: 2.0,
        height: 0.0,
        width: 1.2,
        depth: 0.1,
      },
      assetZone: {
        minDistance: 2.0,
        maxDistance: 4.0,
        horizontalSpread: 90,
        verticalOffset: TABLE_HEIGHT,
      },
      interactionZone: {
        minDistance: 0.5,
        maxDistance: 5.0,
        floorY: GROUND_LEVEL,
        ceilingY: 3.0,
      },
    });
    layoutSystem.setNormalizedSize(NORMALIZED_SIZE);
    professionalLayoutRef.current = layoutSystem;
    
    console.log(`[XRLessonPlayerV3] Professional Layout System initialized`);
    addDebug(`═══ PROFESSIONAL LAYOUT SYSTEM ═══`);
    addDebug(`Normalized Size: ${NORMALIZED_SIZE}m`);
    addDebug(`UI Zone: 2.0m distance, left side`);
    addDebug(`Asset Zone: 2.0-4.0m, right side`);
    addDebug(`Collision Detection: ENABLED`);
    
    // ═══════════════════════════════════════════════════════════════════════
    // VR LESSON EXPERIENCE - World-Class VR EdTech Layout
    // Creates stage platform, professional lighting, and natural grab controls
    // ═══════════════════════════════════════════════════════════════════════
    const vrExperience = new VRLessonExperience({
      panelSide: 'left',
      panel: {
        distance: 2.0,
        width: 1.2,
        height: 1.4,
        horizontalOffset: -1.0,
        verticalOffset: 0.0,
        tiltAngle: -8,
      },
      assetStage: {
        distance: 2.5,
        width: 3.0,
        depth: 2.0,
        horizontalOffset: 0.8,
        floorHeight: GROUND_LEVEL,
      },
      normalizedSize: NORMALIZED_SIZE,
      modelSpacing: 0.4,
    });
    vrExperience.initialize(scene);
    vrExperienceRef.current = vrExperience;
    
    console.log(`[XRLessonPlayerV3] VR Lesson Experience initialized`);
    addDebug(`═══ VR LESSON EXPERIENCE ═══`);
    addDebug(`Panel: LEFT side, 2.0m distance`);
    addDebug(`Asset Stage: RIGHT side, circular platform`);
    addDebug(`Stage Lighting: Spotlight + Ambient`);
    addDebug(`Natural Grab: ENABLED`);
    
    // Initialize raycaster for VR controller interaction
    const raycaster = new THREE.Raycaster();
    raycasterRef.current = raycaster;
    
    // Initialize XR Controller Model Factory
    const controllerModelFactory = new XRControllerModelFactory();
    controllerModelFactoryRef.current = controllerModelFactory;
    
    /*
      Hands.

      Set up unconditionally: getHand() returns a Group that simply never reports
      joints on a headset without hand tracking, so there is nothing to detect and
      nothing to switch off. Controllers keep working alongside, which is what a
      classroom of mixed hardware needs.
    */
    handsRef.current = [0, 1].map((index) => {
      const hand = rendererRef.current!.xr.getHand(index) as THREE.Group;
      hand.add(new OculusHandModel(hand));
      scene.add(hand);

      /*
        The pointer.

        getHand(index) and getController(index) address the SAME input slot, so
        the controller's ray line kept drawing while a hand ray drew as well —
        two pointers per hand. There is one now: this model while the hand is
        tracked, the controller's own line otherwise.
      */
      const controller = rendererRef.current!.xr.getController(index) as THREE.Group;
      const pointer = new OculusHandPointerModel(hand, controller);
      hand.add(pointer);

      const grabProxy = new THREE.Object3D();
      grabProxy.name = `handGrabProxy${index}`;
      scene.add(grabProxy);

      return {
        hand,
        tracker: createHandGestureTracker(),
        pointer,
        controllerRay: null,
        grabProxy,
        grabbing: false,
        pinchedAt: 0,
        last: null,
      };
    });

    // Create reticle for raycast visualization
    const reticleGeometry = new THREE.RingGeometry(0.02, 0.04, 32);
    const reticleMaterial = new THREE.MeshBasicMaterial({ 
      color: 0xffffff,
      transparent: true,
      opacity: 0.8
    });
    const reticle = new THREE.Mesh(reticleGeometry, reticleMaterial);
    reticle.matrixAutoUpdate = false;
    reticle.visible = false;
    reticle.name = 'reticle';
    scene.add(reticle);
    reticleRef.current = reticle;
    
    // Setup XR Controllers
    const controller1 = rendererRef.current.xr.getController(0);
    const controller2 = rendererRef.current.xr.getController(1);
    
    // Setup controller connection handlers (only once per controller)
    const setupController = (controllerIndex: number, controller: THREE.Group) => {
      if (controllersSetupRef.current.has(controllerIndex)) {
        return; // Already setup
      }
      
      if (!rendererRef.current) {
        console.error('[XRLessonPlayerV3] Renderer not initialized when setting up controller');
        return;
      }
      
      controllersSetupRef.current.add(controllerIndex);
      addDebug(`Controller ${controllerIndex + 1} connected`);
      
      const controllerGrip = rendererRef.current.xr.getControllerGrip(controllerIndex);
      const gripModel = controllerModelFactory.createControllerModel(controllerGrip);
      controllerGrip.add(gripModel);
      scene.add(controllerGrip);
      
      if (controllerIndex === 0) {
        controller1Ref.current = controller;
      } else {
        controller2Ref.current = controller;
      }
      
      scene.add(controller);
      
      // Add ray visualization
      const geometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(0, 0, -1)
      ]);
      const line = new THREE.Line(geometry);
      line.name = 'ray';
      line.scale.z = 5;
      controller.add(line);
      
      // Store input source for haptic feedback (update if session available)
      if (rendererRef.current?.xr?.getSession()) {
        const session = rendererRef.current.xr.getSession();
        if (session.inputSources && session.inputSources[controllerIndex]) {
          if (!inputSourcesRef.current) {
            inputSourcesRef.current = [];
          }
          inputSourcesRef.current[controllerIndex] = session.inputSources[controllerIndex];
          console.log(`[HAPTIC] Input source ${controllerIndex} stored for haptic feedback`);
        }
      }
      
      // Controller select handlers (debounced)
      const debouncedSelect = (() => {
        let timeout: NodeJS.Timeout | null = null;
        return () => {
          if (timeout) clearTimeout(timeout);
          timeout = setTimeout(() => {
            handleControllerSelect(controller);
          }, 50);
        };
      })();
      
      controller.addEventListener('selectstart', debouncedSelect);
    };
    
    controller1.addEventListener('connected', () => setupController(0, controller1));
    controller2.addEventListener('connected', () => setupController(1, controller2));

    /*
      What each controller slot is holding right now, and whether its select is
      down.

      A tracked hand is ALSO a controller as far as three.js is concerned: its
      input source fires selectstart on a pinch, which reached
      handleControllerSelect ~50ms after updateHands had already acted on the
      same pinch. Every hand press on the panel therefore landed twice. Recorded
      on every 'connected' (not just the first, which setupController guards)
      because a slot switches between a controller and a hand whenever the
      student puts the controller down.

      `selecting` is what ends a controller's panel drag; see
      updateControllerPanelDrag.
    */
    [controller1, controller2].forEach((controller) => {
      controller.addEventListener('connected', (event) => {
        const source = (event as { data?: XRInputSource }).data ?? null;
        controller.userData.inputSource = source;
        controller.userData.isHand = Boolean(source?.hand);
      });
      controller.addEventListener('disconnected', () => {
        controller.userData.inputSource = null;
        controller.userData.isHand = false;
        controller.userData.selecting = false;
        controller.userData.squeezing = false;
        controller.userData.gripKey = null;
      });
      controller.addEventListener('selectstart', () => {
        controller.userData.selecting = true;
      });
      controller.addEventListener('selectend', () => {
        controller.userData.selecting = false;
      });
      /*
        The grip, for the model gestures in updateControllerModelGestures.

        A tracked hand's input source fires squeeze for a fist, which
        updateHands already reads from the joints — so, as with select above,
        hands are ignored here. The model a grip acts on is decided once, at
        the press: the one the controller points at, else the nearest one
        within reach. Deciding it every frame would hand the gesture to
        whichever model the controller happened to drift across.
      */
      controller.addEventListener('squeezestart', () => {
        if (controller.userData.isHand) return;
        const gestures = modelGesturesRef.current;
        const ray = controllerRay(controller);
        controller.userData.squeezing = true;
        controller.userData.gripKey = gestures.keyAlongRay(ray) ?? gestures.nearestKey(ray.origin, GRIP_REACH_M);
      });
      controller.addEventListener('squeezeend', () => {
        controller.userData.squeezing = false;
        controller.userData.gripKey = null;
      });
    });
    
    // Controller interaction handler
    const handleControllerSelect = (controller: THREE.Group) => {
      try {
        if (!raycasterRef.current || !controller) return;
        // Hands are handled in updateHands, which has already acted on this
        // pinch; see the 'connected' tracking above.
        if (controller.userData.isHand) return;
        
        const raycaster = raycasterRef.current;
        const tempMatrix = new THREE.Matrix4();
        tempMatrix.identity().extractRotation(controller.matrixWorld);
        
        const direction = new THREE.Vector3(0, 0, -1);
        direction.applyMatrix4(tempMatrix);
        
        const origin = new THREE.Vector3();
        controller.getWorldPosition(origin);
        
        raycaster.set(origin, direction);
        
        // Check the lesson panel first, so a button press is never stolen by an
        // asset behind it.
        const panelMesh = lessonPanelRef.current?.mesh;
        // Only a panel that is showing: a hidden mesh still intersects, and would
        // swallow the press meant for the model behind where it was.
        const panelIntersects = panelMesh?.visible ? raycaster.intersectObject(panelMesh, false) : [];

        if (panelIntersects.length > 0 && panelIntersects[0].uv) {
          const uv = panelIntersects[0].uv;
          // The grab bar and resize corner start a drag this controller carries
          // until its trigger comes up (updateControllerPanelDrag).
          const handle = actionAtUv(lessonPanelRegionsRef.current, uv.x, uv.y);
          if (pressPanelHandle(handle, raycaster.ray.clone(), panelIntersects[0].point, 'controller', controller)) {
            triggerHapticFeedback(controller);
            return;
          }
          const buttonId = `lessonPanel_${Math.round(uv.x * 100)}_${Math.round(uv.y * 100)}`;
          const now = Date.now();
          const lastClick = lastGrabTimeRef.current.get(buttonId) || 0;
          if (now - lastClick > 100) {
            lastGrabTimeRef.current.set(buttonId, now);
            triggerHapticFeedback(controller);
            lessonPanelUvRef.current(uv.x, uv.y);
          }
          return;
        }
        
        // ═══════════════════════════════════════════════════════════════
        // CRASH-SAFE 3D OBJECT GRABBING
        // Uses cached interactables from Stable Layout System
        // NO SCENE TRAVERSAL during interaction to prevent crash
        // ═══════════════════════════════════════════════════════════════
        
        // Guard against rapid-fire interactions
        const guard = interactionGuardRef.current;
        const now = Date.now();
        
        if (guard.isProcessing) {
          console.log('[XRLessonPlayerV3] CRASH GUARD: Interaction already processing, skipping');
          return;
        }
        
        // Rate limit: max 10 interactions per second
        if (now - guard.lastInteractionTime < 100) {
          guard.interactionCount++;
          if (guard.interactionCount > 10) {
            console.warn('[XRLessonPlayerV3] CRASH GUARD: Interaction rate limit hit');
            return;
          }
        } else {
          guard.interactionCount = 0;
        }
        guard.lastInteractionTime = now;
        guard.isProcessing = true;
        
        try {
          // Separate UI and asset layers for clean interaction
          const uiPanels: THREE.Mesh[] = [];
          const assetObjects: THREE.Object3D[] = [];
          
          // Collect UI panels first (priority)
          const lessonMesh = lessonPanelRef.current?.mesh;
          if (lessonMesh && lessonMesh.visible) uiPanels.push(lessonMesh);
          
          // Collect asset objects (only if not hitting UI)
          // CRITICAL FIX: Use getAllInteractableMeshes() for reliable raycast hit detection
          if (stableLayoutRef.current) {
            const interactableMeshes = stableLayoutRef.current.getAllInteractableMeshes();
            interactableMeshes.forEach((mesh) => {
              if (mesh.visible) {
                assetObjects.push(mesh);
              }
            });
            console.log(`[RAYCAST] Interactable meshes: ${assetObjects.length}`);
          }
          
          // Raycast UI layer first (priority)
          const uiIntersects = raycaster.intersectObjects(uiPanels, false);
          if (uiIntersects.length > 0) {
            const hitObject = uiIntersects[0].object;
            
            // Handle UI panel button clicks
            if (hitObject.userData.hasButtons && hitObject.userData.buttons) {
              const buttons = hitObject.userData.buttons;
              const canvasWidth = hitObject.userData.canvasWidth || 1000;
              const canvasHeight = hitObject.userData.canvasHeight || 700;
              
              // Convert 3D intersection to 2D canvas coordinates
              // Panel geometry is 2.0 x 1.4 units (width x height)
              const localPoint = new THREE.Vector3();
              hitObject.worldToLocal(localPoint.copy(uiIntersects[0].point));
              
              // Convert from local space (-1 to 1) to canvas coordinates (0 to width/height)
              // Panel extends from -1.0 to +1.0 in X, -0.7 to +0.7 in Y
              const canvasX = ((localPoint.x + 1.0) / 2.0) * canvasWidth;
              const canvasY = ((1.0 - localPoint.y) / 1.4) * canvasHeight; // Flip Y axis
              
              console.log(`[UI] Raycast hit at local (${localPoint.x.toFixed(3)}, ${localPoint.y.toFixed(3)}) -> canvas (${canvasX.toFixed(0)}, ${canvasY.toFixed(0)})`);
              
              // Check which button was clicked
              for (const button of buttons) {
                const { bounds, action } = button;
                if (
                  canvasX >= bounds.x &&
                  canvasX <= bounds.x + bounds.width &&
                  canvasY >= bounds.y &&
                  canvasY <= bounds.y + bounds.height
                ) {
                  console.log(`[UI] Button clicked at (${canvasX.toFixed(0)}, ${canvasY.toFixed(0)})`);
                  triggerHapticFeedback(controller);
                  action();
                  guard.isProcessing = false;
                  return;
                }
              }
            }
            
            guard.isProcessing = false;
            return; // UI handled, don't process as asset grab
          }
          
          // If no UI hit, check asset layer
          // CRITICAL FIX: Now raycasting against all meshes, not just root models
          const assetIntersects = raycaster.intersectObjects(assetObjects, false); // false = we already have meshes
          
          if (assetIntersects.length > 0) {
            const hitMesh = assetIntersects[0].object;
            
            // CRITICAL: Find the root model from the hit mesh
            let rootModel: THREE.Object3D | null = null;
            if (stableLayoutRef.current) {
              rootModel = stableLayoutRef.current.findRootModel(hitMesh);
            }
            
            // Log hit details for debugging
            console.log(`[RAYCAST] Hit mesh: "${hitMesh.name}" (${hitMesh.uuid.substring(0, 8)})`);
            console.log(`[RAYCAST] Root model: "${rootModel?.name || 'NOT FOUND'}" (${rootModel?.uuid.substring(0, 8) || 'N/A'})`);
            console.log(`[RAYCAST] Slot index: ${rootModel?.userData.slotIndex ?? 'N/A'}`);
            
            if (!rootModel) {
              console.warn(`[RAYCAST] ⚠️ Could not find root model for hit mesh "${hitMesh.name}"`);
              addDebug(`⚠️ Raycast hit mesh but no root model found`);
              guard.isProcessing = false;
              return;
            }
            
            const objId = rootModel.uuid || rootModel.name || 'unknown';
            
            // Debounce grabs. Not highlight taps: the debounce is per model, so
            // it would swallow a quick second tap on another part of the same
            // model, and a tap never starts a grab for it to protect.
            const highlightTap = highlightModeRef.current && classroomRef.current.isClassHost;
            const lastGrab = lastGrabTimeRef.current.get(objId) || 0;
            if (!highlightTap && now - lastGrab < 300) {
              guard.isProcessing = false;
              return;
            }
            lastGrabTimeRef.current.set(objId, now);

            // Whatever the teacher reaches for becomes the target of the model
            // controls, so Explode, Isolate and Section act on the model just
            // pointed at rather than on whichever asset happened to load first.
            // Students run this too, but publishing is host-only, so it only
            // ever changes their own local target.
            const pickedPart = modelToolsRef.current?.pick(raycaster);
            // In highlight mode the trigger is a tap on a part, not a grab: the
            // teacher is pointing something out, and the model jumping into
            // their hand would move it out from under the tag.
            if (pickedPart && highlightTap) {
              toggleHighlightRef.current(pickedPart);
              triggerHapticFeedback(controller);
              return;
            }
            if (pickedPart) {
              selectPickedPartRef.current(pickedPart.key, pickedPart.partId, pickedPart.name);
            }

            // Priority 1: Stable Layout System (crash-safe)
            if (stableLayoutRef.current) {
              const grabbed = stableLayoutRef.current.startGrab(rootModel, controller);
              if (grabbed) {
                // Mark root model as grabbed to disable gravity
                rootModel.userData.isGrabbed = true;
                // Trigger haptic feedback on successful grab
                triggerHapticFeedback(controller);
                console.log(`[GRAB] ✅ Successfully grabbed: "${rootModel.name}" (slot ${rootModel.userData.slotIndex})`);
                addDebug(`🎯 Grabbed: ${rootModel.name || 'object'} (slot ${rootModel.userData.slotIndex})`);
              } else {
                console.warn(`[GRAB] ❌ Failed to grab: "${rootModel.name}"`);
                addDebug(`❌ Grab failed: ${rootModel.name || 'object'}`);
              }
            }
            // Priority 2: VR Experience (legacy)
            else if (vrExperienceRef.current) {
              const grabbed = vrExperienceRef.current.startGrab(rootModel, controller);
              if (grabbed) {
                rootModel.userData.isGrabbed = true;
                rootModel.userData.grabController = controller;
                triggerHapticFeedback(controller);
                addDebug(`🎯 Grabbed: ${rootModel.name || 'object'} (VR Experience)`);
              }
            }
          }
        } catch (grabErr) {
          console.error('[XRLessonPlayerV3] CRASH GUARD: Error in grab:', grabErr);
        } finally {
          guard.isProcessing = false;
        }
      } catch (err: any) {
        console.error('[XRLessonPlayerV3] Error in handleControllerSelect:', err);
      }
    };
    
    // Handle controller release (CRASH-SAFE)
    const handleControllerRelease = (controller: THREE.Group) => {
      try {
        // ═══════════════════════════════════════════════════════════════
        // CRASH-SAFE RELEASE - Priority order for stability
        // ═══════════════════════════════════════════════════════════════
        
        // Priority 1: Stable Layout System
        if (stableLayoutRef.current && stableLayoutRef.current.isGrabbing()) {
          const grabbedModel = stableLayoutRef.current.getGrabbedModel();
          stableLayoutRef.current.releaseGrab();
          
          if (grabbedModel) {
            grabbedModel.userData.isGrabbed = false;
            grabbedModel.userData.grabController = null;
            addDebug(`🎯 Released: ${grabbedModel.name || 'object'} (Stable Layout)`);
          }
          return;
        }
        
        // Priority 2: VR Experience
        if (vrExperienceRef.current && vrExperienceRef.current.isGrabbing()) {
          const grabbedObj = vrExperienceRef.current.getGrabbedObject();
          vrExperienceRef.current.releaseGrab();
          
          if (grabbedObj) {
            grabbedObj.userData.isGrabbed = false;
            grabbedObj.userData.grabController = null;
            addDebug(`🎯 Released: ${grabbedObj.name || 'object'} (VR Experience)`);
          }
          return;
        }
        
        // Priority 3: Fallback (NO SCENE TRAVERSAL - only check assets group)
        const assetsGroup = sceneRef.current?.getObjectByName('assetsGroup');
        if (assetsGroup) {
          assetsGroup.children.forEach((obj) => {
            if (obj.userData.isGrabbed && obj.userData.grabController === controller) {
              obj.userData.isGrabbed = false;
              obj.userData.grabController = null;
              addDebug(`Released: ${obj.name || 'object'}`);
            }
          });
        }
      } catch (err: any) {
        console.error('[XRLessonPlayerV3] CRASH GUARD: Error in handleControllerRelease:', err);
      }
    };
    
    // Add release handlers
    controller1.addEventListener('selectend', () => handleControllerRelease(controller1));
    controller2.addEventListener('selectend', () => handleControllerRelease(controller2));
    
    
    // Drag-to-look. Without this the camera is immovable on a flat screen, and
    // view sync has nothing to read from or write to.
    if (rendererRef.current && cameraRef.current) {
      lookControlsRef.current?.dispose();
      lookControlsRef.current = createLookControls({
        camera: cameraRef.current,
        domElement: rendererRef.current.domElement,
        // Stand down entirely in a headset — the device owns the pose there.
        isPresenting: () => rendererRef.current?.xr?.isPresenting === true,
        // Over a model the wheel sizes the model instead (handleAssetWheel).
        shouldZoom: (event) => modelKeyAtPointerRef.current(event) === null,
        onChange: (h, v, fov) => {
          // ONLY a real drag suspends teacher-view follow. onChange also fires
          // while the teacher's own view is being applied, and marking that as
          // "the student is looking around" would make follow block itself for
          // the whole grace window on every update.
          if (lookControlsRef.current?.isDragging()) {
            classroomRef.current.markStudentLooking();
          }
          viewListenersRef.current.forEach((listener) => listener(h, v, fov));
        },
      });
    }

    // Animation loop (XR-compatible) - only if renderer exists
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      lastAnimationTimeRef.current = performance.now() / 1000;
      rendererRef.current.setAnimationLoop((time: number) => {
        try {
          const now = time / 1000;
          const delta = lastAnimationTimeRef.current ? Math.min(now - lastAnimationTimeRef.current, 0.1) : 0;
          lastAnimationTimeRef.current = now;
          animationMixersRef.current.forEach((mixer) => mixer.update(delta));
          lookControlsRef.current?.update();
          inkLayerRef.current?.update();
          // Keeps an active cross-section on the model when the model is moved.
          // Returns immediately when nothing is being sectioned.
          modelToolsRef.current?.update();
          // Tags follow their parts through explode and grabs, and face
          // whichever eye rig is rendering. Returns at once with none up.
          if (partLabelsRef.current && rendererRef.current && cameraRef.current) {
            partLabelsRef.current.update(
              rendererRef.current.xr.isPresenting ? rendererRef.current.xr.getCamera() : cameraRef.current,
              now
            );
          }
          updateHands();
          updateControllerPanelDrag();
          if (teleportRef.current && rendererRef.current && cameraRef.current) {
            // The XR camera when presenting, the flat one otherwise, so the
            // vignette is centred on whichever eye rig is actually rendering.
            const viewer = rendererRef.current.xr.isPresenting
              ? rendererRef.current.xr.getCamera()
              : cameraRef.current;
            teleportRef.current.update(delta, viewer);
          }

          /*
            Turn the panel to face the viewer — about the vertical axis only.

            A full lookAt tips the panel toward the head, so glancing down at the
            models left it pitched back and harder to read, and in a headset the
            tilt changes continuously as you move. Turning in place is what a
            board on a stand does, and it is what the eye expects.
          */
          if (lessonPanelRef.current && cameraRef.current) {
            const panelMesh = lessonPanelRef.current.mesh;
            const toViewer = cameraRef.current.getWorldPosition(new THREE.Vector3());
            panelMesh.rotation.set(
              0,
              Math.atan2(toViewer.x - panelMesh.position.x, toViewer.z - panelMesh.position.z),
              0
            );
          }
          
          // Gravity simulation: Make assets rest on dock when not grabbed
          if (assetsGroupRef.current && sceneLayoutRef.current && stableLayoutRef.current) {
            const isGrabbing = stableLayoutRef.current.isGrabbing();
            const dockSurfaceY = sceneLayoutRef.current.getAssetDockSurfaceY(GROUND_LEVEL);
            
            /*
              Settle assets onto the dock surface.

              Measured from the bottom of the bounding box, the same rule
              placeAssetOnDock uses. It used to target `dockSurfaceY + height/2`,
              which assumes the group's origin sits at the centre of the
              geometry — so for a model authored off its own origin this ran
              every frame and dragged it straight back off the dock, undoing the
              placement.

              Skipped for a model while it is exploded: the parts have moved,
              so the box no longer describes the model, and settling on it would
              shove the whole asset upward as it is pulled apart. Decided per
              model, because a student can pull one apart while the others
              stand assembled and still need settling.
            */
            const tools = modelToolsRef.current;
            assetsGroupRef.current.children.forEach((asset) => {
              const obj = asset as THREE.Object3D;
              const key = tools?.keyOf(obj);
              const exploded = tools && key ? tools.explodeAmount(key) > 0 : modelExplodeRef.current > 0;

              // Check if this specific asset is being grabbed
              const isThisAssetGrabbed = obj.userData.isGrabbed || isGrabbing;
              
              if (
                exploded ||
                !obj.userData.restsOnDock ||
                isThisAssetGrabbed ||
                obj.userData.dockSurfaceY === undefined
              ) {
                return;
              }

              const box = new THREE.Box3().setFromObject(obj);
              const drop = box.min.y - obj.userData.dockSurfaceY;
              if (drop > 0.01) {
                // Floating: fall, but never past the surface.
                obj.position.y -= Math.min(0.015, drop);
                obj.updateMatrixWorld(true);
              } else if (drop < -0.01) {
                // Sunk through the surface: put it back on top.
                obj.position.y -= drop;
                obj.updateMatrixWorld(true);
              }
            });
          }
          
          // Ground plane is always transparent (no conditional visibility)
          
          // Update raycast for controllers (for hover feedback)
          if (raycasterRef.current && reticleRef.current && controller1Ref.current) {
            const raycaster = raycasterRef.current;
            const reticle = reticleRef.current;
            const controller = controller1Ref.current;
            
            if (controller.visible) {
              const tempMatrix = new THREE.Matrix4();
              tempMatrix.identity().extractRotation(controller.matrixWorld);
              
              const direction = new THREE.Vector3(0, 0, -1);
              direction.applyMatrix4(tempMatrix);
              
              const origin = new THREE.Vector3();
              controller.getWorldPosition(origin);
              
              raycaster.set(origin, direction);
              
              // Separate UI layer from asset layer for clean raycast
              const uiPanels: THREE.Mesh[] = [];
              const assetObjects: THREE.Object3D[] = [];
              
              // Collect UI panels (layer: 'ui')
              const lessonMesh = lessonPanelRef.current?.mesh;
              if (lessonMesh && lessonMesh.visible) uiPanels.push(lessonMesh);
              
              // Collect asset objects (layer: 'asset')
              if (sceneRef.current) {
                sceneRef.current.traverse((obj) => {
                  if (obj.userData.isInteractable && obj.visible && obj.userData.layer === 'asset') {
                    assetObjects.push(obj);
                  }
                });
              }
              
              // Priority: UI layer first, then assets
              const uiIntersects = raycaster.intersectObjects(uiPanels, false);
              const assetIntersects = uiIntersects.length === 0 
                ? raycaster.intersectObjects(assetObjects, false)
                : [];
              
              const intersects = [...uiIntersects, ...assetIntersects];
              
              if (intersects.length > 0) {
                const intersect = intersects[0];
                reticle.visible = true;
                reticle.position.copy(intersect.point);
                reticle.lookAt(origin);
                
                // Highlight hovered object (only 3D objects, not panels)
                const obj = intersect.object;
                // The lesson panel is interactable too, but it is not a 3D object:
                // growing it by 5% on hover, and restoring a remembered scale when
                // the ray leaves, would undo any resize made while pointing at it.
                if (obj.userData.isInteractable && obj.userData.layer !== 'ui' && !obj.userData.hasButtons) {
                  if (hoveredObjectRef.current !== obj) {
                    // Remove previous highlight
                    if (hoveredObjectRef.current && hoveredObjectRef.current.userData.originalScale) {
                      hoveredObjectRef.current.scale.copy(hoveredObjectRef.current.userData.originalScale);
                    }
                    
                    // Add new highlight
                    hoveredObjectRef.current = obj;
                    if (!obj.userData.originalScale) {
                      obj.userData.originalScale = new THREE.Vector3().copy(obj.scale);
                    }
                    obj.scale.multiplyScalar(1.05);
                  }
                } else {
                  // Clear 3D object highlight when hovering panels
                  if (hoveredObjectRef.current && hoveredObjectRef.current.userData.originalScale) {
                    hoveredObjectRef.current.scale.copy(hoveredObjectRef.current.userData.originalScale);
                    hoveredObjectRef.current = null;
                  }
                }
              } else {
                reticle.visible = false;
                if (hoveredObjectRef.current && hoveredObjectRef.current.userData.originalScale) {
                  hoveredObjectRef.current.scale.copy(hoveredObjectRef.current.userData.originalScale);
                  hoveredObjectRef.current = null;
                }
              }
            }
          }
          
          // Update grabbed objects position AND handle rotation/scale
          if (sceneRef.current) {
            // Each controller's own thumbstick; see readStick for why not one reading.
            const xr = rendererRef.current?.xr;
            const leftController = xr ? xr.getController(0) : null;
            const rightController = xr ? xr.getController(1) : null;
            const leftStick = gestureScratch.stick0;
            const rightStick = gestureScratch.stick1;
            readStick(leftController, leftStick);
            readStick(rightController, rightStick);
            const gripHeld =
              leftController?.userData.squeezing === true || rightController?.userData.squeezing === true;

            /*
              Teleport aiming.

              Only when nothing is grabbed: the thumbstick already rotates and
              scales a held model below, and stealing it mid-grab would make both
              behaviours unpredictable. Nor while a grip is held, when the stick
              explodes the gripped model instead. Push forward to aim, release to
              travel; the arc leaves the controller whose stick was pushed.
            */
            const holdingSomething = stableLayoutRef.current?.isGrabbing() ?? false;
            const teleport = teleportRef.current;
            if (teleport && !holdingSomething && !gripHeld) {
              const aimSource =
                leftStick.y < -0.7 ? leftController : rightStick.y < -0.7 ? rightController : null;
              if (aimSource && !teleportArmedRef.current) {
                teleportArmedRef.current = true;
                teleport.beginAim(aimSource);
              } else if (!aimSource && teleportArmedRef.current) {
                teleportArmedRef.current = false;
                if (rendererRef.current) teleport.commit(rendererRef.current);
              }
              if (teleport.isAiming()) teleport.updateAim();
            } else if (teleport?.isAiming()) {
              // Grabbed or gripped something mid-aim: abandon it rather than
              // teleporting while holding an object.
              teleport.cancel();
              teleportArmedRef.current = false;
            }
            updateControllerModelGestures(delta);

            // ═══════════════════════════════════════════════════════════════
            // CRASH-SAFE GRAB UPDATE - Use Stable Layout System
            // NO TRAVERSAL - uses cached interactables only
            // ═══════════════════════════════════════════════════════════════
            try {
              // Priority 1: Stable Layout System (crash-safe)
              if (stableLayoutRef.current && stableLayoutRef.current.isGrabbing()) {
                stableLayoutRef.current.updateGrab();
                
                /*
                  The holding controller's own stick turns and sizes what it
                  holds: X turns it, Y sizes it, within STUDENT_SCALE_LIMITS of
                  the size the dock gave it — the same limits as every other
                  gesture. It goes through the local overrides, so a relayout
                  keeps the result. A controller that is also gripping is
                  exploding with its stick instead (updateControllerModelGestures).
                */
                const grabbedModel = stableLayoutRef.current.getGrabbedModel();
                const holder =
                  leftController?.userData.selecting && !leftController.userData.isHand
                    ? leftController
                    : rightController?.userData.selecting && !rightController.userData.isHand
                      ? rightController
                      : null;
                const gestures = modelGesturesRef.current;
                const heldKey = grabbedModel ? gestures.keyOf(grabbedModel) : null;
                if (holder && heldKey && !holder.userData.squeezing) {
                  const stick = holder === leftController ? leftStick : rightStick;
                  if (Math.abs(stick.x) > 0.1) {
                    gestures.setYaw(heldKey, gestures.yawOf(heldKey) + stick.x * HELD_TURN_RATE * delta);
                  }
                  if (Math.abs(stick.y) > 0.1) {
                    gestures.setScale(heldKey, gestures.scaleOf(heldKey) * Math.exp(stick.y * HELD_SCALE_RATE * delta));
                  }
                }
              }
              // Priority 2: VR Experience (legacy)
              else if (vrExperienceRef.current && vrExperienceRef.current.isGrabbing()) {
                vrExperienceRef.current.updateGrab();
              }
            } catch (grabErr) {
              console.error('[XRLessonPlayerV3] CRASH GUARD: Error in grab update:', grabErr);
            }
          }
          
          if (rendererRef.current && sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
          }
        } catch (renderErr: any) {
          console.error('[XRLessonPlayerV3] Render error:', renderErr);
          // Don't stop the loop, just log the error
        }
      });
    }
    
    // Handle resize
    const handleResize = () => {
      try {
        if (cameraRef.current && rendererRef.current) {
          cameraRef.current.aspect = window.innerWidth / window.innerHeight;
          cameraRef.current.updateProjectionMatrix();
          rendererRef.current.setSize(window.innerWidth, window.innerHeight);
        }
      } catch (resizeErr: any) {
        console.error('[XRLessonPlayerV3] Resize error:', resizeErr);
      }
    };
    window.addEventListener('resize', handleResize);
    
      // Cleanup
      return () => {
        setIsSceneReady(false);
        try {
          window.removeEventListener('resize', handleResize);
          // Remove desktop pointer handler if we attached one
          try {
            const canvasEl = rendererRef.current?.domElement;
            const panelHandler = (rendererRef as any)?._panelPointerUp;
            if (panelHandler) {
              canvasEl?.removeEventListener('pointerup', panelHandler);
              (rendererRef as any)._panelPointerUp = null;
            }
            const assetDown = (rendererRef as any)?._assetPointerDown;
            const assetMove = (rendererRef as any)?._assetPointerMove;
            const assetUp = (rendererRef as any)?._assetPointerUp;
            if (assetDown) canvasEl?.removeEventListener('pointerdown', assetDown);
            if (assetMove) canvasEl?.removeEventListener('pointermove', assetMove);
            if (assetUp) {
              canvasEl?.removeEventListener('pointerup', assetUp);
              canvasEl?.removeEventListener('pointercancel', assetUp);
            }
            (rendererRef as any)._assetPointerDown = null;
            (rendererRef as any)._assetPointerMove = null;
            (rendererRef as any)._assetPointerUp = null;
            const assetWheel = (rendererRef as any)?._assetWheel;
            if (assetWheel) canvasEl?.removeEventListener('wheel', assetWheel);
            (rendererRef as any)._assetWheel = null;
            modelKeyAtPointerRef.current = () => null;
          } catch (e) {
            // ignore
          }
          lookControlsRef.current?.dispose();
          lookControlsRef.current = null;
          if (rendererRef.current) {
            rendererRef.current.setAnimationLoop(null);
            rendererRef.current.dispose();
          }
          
          if (vrButtonRef.current && containerRef.current) {
            try {
              containerRef.current.removeChild(vrButtonRef.current);
            } catch (e) {
              // Button may already be removed
            }
          }
          if (containerRef.current && rendererRef.current?.domElement) {
            try {
              containerRef.current.removeChild(rendererRef.current.domElement);
            } catch (e) {
              // Element may already be removed
            }
          }
        } catch (cleanupErr: any) {
          console.error('[XRLessonPlayerV3] Cleanup error:', cleanupErr);
        }
      };
    } catch (initErr: any) {
      console.error('[XRLessonPlayerV3] Scene initialization error:', initErr);
      addDebug(`Scene init error: ${initErr?.message || initErr}`);
      setErrorMessage(`Failed to initialize scene: ${initErr?.message || 'Unknown error'}`);
      setLoadingState('error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Content is loaded by its own effects, so nothing here depends on which
  // lesson is running: the renderer, camera, lights, controllers and hands are
  // built once and live for as long as the player is mounted.
  }, [isVRSupported, addDebug]);

  // ============================================================================
  // Skybox
  //
  // Deliberately NOT part of scene setup.
  //
  // The scene effect creates the WebGLRenderer, and it used to carry `skyboxUrl`
  // in its dependency array — so changing the environment disposed the renderer
  // and rebuilt it. That ends any running WebXR session, which means the one
  // thing that should be able to change mid-lesson was also the thing that threw
  // every student out of their headset. Separated so the environment can be
  // swapped while the session, the camera rig and the hands all keep running.
  // ============================================================================

  useEffect(() => {
    if (!isSceneReady || !sceneRef.current || !skyboxUrl) return;

    // Out with the old, explicitly. A skybox is a 500-unit sphere carrying a
    // full-resolution equirectangular texture; leaving the previous one to the
    // garbage collector is how a headset runs out of memory partway through a
    // chapter.
    const previous = sceneRef.current.getObjectByName('skybox');
    if (previous) {
      sceneRef.current.remove(previous);
      previous.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (!mesh.isMesh) return;
        mesh.geometry?.dispose();
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const material of materials) {
          if (!material) continue;
          (material as any).map?.dispose?.();
          material.dispose();
        }
      });
    }

    // Load skybox inline (to avoid useCallback dependency issues)
    const imageFallback = fallbackImageUrl || lessonData?.topic?.skybox_url || null;
    
    (async () => {
      try {
        const urlStr = String(skyboxUrl || '');
        const fallbackStr = imageFallback ? String(imageFallback) : null;
        
        console.log('[XRLessonPlayerV3] Loading skybox:', urlStr.substring(0, 60));
        setLoadingMessage('Loading 360° environment...');
        
        // Setup loaders
        const gltfLoader = new GLTFLoader();
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
        gltfLoader.setDRACOLoader(dracoLoader);
        
        // Helper to load as equirectangular image
        const loadAsImage = async (imageUrl: string): Promise<void> => {
          console.log('[XRLessonPlayerV3] Loading as image:', imageUrl.substring(0, 60));
          const textureLoader = new THREE.TextureLoader();
          
          // Add crossOrigin for external images
          textureLoader.crossOrigin = 'anonymous';
          
          const texture = await new Promise<THREE.Texture>((resolve, reject) => {
            textureLoader.load(
              imageUrl, 
              (tex) => {
                console.log('[XRLessonPlayerV3] Texture loaded:', tex.image?.width, 'x', tex.image?.height);
                resolve(tex);
              }, 
              undefined, 
              (err) => {
                console.error('[XRLessonPlayerV3] Texture load error:', err);
                reject(err);
              }
            );
          });
          
          // Configure texture for equirectangular mapping
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          
          // Create a large sphere (500 units radius) - camera is at center
          // Use FrontSide since we're INSIDE the sphere looking OUT
          // Flip UV coordinates by scaling geometry negatively on X
          const geometry = new THREE.SphereGeometry(500, 64, 32);
          geometry.scale(-1, 1, 1); // Flip to see texture from inside
          
          const material = new THREE.MeshBasicMaterial({
            map: texture,
            side: THREE.FrontSide, // We flipped geometry, so use FrontSide
          });
          
          const skyboxMesh = new THREE.Mesh(geometry, material);
          skyboxMesh.name = 'skybox';
          skyboxMesh.position.set(0, 0, 0); // Center at origin
          if (sceneRef.current) {
            sceneRef.current.add(skyboxMesh);
            console.log('[XRLessonPlayerV3] ✅ Image skybox added, children:', sceneRef.current.children.length);
            setLoadingState('ready');
          }
        };
        
        // Check if URL looks like GLB
        const urlLower = urlStr.toLowerCase();
        const looksLikeGLB = urlLower.includes('.glb') || urlLower.includes('.gltf');
        
        if (looksLikeGLB) {
          try {
            console.log('[XRLessonPlayerV3] Attempting GLB load...');
            const gltf: any = await new Promise<any>((resolve, reject) => {
              gltfLoader.load(urlStr, resolve, (p) => {
                const pct = p.total > 0 ? Math.round((p.loaded / p.total) * 100) : 0;
                setLoadingMessage(`Loading skybox: ${pct}%`);
              }, reject);
            });
            
            console.log('[XRLessonPlayerV3] GLB loaded, processing...');
            
            const box = new THREE.Box3().setFromObject(gltf.scene);
            const size = box.getSize(new THREE.Vector3());
            const center = box.getCenter(new THREE.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z);
            const scale = maxDim > 0 ? 200 / maxDim : 1;
            
            gltf.scene.scale.setScalar(scale);
            gltf.scene.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
            
            gltf.scene.traverse((child: any) => {
              if (child instanceof THREE.Mesh && child.material) {
                const mats = Array.isArray(child.material) ? child.material : [child.material];
                mats.forEach((mat: any) => {
                  const tex = mat.map;
                  if (tex) {
                    child.material = new THREE.MeshBasicMaterial({ map: tex, side: THREE.BackSide, depthWrite: false });
                    tex.colorSpace = THREE.SRGBColorSpace;
                  } else {
                    mat.side = THREE.BackSide;
                    mat.depthWrite = false;
                  }
                });
              }
            });
            
            gltf.scene.name = 'skybox';
            gltf.scene.renderOrder = -1000;
            if (sceneRef.current) {
              sceneRef.current.add(gltf.scene);
              console.log('[XRLessonPlayerV3] ✅ GLB skybox added');
              setLoadingState('ready');
            }
            
          } catch (glbErr: any) {
            console.warn('[XRLessonPlayerV3] GLB failed:', glbErr?.message);
            const imageToLoad = fallbackStr || urlStr;
            await loadAsImage(imageToLoad);
          }
        } else {
          await loadAsImage(urlStr);
        }
        
      } catch (err: any) {
        console.error('[XRLessonPlayerV3] Skybox load error:', err);
        setErrorMessage(`Failed to load skybox: ${err?.message || 'Unknown error'}`);
        setLoadingState('error');
      }
    })();
  }, [skyboxUrl, fallbackImageUrl, isSceneReady, lessonData?.topic?.skybox_url, addDebug]);

  
  // ============================================================================
  // Load 3D Assets into Scene
  // ============================================================================
  
  /*
    Which asset load is the current one.

    Bumped whenever the effect re-runs, and checked by the running load at every
    point it could have been superseded. Without it a load that started before a
    re-run still added its models to the scene when it finally finished, on top
    of the ones that replaced them — the same asset visibly in the room twice,
    with only one of the two copies answering the controls.

    The same generation-token shape as lib/lesson/narration.ts, which solved the
    identical problem for audio.
  */
  const assetLoadGenerationRef = useRef(0);
  // A load skipped because the scene was not ready yet; retried once it is. See assetLoadGate.
  const deferredAssetLoadRef = useRef(false);
  const [assetLoadNonce, setAssetLoadNonce] = useState(0);

  useEffect(() => {
    const generation = assetLoadGenerationRef.current + 1;
    assetLoadGenerationRef.current = generation;
    /** False once a newer load has started; nothing stale may touch the scene. */
    const isCurrent = () => assetLoadGenerationRef.current === generation;
    
    // Log whenever this effect runs
    console.log('[XRLessonPlayerV3] ========== ASSET LOADING EFFECT TRIGGERED ==========');
    console.log('[XRLessonPlayerV3] Effect conditions:', {
      meshyAssetsLength: meshyAssets.length,
      loadingState,
      hasScene: !!sceneRef.current,
      generation,
      meshyAssets: meshyAssets.map(a => ({ 
        id: a.id, 
        name: a.name, 
        url: a.glbUrl?.substring(0, 50),
        fileSize: (a as any).fileSize ? `${((a as any).fileSize / (1024 * 1024)).toFixed(2)}MB` : 'unknown'
      }))
    });
    
    // Always log to debug panel
    addDebug(`========== ASSET LOADING EFFECT ==========`);
    addDebug(`Assets: ${meshyAssets.length} | State: ${loadingState} | Scene: ${!!sceneRef.current}`);
    addDebug(`Load generation: ${generation}`);
    
    try {
      // Allow asset loading when scene is ready OR when in VR
      if (meshyAssets.length === 0) {
        deferredAssetLoadRef.current = false;
        console.warn('[XRLessonPlayerV3] Asset loading skipped: no assets');
        addDebug(`❌ Asset loading skipped: no assets (meshyAssets.length=0)`);
        return;
      }

      if (!sceneRef.current || !shouldLoadAssets(loadingState, isSceneReady)) {
        // Retried by the effect below once the scene is ready — otherwise a model
        // list that arrives mid-swap (state 'loading') is never loaded at all.
        deferredAssetLoadRef.current = true;
        console.warn('[XRLessonPlayerV3] Asset loading deferred: scene not ready', loadingState);
        addDebug(`⏳ Asset loading deferred: state=${loadingState}, sceneReady=${isSceneReady}`);
        return;
      }
      deferredAssetLoadRef.current = false;

      
      console.log('[XRLessonPlayerV3] ✅ Asset loading conditions met!');
      addDebug(`✅ Asset loading conditions met: scene ready, ${meshyAssets.length} asset(s), state=${loadingState}`);
      
      const scene = sceneRef.current;
      if (!scene) {
        console.error('[XRLessonPlayerV3] Scene ref exists but scene is null');
        addDebug(`❌ Scene ref exists but scene is null`);
        return;
      }
      
      // Check if assets are already loaded - but allow reload if count doesn't match
      const existingGroup = scene.getObjectByName('assetsGroup');
      if (existingGroup && existingGroup.children.length === meshyAssets.length) {
        console.log('[XRLessonPlayerV3] Assets group already exists with correct count, checking visibility...');
        addDebug(`⚠️ Assets group exists (${existingGroup.children.length} children), checking...`);
        
        // Check if assets are actually visible
        let hasVisibleAssets = false;
        existingGroup.traverse((obj) => {
          if (obj instanceof THREE.Mesh || (obj instanceof THREE.Group && obj.children.length > 0)) {
            if (obj.visible) hasVisibleAssets = true;
          }
        });
        
        if (hasVisibleAssets) {
          console.log('[XRLessonPlayerV3] Assets are visible, skipping reload');
          addDebug(`✅ Assets are visible, skipping reload`);
          return;
        } else {
          console.log('[XRLessonPlayerV3] Assets exist but not visible, reloading...');
          addDebug(`⚠️ Assets exist but not visible, removing and reloading...`);
          scene.remove(existingGroup);
          assetsGroupRef.current = null;
          
          // Unlock StableLayoutSystem and reset interaction state
          if (stableLayoutRef.current) {
            stableLayoutRef.current.unlockLayout();
            stableLayoutRef.current.releaseGrab(); // Release any grabbed models
          }
          // Reset interaction guard
          interactionGuardRef.current = { lastInteractionTime: 0, interactionCount: 0, isProcessing: false };
          lastGrabTimeRef.current.clear();
          hoveredObjectRef.current = null;
        }
      }
      
      // Remove existing group if it exists but is incomplete
      if (existingGroup && existingGroup.children.length !== meshyAssets.length) {
        console.log('[XRLessonPlayerV3] Removing incomplete assets group');
        scene.remove(existingGroup);
        assetsGroupRef.current = null;
        
        // Unlock StableLayoutSystem and reset interaction state
        if (stableLayoutRef.current) {
          stableLayoutRef.current.unlockLayout();
          stableLayoutRef.current.releaseGrab(); // Release any grabbed models
        }
        // Reset interaction guard
        interactionGuardRef.current = { lastInteractionTime: 0, interactionCount: 0, isProcessing: false };
        lastGrabTimeRef.current.clear();
        hoveredObjectRef.current = null;
      }
      
      addDebug('[XRLessonPlayerV3] Loading 3D assets into scene...');
      console.log('[XRLessonPlayerV3] Loading 3D assets into scene...');
    
    // Create a group to hold all assets
    const assetsGroup = new THREE.Group();
    assetsGroup.name = 'assetsGroup';
    assetsGroupRef.current = assetsGroup;
    
    // GLTFLoader supports both .gltf and .glb files natively
    // GLB is just the binary version of GLTF (single file vs JSON + bin)
    const gltfLoader = new GLTFLoader();
    
    // DRACO loader for compressed geometry (optional, but improves performance)
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
    dracoLoader.preload(); // Preload decoder for faster loading
    gltfLoader.setDRACOLoader(dracoLoader);
    
    // Verify GLB support
    console.log('[XRLessonPlayerV3] GLTFLoader initialized - supports .gltf and .glb files');
    addDebug('GLTFLoader ready (supports .gltf and .glb)');
    
    // Position assets directly in front of user (centered)
    animationMixersRef.current = []; // Clear mixers when reloading assets
    const loadAssets = async () => {
      // CRITICAL: Always log to both console and debug panel
      console.log(`[XRLessonPlayerV3] ========== STARTING ASSET LOADING ==========`);
      console.log(`[XRLessonPlayerV3] Total assets to load: ${meshyAssets.length}`);
      console.log(`[XRLessonPlayerV3] Assets:`, meshyAssets.map(a => ({ 
        id: a.id, 
        name: a.name, 
        url: a.glbUrl?.substring(0, 60),
        fileSize: (a as any).fileSize ? `${((a as any).fileSize / (1024 * 1024)).toFixed(2)}MB` : 'unknown'
      })));
      
      // Force debug panel update
      addDebug(`========== STARTING ASSET LOADING ==========`);
      addDebug(`Total assets: ${meshyAssets.length}`);
      meshyAssets.forEach((asset, idx) => {
        const sizeMB = (asset as any).fileSize ? ((asset as any).fileSize / (1024 * 1024)).toFixed(2) : 'unknown';
        addDebug(`Asset ${idx + 1}: ${asset.name || asset.id} (${sizeMB}MB)`);
      });
      
      // Reset loaded count
      setAssetsLoaded(0);
      // Fresh groups are about to be built, so the id -> group map starts empty.
      // Stated here rather than relying on the effect cleanup, because the
      // duplicate guard below is only meaningful if this holds.
      assetRefs.current.clear();
      
      for (let i = 0; i < meshyAssets.length; i++) {
        // A newer load has taken over. Stop here rather than spend the network
        // on models that will be thrown away.
        if (!isCurrent()) {
          addDebug(`Load ${generation} superseded; abandoning the rest`);
          discardAssetsGroup(assetsGroup);
          return;
        }
        const asset = meshyAssets[i];

        /*
          One group per asset id, always.

          assetRefs is keyed by id, so a second group for the same id overwrites
          the first here and becomes invisible to the model tools — on screen,
          but deaf to Explode, Isolate and Section. mergeLessonAssets should
          never hand us a duplicate; this makes sure that a bug upstream costs
          us a missing copy rather than an uncontrollable one.
        */
        if (assetRefs.current.has(asset.id)) {
          console.warn(`[XRLessonPlayerV3] Skipping duplicate asset id: ${asset.id}`);
          addDebug(`⚠️ Skipped duplicate asset id: ${asset.id}`);
          continue;
        }
        
        try {
          console.log(`[XRLessonPlayerV3] Loading asset ${i + 1}/${meshyAssets.length}:`, {
            name: asset.name || asset.id,
            url: asset.glbUrl?.substring(0, 80)
          });
          // Check file type from URL
          const assetUrl = asset.glbUrl || '';
          const isGLB = assetUrl.toLowerCase().includes('.glb');
          const isGLTF = assetUrl.toLowerCase().includes('.gltf');
          
          console.log(`[XRLessonPlayerV3] Loading asset ${i + 1}/${meshyAssets.length}:`, {
            name: asset.name || asset.id,
            type: isGLB ? 'GLB (binary)' : isGLTF ? 'GLTF (text)' : 'unknown',
            url: assetUrl.substring(0, 80)
          });
          addDebug(`========== LOADING ASSET ${i + 1}/${meshyAssets.length} ==========`);
          addDebug(`Name: ${asset.name || asset.id}`);
          addDebug(`Type: ${isGLB ? 'GLB (binary)' : isGLTF ? 'GLTF (text)' : 'unknown'}`);
          addDebug(`URL: ${assetUrl.substring(0, 60)}...`);
          if ((asset as any).fileSize) {
            addDebug(`Size: ${((asset as any).fileSize / (1024 * 1024)).toFixed(2)}MB`);
          }
          
          if (!assetUrl) {
            throw new Error('No asset URL provided');
          }
          
          if (!isGLB && !isGLTF) {
            console.warn(`[XRLessonPlayerV3] Asset ${i + 1} may not be GLTF/GLB format: ${assetUrl}`);
            addDebug(`⚠️ Warning: Asset may not be GLTF/GLB format`);
          }
          
          // Check file size and adjust timeout accordingly
          const fileSizeMB = (asset as any).fileSize ? (asset as any).fileSize / (1024 * 1024) : 0;
          const timeoutMs = fileSizeMB > 5 ? 120000 : fileSizeMB > 3 ? 60000 : 30000; // 2min for >5MB, 1min for >3MB, 30s for smaller
          
          if (fileSizeMB > 5) {
            console.warn(`[XRLessonPlayerV3] Large asset detected: ${fileSizeMB.toFixed(2)}MB, using extended timeout`);
            addDebug(`⚠️ Large asset: ${fileSizeMB.toFixed(2)}MB, this may take a while...`);
          }
          
          const gltf = await new Promise<any>((resolve, reject) => {
            let lastProgress = 0;
            const startTime = Date.now();
            
            const timeout = setTimeout(() => {
              reject(new Error(`GLB loading timeout after ${timeoutMs / 1000} seconds (file size: ${fileSizeMB.toFixed(2)}MB)`));
            }, timeoutMs);
            
            gltfLoader.load(
              assetUrl,
              (gltf) => {
                clearTimeout(timeout);
                const loadTime = ((Date.now() - startTime) / 1000).toFixed(1);
                console.log(`[XRLessonPlayerV3] ✅ GLTF loaded for asset ${i + 1} in ${loadTime}s:`, {
                  scene: gltf.scene,
                  animations: gltf.animations?.length || 0,
                  cameras: gltf.cameras?.length || 0,
                  fileSizeMB: fileSizeMB.toFixed(2)
                });
                addDebug(`✅ GLTF loaded in ${loadTime}s (${fileSizeMB.toFixed(2)}MB)`);
                resolve(gltf);
              },
              (progress) => {
                if (progress.total > 0) {
                  const pct = Math.round((progress.loaded / progress.total) * 100);
                  const loadedMB = (progress.loaded / (1024 * 1024)).toFixed(2);
                  const totalMB = (progress.total / (1024 * 1024)).toFixed(2);
                  
                  // Only log every 10% or if it's a large file
                  if (pct - lastProgress >= 10 || fileSizeMB > 3) {
                    console.log(`[XRLessonPlayerV3] Asset ${i + 1} loading: ${pct}% (${loadedMB}MB / ${totalMB}MB)`);
                    addDebug(`Asset ${i + 1} loading: ${pct}% (${loadedMB}MB / ${totalMB}MB)`);
                    lastProgress = pct;
                  }
                } else if (progress.loaded > 0) {
                  // Show progress even if total is unknown
                  const loadedMB = (progress.loaded / (1024 * 1024)).toFixed(2);
                  console.log(`[XRLessonPlayerV3] Asset ${i + 1} loading: ${loadedMB}MB loaded...`);
                  addDebug(`Asset ${i + 1} loading: ${loadedMB}MB loaded...`);
                }
              },
              (error) => {
                clearTimeout(timeout);
                console.error(`[XRLessonPlayerV3] GLB load error for asset ${i + 1}:`, error);
                addDebug(`❌ GLB load error: ${error?.message || error}`);
                reject(error);
              }
            );
          });
          
          console.log(`[XRLessonPlayerV3] ✅ GLTF loaded for asset ${i + 1}, processing...`);
          addDebug(`✅ GLTF loaded successfully`);
          if (!isCurrent()) {
            // Downloaded into a load that no longer counts. The GLB is real and
            // holds GPU memory, so release it rather than letting it fall out of
            // scope unreferenced.
            disposeObject3D(gltf.scene);
            discardAssetsGroup(assetsGroup);
            return;
          }

          addDebug(`Processing geometry and materials...`);
          
          // ═══════════════════════════════════════════════════════════════════════
          // MODEL ANALYSIS SYSTEM - Analyze model geometry for smart positioning
          // ═══════════════════════════════════════════════════════════════════════
          const box = new THREE.Box3().setFromObject(gltf.scene);
          const size = box.getSize(new THREE.Vector3());
          const center = box.getCenter(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          
          // Calculate model's bottom position (min Y of bounding box)
          const modelBottom = box.min.y;
          const modelTop = box.max.y;
          const modelHeight = modelTop - modelBottom;
          
          // Analyze model type based on center offset from bottom
          // If center.y is close to bottom, it's a "bottom-origin" model
          // If center.y is in middle, it's a "center-origin" model
          const centerOffset = center.y - modelBottom;
          const centerRatio = modelHeight > 0 ? centerOffset / modelHeight : 0.5;
          const modelType = centerRatio < 0.3 ? 'BOTTOM_ORIGIN' : 
                           centerRatio > 0.7 ? 'TOP_ORIGIN' : 'CENTER_ORIGIN';
          
          addDebug(`═══ MODEL ANALYSIS (Asset ${i + 1}) ═══`);
          addDebug(`Original size: ${size.x.toFixed(2)} x ${size.y.toFixed(2)} x ${size.z.toFixed(2)}`);
          addDebug(`Model bottom Y: ${modelBottom.toFixed(2)}m`);
          addDebug(`Model center Y: ${center.y.toFixed(2)}m`);
          addDebug(`Model top Y: ${modelTop.toFixed(2)}m`);
          addDebug(`Model height: ${modelHeight.toFixed(2)}m`);
          addDebug(`Center ratio: ${(centerRatio * 100).toFixed(0)}% from bottom`);
          addDebug(`Model type: ${modelType}`);
          
          console.log(`🔍 [MODEL ANALYSIS] Asset ${i + 1}:`, {
            size: `${size.x.toFixed(2)} x ${size.y.toFixed(2)} x ${size.z.toFixed(2)}`,
            modelBottom: modelBottom.toFixed(2),
            modelCenter: center.y.toFixed(2),
            modelTop: modelTop.toFixed(2),
            modelHeight: modelHeight.toFixed(2),
            centerRatio: centerRatio.toFixed(2),
            modelType
          });
          
          // ═══════════════════════════════════════════════════════════════════════
          // NORMALIZED BOUNDING BOX SCALING
          // All assets are scaled to the SAME normalized size for visual consistency
          // This ensures a tiny model and a huge model both appear at ~1.2m
          // Uses the global NORMALIZED_SIZE constant defined at component level
          // ═══════════════════════════════════════════════════════════════════════
          let scale = maxDim > 0 ? NORMALIZED_SIZE / maxDim : 1;
          
          // Ensure minimum scale for very small models (prevent invisible assets)
          const minScale = 0.01;  // Allow very small scaling for huge models
          const maxScale = 100.0; // Allow large scaling for tiny models
          scale = Math.max(minScale, Math.min(maxScale, scale));
          
          console.log(`📏 [NORMALIZED SCALING] Asset ${i + 1}:`, {
            originalMaxDim: `${maxDim.toFixed(2)}m`,
            normalizedSize: `${NORMALIZED_SIZE}m`,
            scaleFactor: scale.toFixed(4),
            fileSizeMB: fileSizeMB.toFixed(2)
          });
          
          addDebug(`═══ NORMALIZED SCALING ═══`);
          addDebug(`Original max dim: ${maxDim.toFixed(2)}m`);
          addDebug(`Normalized to: ${NORMALIZED_SIZE}m`);
          addDebug(`Scale factor: ${scale.toFixed(4)}`);
          
          // ═══════════════════════════════════════════════════════════════════════
          // SMART CENTERING - Position model so its BOTTOM sits at Y=0 initially
          // Instead of centering at origin, we place the bottom at Y=0
          // This makes subsequent positioning predictable
          // ═══════════════════════════════════════════════════════════════════════
          
          // Center horizontally (X, Z) but place BOTTOM at Y=0. See
          // groundedOffset for why the offset has to be scaled — getting this
          // wrong is what left uploaded models floating above the dock.
          gltf.scene.position.copy(groundedOffset(box, scale));
          gltf.scene.scale.setScalar(scale);
          gltf.scene.name = `asset_${asset.id}`;
          
          // Calculate scaled height (needed for positioning)
          const scaledHeight = modelHeight * scale;
          
          addDebug(`Scaled height: ${scaledHeight.toFixed(2)}m`);
          console.log(`🔍 [MODEL ANALYSIS] Scaled height: ${scaledHeight.toFixed(2)}m, bottom now at local Y=0`);
          
          // Ensure the scene is visible and not culled
          gltf.scene.visible = true;
          gltf.scene.frustumCulled = false; // Disable frustum culling for large/complex models
          
          // ═══════════════════════════════════════════════════════════════════════
          // ASSET POSITION - PLACEHOLDER SYSTEM
          // Assets will be positioned using placeholder system after all are loaded
          // For now, set temporary position (will be updated by placeholder system)
          // ═══════════════════════════════════════════════════════════════════════
          console.log(`[ASSET_PLACEHOLDER] Asset ${i + 1}/${meshyAssets.length} loaded, will be positioned by placeholder system`);
          addDebug(`Asset ${i + 1} loaded (placeholder positioning pending)`);
          
          // STATE-OF-ART RENDERING: Enhance materials and lighting
          gltf.scene.traverse((child: any) => {
            if (child instanceof THREE.Mesh) {
              // Enable high-quality shadows
              child.castShadow = true;
              child.receiveShadow = true;
              child.userData.isInteractable = true;
              child.userData.originalMaterial = child.material; // Store original
              child.userData.originalScale = new THREE.Vector3().copy(child.scale);
              
              if (child.material) {
                const mats = Array.isArray(child.material) ? child.material : [child.material];
                mats.forEach((mat: any) => {
                  // Ensure material is visible and properly configured
                  mat.visible = true;
                  mat.transparent = false; // Ensure not transparent unless explicitly set
                  if (mat.opacity !== undefined) {
                    mat.opacity = 1.0; // Ensure fully opaque
                  }
                  
                  // High-quality texture settings
                  if (mat.map) {
                    mat.map.colorSpace = THREE.SRGBColorSpace;
                    mat.map.generateMipmaps = true;
                    mat.map.minFilter = THREE.LinearMipmapLinearFilter;
                    mat.map.magFilter = THREE.LinearFilter;
                    mat.map.anisotropy = rendererRef.current?.capabilities.getMaxAnisotropy() || 1;
                  }
                  
                  // Normal maps for better detail
                  if (mat.normalMap) {
                    mat.normalMap.colorSpace = THREE.LinearSRGBColorSpace;
                  }
                  
                  // Environment maps for reflections
                  if (mat.envMap) {
                    mat.envMapIntensity = 1.0;
                  }
                  
                  // Material quality settings
                  if (!mat.colorSpace) {
                    mat.colorSpace = THREE.SRGBColorSpace;
                  }
                  
                  // Ensure proper roughness and metalness for PBR
                  if (mat.roughness !== undefined) {
                    mat.roughness = Math.max(0, Math.min(1, mat.roughness));
                  }
                  if (mat.metalness !== undefined) {
                    mat.metalness = Math.max(0, Math.min(1, mat.metalness));
                  }
                  
                  // Mark as updated
                  mat.needsUpdate = true;
                });
              }
            }
          });

          // Play GLB animations (e.g. Meshy rigged+animated assets)
          if (gltf.animations && gltf.animations.length > 0) {
            const mixer = new THREE.AnimationMixer(gltf.scene);
            gltf.animations.forEach((clip: THREE.AnimationClip) => mixer.clipAction(clip).play());
            animationMixersRef.current.push(mixer);
            addDebug(`Animation: ${gltf.animations.length} clip(s) playing`);
          }

          // Wrap in a group for interaction
          const assetGroup = new THREE.Group();
          assetGroup.name = `assetGroup_${asset.id}`;
          assetGroup.add(gltf.scene);
          
          // CRITICAL: Do NOT set temporary position - assets will be placed ONLY on dock
          // Position will be set by SceneLayoutSystem.placeAssetOnDock()
          // Start at origin (0,0,0) - will be moved to dock position by layout system
          assetGroup.position.set(0, 0, 0);
          
          // Mark as asset layer for raycast filtering
          assetGroup.userData.layer = 'asset';
          assetGroup.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.userData.layer = 'asset';
            }
          });
          assetGroup.userData.isInteractable = true;
          assetGroup.userData.originalPosition = new THREE.Vector3().copy(assetGroup.position);
          assetGroup.userData.originalRotation = new THREE.Euler().copy(assetGroup.rotation);
          assetGroup.userData.originalScale = new THREE.Vector3().setScalar(1.0);
          assetGroup.userData.assetIndex = i; // Store index for placeholder assignment
          
          // Make sure the group and all children are visible
          assetGroup.visible = true;
          let meshCount = 0;
          assetGroup.traverse((obj) => {
            obj.visible = true;
            if (obj instanceof THREE.Mesh) {
              meshCount++;
              // Ensure material is visible
              if (obj.material) {
                const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
                mats.forEach((mat: any) => {
                  if (mat) {
                    mat.visible = true;
                    mat.transparent = false;
                    mat.opacity = 1.0;
                  }
                });
              }
            }
          });
          
          // Calculate final bounding box after scaling and positioning
          const finalBox = new THREE.Box3().setFromObject(assetGroup);
          const finalSize = finalBox.getSize(new THREE.Vector3());
          const finalCenter = finalBox.getCenter(new THREE.Vector3());
          const worldPos = new THREE.Vector3();
          assetGroup.getWorldPosition(worldPos);
          
          console.log(`[XRLessonPlayerV3] Asset group created:`, {
            name: assetGroup.name,
            position: assetGroup.position,
            worldPosition: worldPos,
            visible: assetGroup.visible,
            meshCount,
            children: assetGroup.children.length,
            finalSize: finalSize,
            finalCenter: finalCenter,
            scale: scale.toFixed(3)
          });
          
          addDebug(`Final asset size: ${finalSize.x.toFixed(2)} x ${finalSize.y.toFixed(2)} x ${finalSize.z.toFixed(2)}`);
          addDebug(`Final world position: (${worldPos.x.toFixed(2)}, ${worldPos.y.toFixed(2)}, ${worldPos.z.toFixed(2)})`);
          
          if (i === 0) {
            primaryAssetRef.current = assetGroup;
            console.log(`[XRLessonPlayerV3] ✅ Primary asset ref set: ${assetGroup.name}`);
            addDebug(`✅ Primary asset ref set: ${assetGroup.name}`);
          }
          
          assetsGroup.add(assetGroup);
          
          // Store asset reference for dock control
          assetRefs.current.set(asset.id, assetGroup);
          
          const newCount = i + 1;
          setAssetsLoaded(newCount);
          // The immersive budget stops redrawing the shadow map every frame, so a
          // newly placed asset needs one explicit refresh to cast at all.
          requestShadowRefresh(rendererRef.current);

          // Re-cache the model's parts. Done here rather than on a timer because
          // this is the moment the geometry is known to be in the scene.
          if (!modelToolsRef.current) modelToolsRef.current = createModelTools();
          const roots = Array.from(assetRefs.current.values());
          modelToolsRef.current.collect(roots);
          // collect() keeps highlights, including ones waiting for a model that
          // had not loaded yet; the tags have to catch up with the parts now here.
          syncPartLabels();
          // Tells the student-apply effect there are new models to catch up.
          setModelToolsVersion((version) => version + 1);
          // Label each asset with the name the lesson gave it. The scene-graph
          // key is `assetGroup_<id>`, which is meaningless in a picker a teacher
          // has to choose from mid-lesson.
          setModelAssets(
            modelToolsRef.current.list().map((entry) => ({
              ...entry,
              name:
                meshyAssets.find((candidate) => `assetGroup_${candidate.id}` === entry.key)?.name ||
                entry.name,
            }))
          );
          setModelSelectedAssetKey(modelToolsRef.current.selectedKey());
          setModelPartCount(modelToolsRef.current.partCount());

          // Keyed the same way addModelMark writes asset_id, so a published mark
          // finds its way back onto the model it was placed on.
          const markTargets = new Map<string, THREE.Object3D>();
          for (const root of roots) markTargets.set(String(root.name || root.uuid), root);
          inkLayerRef.current?.setMarkTargets(markTargets);
          
          console.log(`[XRLessonPlayerV3] ✅ Asset ${newCount}/${meshyAssets.length} added to assetsGroup:`, {
            name: asset.name || asset.id,
            position: assetGroup.position,
            visible: assetGroup.visible,
            meshCount,
            fileSizeMB: fileSizeMB.toFixed(2)
          });
          addDebug(`========== ASSET ${newCount} COMPLETE ==========`);
          addDebug(`✅ Added: ${asset.name || asset.id}`);
          addDebug(`Size: ${fileSizeMB.toFixed(2)}MB | Meshes: ${meshCount}`);
          addDebug(`Position: (${assetGroup.position.x.toFixed(2)}, ${assetGroup.position.y.toFixed(2)}, ${assetGroup.position.z.toFixed(2)})`);
          addDebug(`Visible: ${assetGroup.visible}`);
          
        } catch (err: any) {
          console.error(`[XRLessonPlayerV3] Failed to load asset ${asset.id}:`, err);
          addDebug(`❌ ERROR loading asset ${i + 1}: ${err?.message || err}`);
        }
      }
      
      // Add assets group to scene
      if (!isCurrent()) {
        // The whole point: a superseded load must never reach the scene. This is
        // where the second copy of every model used to arrive.
        addDebug(`Load ${generation} finished after being superseded; discarded`);
        discardAssetsGroup(assetsGroup);
        return;
      }
      if (sceneRef.current) {
        console.log(`[XRLessonPlayerV3] Adding assetsGroup to scene (${assetsGroup.children.length} children)`);
        const displaced = attachAssetsGroup(sceneRef.current, assetsGroup);
        if (displaced > 0) {
          // Belt and braces. If this ever fires, two loads reached the scene and
          // the generation check above did not catch it — worth knowing.
          console.warn(`[XRLessonPlayerV3] Displaced ${displaced} stale assets group(s)`);
          addDebug(`⚠️ Displaced ${displaced} stale assets group(s)`);
        }
        
        // Verify it's in the scene
        const foundGroup = sceneRef.current.getObjectByName('assetsGroup');
        if (foundGroup) {
          console.log('[XRLessonPlayerV3] ✅ Assets group added to scene:', {
            children: foundGroup.children.length,
            position: foundGroup.position,
            visible: foundGroup.visible
          });
          addDebug(`✅ Assets group added to scene (${foundGroup.children.length} assets)`);
          addDebug(`Position: (${foundGroup.position.x.toFixed(2)}, ${foundGroup.position.y.toFixed(2)}, ${foundGroup.position.z.toFixed(2)})`);
          addDebug(`Visible: ${foundGroup.visible}`);
          
          // Log all children with detailed info
          let meshTotal = 0;
          foundGroup.traverse((obj) => {
            if (obj instanceof THREE.Group || obj instanceof THREE.Mesh) {
              const worldPos = new THREE.Vector3();
              obj.getWorldPosition(worldPos);
              if (obj instanceof THREE.Mesh) meshTotal++;
              console.log('[XRLessonPlayerV3] Asset child:', {
                name: obj.name || obj.type,
                visible: obj.visible,
                worldPos: worldPos,
                isMesh: obj instanceof THREE.Mesh
              });
            }
          });
          addDebug(`Total meshes in scene: ${meshTotal}`);
          
          // Add a test cube to verify scene rendering works (only if no assets visible)
          if (meshTotal === 0) {
            const testGeometry = new THREE.BoxGeometry(0.2, 0.2, 0.2);
            const testMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
            const testCube = new THREE.Mesh(testGeometry, testMaterial);
            testCube.position.set(-1.0, 1.5, -1.7);
            testCube.name = 'testCube';
            sceneRef.current.add(testCube);
            addDebug(`🔴 Added RED test cube - no meshes found in assets!`);
          }
        } else {
          console.error('[XRLessonPlayerV3] ❌ Assets group not found after adding!');
          addDebug(`❌ ERROR: Assets group not found in scene after adding!`);
        }
        
        console.log('[XRLessonPlayerV3] ========== ASSET LOADING COMPLETE ==========');
        console.log('[XRLessonPlayerV3] Final scene info:', {
          totalChildren: sceneRef.current.children.length,
          assetChildren: foundGroup?.children.length || 0,
          assetsLoaded: assetsLoaded,
          meshyAssetsCount: meshyAssets.length
        });
        
        // Force debug panel update with all details
        addDebug(`========== ASSET LOADING COMPLETE ==========`);
        addDebug(`✅ Total scene children: ${sceneRef.current.children.length}`);
        addDebug(`✅ Asset groups in scene: ${foundGroup?.children.length || 0}`);
        addDebug(`✅ Assets loaded counter: ${assetsLoaded}/${meshyAssets.length}`);
        
        if (foundGroup && foundGroup.children.length > 0) {
          addDebug(`✅ SUCCESS: ${foundGroup.children.length} asset(s) visible in scene!`);
        } else {
          addDebug(`❌ WARNING: No assets found in scene after loading!`);
        }
        
        // Force a render to ensure visibility
        if (rendererRef.current && cameraRef.current) {
          rendererRef.current.render(sceneRef.current, cameraRef.current);
          console.log('[XRLessonPlayerV3] ✅ Forced render after adding assets');
          addDebug(`✅ Forced render complete`);
        }
        
        // ═══════════════════════════════════════════════════════════════════
        // SCENE LAYOUT SYSTEM - Production-grade, scalable asset placement
        // ═══════════════════════════════════════════════════════════════════
        if (cameraRef.current && foundGroup && foundGroup.children.length > 0) {
          const assetCount = foundGroup.children.length;
          console.log(`\n[SCENE_LAYOUT] ═══════════════════════════════════════`);
          console.log(`[SCENE_LAYOUT] Total assets to place: ${assetCount}`);
          console.log(`[SCENE_LAYOUT] Strategy: ${placementStrategy}`);
          
          // Initialize Scene Layout System
          if (!sceneLayoutRef.current) {
            sceneLayoutRef.current = new SceneLayoutSystem({
              assetDock: {
                distance: 0.7,      // Hands distance
                height: 0.9,       // Desk height
                width: 1.8,         // Wider for more assets
                depth: 0.8,
                maxAssetSize: 0.25, // 25cm max
              },
              introDock: {
                distance: 2.5,      // Further away from asset dock
                height: 1.2,        // Eye level
                width: 2.0,
                height_panel: 1.4,
                spacing: 1.5,       // Clear spacing between zones
              },
              ground: {
                size: 20,
                gridDivisions: 20,
                fadeAngle: 30,
              },
            }, placementStrategy);
            addDebug(`✅ Scene Layout System initialized (strategy: ${placementStrategy})`);
          } else {
            // Update strategy if changed
            sceneLayoutRef.current.setStrategy(placementStrategy);
          }
          
          // Ground plane belongs to the room and is built once here.
          if (sceneRef.current) {
            sceneLayoutRef.current.createGroundPlane(sceneRef.current, GROUND_LEVEL);
            addDebug(`✅ Ground plane created`);
          }

          /*
            Dock and model placement now come from the one workspace frame.

            What stood here computed its own anchor from the camera at this
            moment, while the lesson panel's layout engine computed a different
            one from a different moment — which is why the dock and the panel
            could face different ways and the room never looked arranged. It also
            carried a "nudge it back onto the dock" fallback that set
            `position.y = dockSurfaceY + height / 2`, the origin-is-the-centre
            assumption fixed everywhere else, which quietly undid the correct
            placement for any model authored off its own origin.
          */
          applyWorkspaceLayoutRef.current();

          console.log(`[SCENE_LAYOUT] ═══════════════════════════════════════`);
          
          // CRITICAL: Show VR button only after all calculations are complete
          if (vrButtonRef.current) {
            vrButtonRef.current.style.display = 'block';
            addDebug(`✅ VR button enabled - calculations complete`);
            console.log(`[SCENE_LAYOUT] VR button enabled - all assets placed`);
          }
          
          // Initialize Stable Layout System for interaction handling (not placement)
          if (!stableLayoutRef.current) {
            stableLayoutRef.current = new StableLayoutSystem({
              stageDistance: 2.5,
              stageWidth: 4.0,
              stageDepth: 2.5,
              horizontalOffset: 0.8,
              floorHeight: GROUND_LEVEL,
              modelSpacing: 0.5,
              normalizedSize: 0.8,
              environmentThreshold: 10.0,
            });
          }
          
          // Initialize layout with camera pose (for interaction only)
          if (!stableLayoutRef.current.isReady()) {
            stableLayoutRef.current.initialize(cameraRef.current, GROUND_LEVEL);
          } else if (loadingState === 'in-vr') {
            stableLayoutRef.current.recomputeAnchors(cameraRef.current, GROUND_LEVEL);
          }
          
          // CRITICAL: Stage assets with StableLayoutSystem for interaction
          // This registers them in the interactable cache so they can be grabbed
          if (stableLayoutRef.current.isReady()) {
            const existingStagedModels = stableLayoutRef.current.getStagedModels();
            if (existingStagedModels.size > 0) {
              stableLayoutRef.current.unlockLayout();
              addDebug('Unlocked layout for re-staging');
            }
            
            // Stage all models (for interaction cache, not positioning)
            const modelsToStage = foundGroup.children as THREE.Object3D[];
            const stagedModels = stableLayoutRef.current.stageModels(modelsToStage);
            
            // ═══════════════════════════════════════════════════════════════
            // CRITICAL VERIFICATION: All assets must be staged for interaction
            // ═══════════════════════════════════════════════════════════════
            console.log(`[INTERACTION] ═══════════════════════════════════════`);
            console.log(`[INTERACTION] STAGING VERIFICATION:`);
            console.log(`[INTERACTION]   Models to stage: ${modelsToStage.length}`);
            console.log(`[INTERACTION]   Models staged: ${stagedModels.length}`);
            console.log(`[INTERACTION]   Match: ${modelsToStage.length === stagedModels.length ? '✅ YES' : '❌ NO - BUG!'}`);
            
            if (modelsToStage.length !== stagedModels.length) {
              console.error(`[INTERACTION] ❌ CRITICAL BUG: ${modelsToStage.length - stagedModels.length} models NOT staged!`);
              addDebug(`❌ BUG: ${modelsToStage.length - stagedModels.length} models not staged!`);
            }
            
            // Verify interactables are available
            const interactables = stableLayoutRef.current.getInteractables();
            const allMeshes = stableLayoutRef.current.getAllInteractableMeshes();
            
            console.log(`[INTERACTION]   Interactable root models: ${interactables.length}`);
            console.log(`[INTERACTION]   Total interactable meshes: ${allMeshes.length}`);
            
            // Debug print all staged models
            stableLayoutRef.current.debugPrintStagedModels();
            
            addDebug(`✅ Assets staged: ${stagedModels.length} models`);
            addDebug(`Interactables: ${interactables.length} roots, ${allMeshes.length} meshes`);
            console.log(`[INTERACTION] ═══════════════════════════════════════`);
          }
          
          // Force render after placement
          if (rendererRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
          }
        }
      } else {
        console.error('[XRLessonPlayerV3] ❌ Scene ref is null!');
        addDebug(`❌ ERROR: Scene ref is null, cannot add assets!`);
      }
    };
    
    loadAssets().catch((err) => {
      console.error('[XRLessonPlayerV3] Fatal error in loadAssets:', err);
      addDebug(`❌ FATAL: Asset loading failed: ${err?.message || err}`);
    });
    
    return () => {
      try {
        // Release any grabbed models
        if (stableLayoutRef.current?.isGrabbing()) {
          stableLayoutRef.current.releaseGrab();
        }
        
        // Unlock layout
        if (stableLayoutRef.current) {
          stableLayoutRef.current.unlockLayout();
        }
        
        // Remove assets from scene
        if (assetsGroupRef.current && sceneRef.current) {
          sceneRef.current.remove(assetsGroupRef.current);
        }
        
        // Reset interaction state
        interactionGuardRef.current = { lastInteractionTime: 0, interactionCount: 0, isProcessing: false };
        lastGrabTimeRef.current.clear();
        hoveredObjectRef.current = null;
        assetRefs.current.clear();
        
        // Cleanup scene layout system
        if (sceneLayoutRef.current && sceneRef.current) {
          sceneLayoutRef.current.dispose(sceneRef.current);
        }
        sceneLayoutRef.current = null;
        assetPlacementsRef.current = [];
        
      } catch (cleanupErr: any) {
        console.error('[XRLessonPlayerV3] Asset cleanup error:', cleanupErr);
      }
    };
    } catch (assetErr: any) {
      console.error('[XRLessonPlayerV3] Asset loading error:', assetErr);
      addDebug(`❌ Asset loading error: ${assetErr?.message || assetErr}`);
    }
  // `loadingState` is deliberately absent. It changes to 'in-vr' when a session
  // starts, and having it here meant entering VR reloaded every model — the race
  // that put a second copy of each one in the scene.
  // `assetLoadNonce` only changes when a skipped load is being retried.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meshyAssets, addDebug, isSceneReady, assetLoadNonce]);

  // Retry a load that was skipped while the scene was not ready. Entering VR does
  // not trigger this (nothing was deferred), so it cannot reload the models.
  useEffect(() => {
    if (shouldRetryDeferredLoad(deferredAssetLoadRef.current, loadingState, isSceneReady)) {
      deferredAssetLoadRef.current = false;
      addDebug(`🔄 Scene ready (${loadingState}) - loading the deferred 3D assets`);
      setAssetLoadNonce((n) => n + 1);
    }
  }, [loadingState, isSceneReady, addDebug]);
  
  // ============================================================================
  // TTS Audio Controls with State Machine
  // ============================================================================
  
  // Get TTS for current phase
  const getTTSForPhase = useCallback((phase: LessonPhase): TTSData | null => {
    if (ttsData.length === 0) {
      console.log('[TTS MATCH] No TTS data available');
      return null;
    }
    
    // Map phase to expected section types
    let targetSections: string[] = ['full'];
    if (phase === 'intro') {
      targetSections = ['intro', 'introduction', 'avatar_intro'];
    } else if (phase === 'explanation') {
      targetSections = ['explanation', 'content', 'avatar_explanation', 'main'];
    } else if (phase === 'outro') {
      targetSections = ['outro', 'conclusion', 'avatar_outro', 'summary'];
    }
    
    console.log(`[TTS MATCH] Looking for phase=${phase}, targetSections=${targetSections.join(',')}`);
    console.log(`[TTS MATCH] Available TTS:`, ttsData.map(t => ({ id: t.id.substring(0, 40), section: t.section })));
    
    // Find matching TTS entry by section field
    const match = ttsData.find(tts => {
      const ttsSection = (tts.section || '').toLowerCase().trim();
      const isMatch = targetSections.some(target => ttsSection === target || ttsSection.includes(target));
      console.log(`[TTS MATCH] Checking ${tts.id.substring(0, 30)}... section="${ttsSection}" => ${isMatch ? 'MATCH' : 'no match'}`);
      return isMatch;
    });
    
    if (match) {
      console.log(`[TTS MATCH] ✅ Found match for ${phase}: ${match.id}`);
      addDebug(`Found TTS for ${phase}: ${match.section}`);
      return match;
    }
    
    // Fallback: Try to match by ID parsing
    const idMatch = ttsData.find(tts => {
      const idLower = (tts.id || '').toLowerCase();
      if (phase === 'intro' && (idLower.includes('_intro_') || idLower.endsWith('_intro'))) return true;
      if (phase === 'explanation' && (idLower.includes('_explanation_') || idLower.includes('_content_'))) return true;
      if (phase === 'outro' && (idLower.includes('_outro_') || idLower.includes('_conclusion_'))) return true;
      return false;
    });
    
    if (idMatch) {
      console.log(`[TTS MATCH] ✅ Found ID match for ${phase}: ${idMatch.id}`);
      addDebug(`Found TTS for ${phase} (by ID): ${idMatch.id.substring(0, 40)}`);
      return idMatch;
    }
    
    console.log(`[TTS MATCH] ⚠️ No match for ${phase}, using fallback (first TTS)`);
    addDebug(`⚠️ No TTS match for ${phase}, using fallback`);
    return ttsData[0] || null;
  }, [ttsData, addDebug]);
  
  /**
   * The ONE place a lesson phase moves forward.
   *
   * This used to be duplicated in three branches of playTTSForPhase (no audio,
   * ended, error), each with its own setTimeout, so a missing or failed clip
   * could queue several advances and skip a phase.
   */
  const advancePhase = useCallback((from: LessonPhase) => {
    setLessonPhase((current) => {
      // Only advance if we are still on the phase this call was made for. A late
      // callback from a superseded clip cannot drag the class forwards.
      if (current !== from) return current;
      if (from === 'intro') return 'explanation';
      if (from === 'explanation') return 'outro';
      if (from === 'outro') return mcqDataRef.current.length > 0 ? 'quiz' : 'completed';
      return current;
    });
  }, []);

  const playTTSForPhase = useCallback((phase: LessonPhase) => {
    const narration = narrationRef.current;
    const tts = getTTSForPhase(phase);

    if (!narration || !tts?.audioUrl) {
      addDebug(`No TTS audio for phase: ${phase}`);
      setTtsState('idle');
      // Give the panel a beat to show the phase before moving on.
      const timer = setTimeout(() => advancePhase(phase), 1000);
      phaseAdvanceTimerRef.current = timer;
      return;
    }

    debugTTS(`Playing TTS for ${phase}: ${(tts as any).script_type || tts.section}`);
    narration.play(tts.audioUrl, {
      onEnded: () => advancePhase(phase),
      onError: (reason) => {
        addDebug(`Audio error for ${phase}: ${reason}`);
        phaseAdvanceTimerRef.current = setTimeout(() => advancePhase(phase), 1000);
      },
    });
  }, [getTTSForPhase, addDebug, debugTTS, advancePhase]);

  const toggleAudio = useCallback(() => {
    if (classroomRef.current.blockStudentPhaseControl('Play/Pause')) return;
    const narration = narrationRef.current;
    if (!narration || !narration.getUrl()) {
      playTTSForPhase(lessonPhase);
      return;
    }
    if (narration.getState() === 'playing') narration.pause();
    else if (!narration.resume()) playTTSForPhase(lessonPhase);
  }, [lessonPhase, playTTSForPhase]);
  
  const stopAudio = useCallback(() => {
    if (phaseAdvanceTimerRef.current) {
      clearTimeout(phaseAdvanceTimerRef.current);
      phaseAdvanceTimerRef.current = null;
    }
    narrationRef.current?.stop();
    setIsAudioPlaying(false);
    setTtsState('idle');
  }, []);
  
  const skipNext = useCallback(() => {
    if (classroomRef.current.blockStudentPhaseControl('Skip')) return;
    stopAudio();
    if (lessonPhase === 'intro') {
      setLessonPhase('explanation');
    } else if (lessonPhase === 'explanation') {
      setLessonPhase('outro');
    } else if (lessonPhase === 'outro') {
      if (mcqData.length > 0) {
        setLessonPhase('quiz');
      } else {
        setLessonPhase('completed');
      }
    }
  }, [lessonPhase, mcqData.length, stopAudio]);
  
  
  // Skip directly to Quiz - for users who want to skip all TTS phases
  const skipToQuiz = useCallback(() => {
    if (classroomRef.current.blockStudentPhaseControl('Skip to quiz')) return;
    stopAudio();
    if (mcqData.length > 0) {
      setLessonPhase('quiz');
      debugQuiz('⏭️ Skipped to Quiz');
    } else {
      setLessonPhase('completed');
      debugQuiz('⏭️ No quiz available - completing lesson');
    }
  }, [mcqData.length, stopAudio, addDebug]);

  // ============================================================================
  // Live class
  //
  // All of the teacher-control behaviour lives in useClassroomSession, shared
  // with the krpano player. This player supplies two things it cannot know:
  // its phase vocabulary, and how to read and write its own camera.
  // ============================================================================

  /** Camera I/O for view sync, backed by the drag-to-look controls. */
  const classroomView = useMemo<ClassroomViewAdapter>(
    () => ({
      read: () => lookControlsRef.current?.read() ?? null,
      apply: (view, { isDirect }) => {
        const renderer = rendererRef.current;
        const camera = cameraRef.current;
        if (renderer?.xr?.isPresenting && camera) {
          // Only an explicit Direct realigns a headset. Continuous follow is
          // suppressed in VR on purpose — applying it would fight the student's
          // own head movement, which is instant motion sickness.
          if (!isDirect) return;
          // Read where they are actually looking now, so a student who has
          // turned since the last Direct still ends up facing the teacher.
          const heading = cameraRotationToHV(camera).h;
          faceXrViewerTowards(renderer, heading, view.h);
          return;
        }
        // A Direct snaps; continuous follow eases, so it reads as the teacher
        // moving rather than the camera being seized.
        lookControlsRef.current?.apply(view.h, view.v, view.fov, isDirect);
      },
      subscribe: (listener) => {
        viewListenersRef.current.add(listener);
        return () => viewListenersRef.current.delete(listener);
      },
      isImmersive: () => rendererRef.current?.xr?.isPresenting === true,
    }),
    []
  );

  /**
   * Playback commands from the teacher. Deliberately declarative: it resets the
   * played-phase marker and flips autoplay rather than calling playTTSForPhase
   * directly, so the autoplay effect fires once with the phase that has actually
   * committed. Calling TTS here reads the phase through a stale closure and
   * double-plays the clip.
   */
  const handleClassPlaybackCommand = useCallback(
    (cmd: 'play' | 'pause' | 'replay') => {
      if (cmd === 'pause') {
        setAutoplayEnabled(false);
        narrationRef.current?.pause();
        return;
      }
      stopAudio();
      lastPlayedPhaseRef.current = null;
      setLessonStarted(true);
      setAutoplayEnabled(true);
    },
    [stopAudio]
  );

  /** The topic on screen: what progress reports and the quiz gate are scoped to. */
  const currentTopicId = String((lessonData as any)?.topic?.topic_id ?? '');
  /** Read by the topic-swap effect without making the topic one of its dependencies. */
  const currentTopicIdRef = useRef(currentTopicId);
  currentTopicIdRef.current = currentTopicId;

  const classroom = useClassroomSession({
    playerPhase: lessonPhase,
    setPlayerPhase: setLessonPhase,
    lessonReady: isSceneReady,
    allReady: isSceneReady && loadingState !== 'loading',
    view: classroomView,
    onPlaybackCommand: handleClassPlaybackCommand,
    pendingQuizRef: pendingQuizReportRef,
    topicId: currentTopicId || null,
    dragGraceTarget: rendererRef.current?.domElement ?? null,
    // Always true for this player: unlike the krpano HUD, V3's in-scene panels
    // are the only lesson UI and render the same flat as they do in a headset.
    immersiveUiDeviceCapable: true,
  });

  // ============================================================================
  // Moving and resizing the lesson panel
  //
  // drawLessonPanel has always drawn a grab bar and a resize corner, and this
  // player used to swallow both, so a panel that landed across a model or out of
  // a student's comfortable view stayed there. The ray maths and the remembered
  // placement live in lib/three/panelPlacement; what is here is only which input
  // owns a drag and where its ray comes from each frame.
  // ============================================================================

  /**
   * Every position in the room for the models currently loaded, under this
   * lesson's layout (lessonLayout.ts): overlap-free slots, each model's size,
   * and the panel.
   *
   * In a headset the panel stands clear of the models. On a flat screen that
   * bearing is off the edge of the window — -45 degrees with a 40 degree panel
   * is past the left edge of a 16:9 view — so it is fitted to what the camera
   * actually shows instead.
   */
  const frameFor = useCallback((anchor: WorkspaceAnchor): PlannedFrame => {
    const camera = cameraRef.current;
    const flat =
      rendererRef.current?.xr?.isPresenting || !camera ? null : flatPanelLayout(camera.aspect, camera.fov);
    const ids = ((assetsGroupRef.current?.children ?? []) as THREE.Object3D[]).map((child, i) =>
      child.name.startsWith('assetGroup_') ? child.name.slice('assetGroup_'.length) : `asset_${i}`
    );
    return planWorkspace(anchor, lessonLayoutRef.current, ids, { flat });
  }, []);

  /**
   * Put the panel where this viewer last left it, or where `frame` says.
   *
   * Sizes the geometry from the frame either way: the frame decides the panel's
   * size in metres, and a viewer's resize lives only in the mesh's scale, so
   * re-laying out never compounds one on the other. Left alone mid-drag — a
   * recentre must not yank the panel out from under the hand holding it; the
   * drag saves against the new anchor when it ends.
   */
  const placeLessonPanel = useCallback((mesh: THREE.Mesh, frame: WorkspaceFrame) => {
    const geometry = mesh.geometry as THREE.PlaneGeometry;
    const current = geometry?.parameters;
    if (
      !current ||
      Math.abs(current.width - frame.panel.width) > 1e-3 ||
      Math.abs(current.height - frame.panel.height) > 1e-3
    ) {
      geometry?.dispose();
      mesh.geometry = new THREE.PlaneGeometry(frame.panel.width, frame.panel.height);
    }
    if (panelDragRef.current) return;

    const mode: ViewMode = rendererRef.current?.xr?.isPresenting ? 'xr' : 'flat';
    const pref = loadPref(panelPrefStorage(lessonLayoutRef.current.preset), 'panel', mode);
    if (pref) {
      const pose = poseFromPref(frame.anchor, pref);
      mesh.position.copy(pose.position);
      mesh.rotation.set(0, pose.yaw, 0);
      mesh.scale.setScalar(pose.scale);
    } else {
      mesh.position.copy(frame.panel.position);
      mesh.rotation.set(0, frame.panel.yaw, 0);
      mesh.scale.setScalar(1);
    }
  }, []);

  /** The double-press on the grab bar: forget the placement, back to the frame's. */
  const resetPanelPlacement = useCallback(() => {
    const mode: ViewMode = rendererRef.current?.xr?.isPresenting ? 'xr' : 'flat';
    savePref(panelPrefStorage(lessonLayoutRef.current.preset), 'panel', mode, null);
    panelDragRef.current = null;
    const mesh = lessonPanelRef.current?.mesh;
    const anchor = workspaceAnchorRef.current;
    if (!mesh || !anchor) return;
    placeLessonPanel(mesh, frameFor(anchor));
  }, [frameFor, placeLessonPanel]);

  /**
   * A press that may have landed on the grab bar or the resize corner.
   *
   * Returns false when `action` is neither, so the caller goes on to treat the
   * press as an ordinary button press. Every input — controller trigger, pinch,
   * mouse, touch — comes through here, so they all share one double-press and one
   * notion of what a drag is.
   */
  const pressPanelHandle = useCallback(
    (
      action: string | null,
      ray: THREE.Ray,
      hitPoint: THREE.Vector3,
      source: PanelDrag['source'],
      owner: PanelDrag['owner']
    ): boolean => {
      if (action !== 'panelGrab' && action !== 'panelResize') return false;
      const mesh = lessonPanelRef.current?.mesh;
      if (!mesh) return false;

      if (action === 'panelGrab') {
        const now = performance.now();
        const isDouble = now - lastPanelGrabPressRef.current < PANEL_RESET_DOUBLE_PRESS_MS;
        // Zeroed after a reset so a third quick press starts a drag rather than
        // resetting again.
        lastPanelGrabPressRef.current = isDouble ? 0 : now;
        if (isDouble) {
          resetPanelPlacement();
          return true;
        }
      }

      const drag: PanelDrag = {
        source,
        owner,
        mode: rendererRef.current?.xr?.isPresenting ? 'xr' : 'flat',
        startPosition: mesh.position.clone(),
        startScale: mesh.scale.x,
      };
      if (action === 'panelResize') {
        // The panel faces along its local +Z; the per-frame turn keeps that
        // toward the viewer, so this is the plane the viewer is looking at.
        const normal = new THREE.Vector3(0, 0, 1).applyQuaternion(mesh.quaternion);
        drag.resize = beginResize(mesh.position, normal, hitPoint, mesh.scale.x);
      } else if (source === 'pointer') {
        const camera = cameraRef.current;
        const normal = camera
          ? camera.getWorldDirection(new THREE.Vector3()).negate()
          : ray.direction.clone().negate();
        drag.plane = {
          plane: new THREE.Plane().setFromNormalAndCoplanarPoint(normal, hitPoint),
          offset: mesh.position.clone().sub(hitPoint),
        };
      } else {
        drag.move = beginMove(ray, hitPoint, mesh.position);
      }
      panelDragRef.current = drag;
      return true;
    },
    [resetPanelPlacement]
  );

  /** Carry the drag along the owning input's current ray. */
  const updatePanelDrag = useCallback((ray: THREE.Ray) => {
    const drag = panelDragRef.current;
    const mesh = lessonPanelRef.current?.mesh;
    if (!drag || !mesh) return;
    if (drag.resize) {
      const scale = updateResize(drag.resize, ray);
      if (scale !== null) mesh.scale.setScalar(scale);
    } else if (drag.move) {
      mesh.position.copy(updateMove(drag.move, ray));
    } else if (drag.plane) {
      const point = ray.intersectPlane(drag.plane.plane, new THREE.Vector3());
      if (point) mesh.position.copy(point.add(drag.plane.offset));
    }
  }, []);

  /**
   * Finish the drag and remember the result, relative to the anchor.
   *
   * The saved preference is clamped, and the panel is snapped to exactly what
   * was saved, so a panel pulled past the limits settles where it will reopen
   * instead of jumping there on the next load.
   */
  const endPanelDrag = useCallback(() => {
    const drag = panelDragRef.current;
    panelDragRef.current = null;
    const mesh = lessonPanelRef.current?.mesh;
    const anchor = workspaceAnchorRef.current;
    if (!drag || !mesh || !anchor) return;
    // A press that never moved (the first half of a double-press, or a tap on
    // the bar) saves nothing, so the panel keeps following the frame's default.
    // "Never moved" allows a centimetre: a controller ray or fingertip always
    // drifts a few millimetres during a tap and the drag follows it every frame,
    // so a tighter test would turn every tap into a saved placement that stops
    // the panel following the frame when the model count changes. The jitter
    // is undone so the panel sits exactly where the frame put it.
    if (
      mesh.position.distanceTo(drag.startPosition) < 0.01 &&
      Math.abs(mesh.scale.x - drag.startScale) < 0.01
    ) {
      mesh.position.copy(drag.startPosition);
      mesh.scale.setScalar(drag.startScale);
      return;
    }
    const pref = prefFromPose(anchor, mesh.position, mesh.scale.x);
    savePref(panelPrefStorage(lessonLayoutRef.current.preset), 'panel', drag.mode, pref);
    const pose = poseFromPref(anchor, pref);
    mesh.position.copy(pose.position);
    mesh.rotation.set(0, pose.yaw, 0);
    mesh.scale.setScalar(pose.scale);
  }, []);

  /**
   * Per frame: steer a controller-owned drag, and end it once the trigger is up.
   *
   * Ended here from the `selecting` flag rather than from selectend, because the
   * press itself reaches handleControllerSelect through a 50ms debounce — a quick
   * click can release before the drag has even begun, and a drag begun after its
   * own release would otherwise follow the controller until the next press.
   */
  const updateControllerPanelDrag = useCallback(() => {
    const drag = panelDragRef.current;
    if (!drag || drag.source !== 'controller') return;
    const controller = drag.owner as THREE.Object3D;
    // The session ended mid-drag: drop it without saving. The sessionend
    // handler normally clears it first; this covers a frame that runs between.
    if (!rendererRef.current?.xr?.isPresenting) {
      panelDragRef.current = null;
      return;
    }
    if (!controller.userData.selecting) {
      endPanelDrag();
      return;
    }
    updatePanelDrag(controllerRay(controller));
  }, [endPanelDrag, updatePanelDrag]);

  /**
   * Per-frame hand handling: point to reach the panel, pinch to act.
   *
   * Two interactions, chosen by where the hand is aimed:
   *
   *   - POINTING at the lesson panel casts a ray from the fingertip and pinching
   *     presses whatever is under it. This is what lets a student sitting at the
   *     back attempt the quiz without walking up to the panel — the reason the
   *     ray exists at all.
   *   - Otherwise a pinch near a 3D asset grabs it, driving the SAME layout
   *     system the controllers use. Hands are another input source, not a
   *     parallel implementation, so asset behaviour is untouched.
   */
  const updateHands = useCallback(() => {
    const renderer = rendererRef.current;
    const camera = cameraRef.current;
    const raycaster = raycasterRef.current;
    if (!renderer?.xr?.isPresenting || !camera || !raycaster) return;

    const panelMesh = lessonPanelRef.current?.mesh;

    for (const slot of handsRef.current) {
      const state = slot.tracker.update(slot.hand as never);
      slot.last = state;
      if (state.justPinched) slot.pinchedAt = performance.now();
      const slotIndex = handsRef.current.indexOf(slot);
      const panelDrag = panelDragRef.current;
      const draggingPanel = panelDrag?.source === 'hand' && panelDrag.owner === slotIndex;

      // Resolve the controller's ray lazily: it is created when the controller
      // connects, which may be after the hands were set up.
      if (!slot.controllerRay) {
        const controller = renderer.xr.getController(slotIndex);
        slot.controllerRay = controller?.getObjectByName('ray') ?? null;
      }
      // Exactly one pointer. A tracked hand owns the slot; otherwise the
      // controller does.
      if (slot.controllerRay) slot.controllerRay.visible = !state.tracked;
      slot.pointer.visible = state.tracked;

      if (!state.tracked) {
        // Same for the panel: a hand the runtime lost cannot release it.
        if (draggingPanel) endPanelDrag();
        if (slot.grabbing) {
          // Hand lost. Drop what it held rather than leaving an asset stuck to
          // a hand the runtime can no longer see.
          stableLayoutRef.current?.releaseGrab();
          slot.grabbing = false;
        }
        continue;
      }

      // The proxy carries BOTH the pinch position and the hand's orientation.
      // updateGrab reads position and quaternion from whatever it was given, so
      // supplying a real rotation here is what makes a held object turn with the
      // wrist — previously this was an empty Object3D and objects only slid.
      if (state.pinchPoint) slot.grabProxy.position.copy(state.pinchPoint);
      if (state.orientation) slot.grabProxy.quaternion.copy(state.orientation);
      slot.grabProxy.updateMatrixWorld();

      // --- Pointing at the panel ---------------------------------------
      // The pointer model raycasts from its own tip, so the panel can be reached
      // from across the room — the reason a student at the back can answer.
      let aimingAtPanel = false;
      if (panelMesh && panelMesh.visible) {
        const hit = slot.pointer.intersectObject(panelMesh, false)?.[0];
        if (hit?.uv) {
          aimingAtPanel = true;
          // Park the cursor on the panel so the aim point is visible.
          slot.pointer.setCursor(hit.distance);
          if (state.justPinched) {
            const handle = actionAtUv(lessonPanelRegionsRef.current, hit.uv.x, hit.uv.y);
            const ray = slot.pointer.raycaster?.ray;
            if (!ray || !pressPanelHandle(handle, ray.clone(), hit.point, 'hand', slotIndex)) {
              lessonPanelUvRef.current(hit.uv.x, hit.uv.y);
            }
          }
        }
      }

      // --- Carrying the panel ------------------------------------------
      // Steered by this hand's own pointer ray until the pinch opens. The ray
      // is the one the pointer model just aimed from the fingertip, so the grab
      // bar stays under the cursor the student is looking at.
      if (draggingPanel) {
        if (state.justReleased) {
          endPanelDrag();
        } else if (slot.pointer.raycaster) {
          updatePanelDrag(slot.pointer.raycaster.ray);
        }
      }

      /*
        A fist beats a recent pinch.

        Closing a fist brings the thumb onto the index finger on the way, so the
        hand reads as pinching a moment before it reads as a fist — and that
        pinch has already grabbed whatever was near it. A fist within
        FIST_BEATS_PINCH_MS of the pinch meant the fist: drop the grab and
        leave the hand to the two-fist explode below.
      */
      if (state.justGrabbed && slot.grabbing && performance.now() - slot.pinchedAt < FIST_BEATS_PINCH_MS) {
        stableLayoutRef.current?.releaseGrab();
        slot.grabbing = false;
      }

      // --- Pinch to grab an asset --------------------------------------
      // Only when not aiming at the panel: a pinch meant for a quiz option must
      // not also snatch whatever happens to be near the hand. Nor from a fist,
      // whose curled fingers read as a pinch too.
      if (!aimingAtPanel && !state.grabbing && state.justPinched && state.pinchPoint && stableLayoutRef.current) {
        const meshes = stableLayoutRef.current.getAllInteractableMeshes?.() ?? [];
        let nearest: THREE.Object3D | null = null;
        // Roughly a hand's reach around the pinch, so grabbing feels like
        // touching rather than pointing.
        let nearestDistance = 0.25;
        const worldPosition = new THREE.Vector3();
        for (const mesh of meshes) {
          mesh.getWorldPosition(worldPosition);
          const distance = worldPosition.distanceTo(state.pinchPoint);
          if (distance < nearestDistance) {
            nearest = mesh;
            nearestDistance = distance;
          }
        }
        if (nearest && stableLayoutRef.current.startGrab(nearest, slot.grabProxy as THREE.Group)) {
          slot.grabbing = true;
        }
      }

      if (state.justReleased && slot.grabbing) {
        // Left exactly where it is — updateGrab already moved it here.
        stableLayoutRef.current?.releaseGrab();
        slot.grabbing = false;
      }
    }

    /*
      Two-hand model gestures, once both hands have been read this frame.

        - One hand holding a model, the other pinching: spread and turn the
          hands to size and turn what is held (twoPointGesture).
        - Two fists: pull them apart to explode the model nearest them
          (explodeGesture). Measured from where the fists closed, so the
          model does not jump to whatever the starting spread would mean.

      Either stops the moment a hand opens or leaves tracking; starting again
      re-anchors on the model as it now is.
    */
    const [first, second] = handsRef.current;
    const live = modelGestureRef.current;
    const gestures = modelGesturesRef.current;
    const one = first?.last;
    const other = second?.last;

    if (one?.tracked && other?.tracked && one.grabbing && other.grabbing) {
      const spread = one.pinchPoint && other.pinchPoint ? one.pinchPoint.distanceTo(other.pinchPoint) : NaN;
      if (!live.fists) {
        const { mid } = gestureScratch;
        mid.copy(first.grabProxy.position).add(second.grabProxy.position).multiplyScalar(0.5);
        const key = gestures.nearestKey(mid, FIST_REACH_M);
        if (key) live.fists = { key, state: beginExplode(spread, gestures.explodeOf(key)) };
      } else {
        // Compared with the gesture's own last output, not the model: a teacher
        // publish mid-gesture would otherwise read as a change and be undone.
        const before = live.fists.state.amount;
        const amount = updateExplode(live.fists.state, spread);
        if (amount !== before) gestures.setExplode(live.fists.key, amount);
      }
    } else {
      live.fists = null;
    }

    const holder = first?.grabbing ? first : second?.grabbing ? second : null;
    const helper = holder === first ? second : first;
    const heldKey = holder ? gestures.keyOf(stableLayoutRef.current?.getGrabbedModel()) : null;
    const holdPoint = holder?.last?.pinchPoint;
    const helpPoint = helper?.last?.tracked && helper.last.pinching && !helper.last.grabbing ? helper.last.pinchPoint : null;
    if (heldKey && holdPoint && helpPoint) {
      const sample = sampleFromPoints(holdPoint, helpPoint);
      if (!live.twoHand || live.twoHand.key !== heldKey) {
        live.twoHand = {
          key: heldKey,
          state: beginTwoPoint(sample, { scale: gestures.scaleOf(heldKey), yaw: gestures.yawOf(heldKey) }),
        };
      } else {
        const { scale, yaw } = updateTwoPoint(live.twoHand.state, sample, STUDENT_SCALE_LIMITS);
        gestures.setScale(heldKey, scale);
        gestures.setYaw(heldKey, yaw);
      }
    } else {
      live.twoHand = null;
    }
  }, [pressPanelHandle, updatePanelDrag, endPanelDrag]);

  /**
   * Controller model gestures, once per frame.
   *
   *   - Grip plus the thumbstick forward or back explodes the model the grip
   *     closed on. The stick is a rate (stickExplodeStep), so holding it over
   *     pulls the parts steadily apart.
   *   - Both grips held: spreading the controllers sizes that model and turning
   *     the pair turns it — the same twoPointGesture as two hands.
   *
   * Grip, not trigger: the trigger already selects, grabs and presses the
   * panel, and overloading it would make every press a potential resize.
   */
  const updateControllerModelGestures = useCallback((delta: number) => {
    const renderer = rendererRef.current;
    if (!renderer?.xr?.isPresenting) return;
    const gestures = modelGesturesRef.current;
    const live = modelGestureRef.current;
    const left = renderer.xr.getController(0);
    const right = renderer.xr.getController(1);
    const { a, b, mid } = gestureScratch;

    if (left.userData.squeezing && right.userData.squeezing) {
      left.getWorldPosition(a);
      right.getWorldPosition(b);
      if (!live.twoGrip) {
        mid.copy(a).add(b).multiplyScalar(0.5);
        const key =
          (left.userData.gripKey as string | null) ??
          (right.userData.gripKey as string | null) ??
          gestures.nearestKey(mid, GRIP_REACH_M);
        if (key) {
          live.twoGrip = {
            key,
            state: beginTwoPoint(sampleFromPoints(a, b), { scale: gestures.scaleOf(key), yaw: gestures.yawOf(key) }),
          };
        }
        return;
      }
      const { scale, yaw } = updateTwoPoint(live.twoGrip.state, sampleFromPoints(a, b), STUDENT_SCALE_LIMITS);
      gestures.setScale(live.twoGrip.key, scale);
      gestures.setYaw(live.twoGrip.key, yaw);
      return;
    }
    live.twoGrip = null;

    for (let i = 0; i < 2; i++) {
      const controller = i === 0 ? left : right;
      const stick = i === 0 ? gestureScratch.stick0 : gestureScratch.stick1;
      const key = controller.userData.gripKey as string | null | undefined;
      if (!controller.userData.squeezing || !key || !readStick(controller, stick)) continue;
      const current = gestures.explodeOf(key);
      const next = stickExplodeStep(current, stick.y, delta);
      if (next !== current) gestures.setExplode(key, next);
    }
  }, []);

  /**
   * End any immersive session, then navigate.
   *
   * Awaiting session.end() matters: navigating first unmounts the renderer and
   * leaves the headset presenting a scene that no longer has anything driving
   * it. Falls through to the navigation whatever happens, so a runtime that
   * refuses to end still gets the student out.
   */
  const leaveImmersiveThenNavigate = useCallback(
    async (to: string) => {
      try {
        const session = rendererRef.current?.xr?.getSession?.();
        if (session) await session.end();
      } catch (err) {
        console.warn('[XRLessonPlayerV3] Could not end the XR session cleanly:', err);
      }
      navigate(to, { replace: true });
    },
    [navigate]
  );

  /**
   * Removed mid-lesson: leave immediately.
   *
   * The dashboard route in ClassSessionContext handles the plain removal case,
   * but a student sitting inside the player should not wait on a route change
   * elsewhere to notice, and a rejoin request must not keep them in the lesson.
   */
  useEffect(() => {
    if (!classroom.isStudentInSession) return;
    if (classroom.isStudentRemoved || !classroom.isAdmitted) {
      toast.info('Your teacher removed you from this class.');
      void leaveImmersiveThenNavigate('/dashboard/student');
    }
    // leaveImmersiveThenNavigate is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroom.isStudentInSession, classroom.isStudentRemoved, classroom.isAdmitted]);

  /**
   * The teacher ended the class: get the student out of the headset.
   *
   * ClassSessionContext already routes them to the dashboard when the session
   * status turns 'ended', but nothing ended the WebXR session — so a student
   * wearing a headset stayed in a scene that was no longer live while the page
   * changed underneath them. Leaving XR first is what they actually experience
   * as the class finishing.
   */
  useEffect(() => {
    if (!classroom.isStudentInSession) return;
    if (classroom.joinedSession?.status !== 'ended') return;
    toast.info('Your teacher ended the class.');
    void leaveImmersiveThenNavigate('/dashboard/student');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroom.isStudentInSession, classroom.joinedSession?.status]);

  // ============================================================================
  // Teacher marker
  //
  // Strokes are stored in sphere coordinates, the same format the krpano player
  // publishes, so a lesson drawn in one player renders in the other. Published
  // on pointer-up only: the teacher sees their own stroke locally at full frame
  // rate and the class receives it complete, which is roughly a 50x reduction in
  // writes compared with publishing during the drag.
  // ============================================================================

  // One place that keeps the loop's copy of the explode amount honest, whether
  // it changed from the toolbar, from a reset, or from the teacher's broadcast.
  useEffect(() => {
    modelExplodeRef.current = modelExplode;
  }, [modelExplode]);

  useEffect(() => {
    markerActiveRef.current = markerActive;
    markerColorRef.current = markerColor;
    // Dragging must not also swing the camera while the marker is down.
    lookControlsRef.current?.setEnabled(!markerActive);
  }, [markerActive, markerColor]);

  const comfortBreak = useComfortBreak({
    isImmersive: isPresentingXR,
    enabled: !classroom.isClassHost,
  });

  /**
   * Identity of the scene these model controls act on.
   *
   * Published alongside the state so a student who has moved on to a different
   * lesson ignores a stale explode value rather than applying it to the wrong
   * model.
   */
  const modelSceneKey = useMemo(() => {
    const chapter = (lessonData as any)?.chapter?.chapter_id ?? '';
    const topic = (lessonData as any)?.topic?.topic_id ?? '';
    return chapter && topic ? `${chapter}:${topic}` : '';
  }, [lessonData]);

  /**
   * Publishing is throttled.
   *
   * The explode slider fires continuously while dragged; writing every frame
   * would put a document write per frame in front of the whole class. 200ms is
   * imperceptible in motion and turns a drag into a handful of writes.
   */
  const modelPublishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modelPendingRef = useRef<Partial<TeacherContentState> | null>(null);
  const publishModelPatch = useCallback(
    (patch: Partial<TeacherContentState>, immediate = false) => {
      if (!classroomRef.current.isClassHost || !modelSceneKey) return;
      modelPendingRef.current = { ...(modelPendingRef.current ?? {}), ...patch };

      const flush = () => {
        modelPublishTimerRef.current = null;
        const pending = modelPendingRef.current;
        modelPendingRef.current = null;
        if (pending) classroomRef.current.publishModelState(pending, modelSceneKey);
      };

      if (immediate) {
        if (modelPublishTimerRef.current) clearTimeout(modelPublishTimerRef.current);
        flush();
        return;
      }
      if (modelPublishTimerRef.current) return;
      modelPublishTimerRef.current = setTimeout(flush, 200);
    },
    [modelSceneKey]
  );

  /**
   * Model tool handlers.
   *
   * Each applies locally and publishes to the class, so students see the same
   * state. Isolate needs a selected part to isolate TO, which comes from a
   * pick — that is the same pick the label-the-part question reads.
   */
  const applyModelExplode = useCallback(
    (t: number) => {
      setModelExplode(t);
      modelExplodeRef.current = t;
      modelToolsRef.current?.explode(t);
      publishModelPatch({ exploded: t });
    },
    [publishModelPatch]
  );

  /**
   * Choose which asset the controls act on.
   *
   * Explode and section then affect that asset alone, which is the whole point
   * once a lesson holds several: pulling every model in the room apart to show
   * one of them is noise. The part count follows the target, so Explode is
   * correctly offered or disabled for the asset actually selected rather than
   * for the sum of everything in the scene.
   */
  const applyModelSelectAsset = useCallback(
    (key: string | null) => {
      const tools = modelToolsRef.current;
      if (!tools) return;
      tools.select(key);
      const resolved = tools.selectedKey();
      setModelSelectedAssetKey(resolved);
      setModelPartCount(tools.partCount());
      // A part of the old asset is not a part of the new one.
      setModelSelectedPartId(null);
      setModelSelectedPartName(null);
      if (modelIsolated) {
        tools.isolate(null, false);
        setModelIsolated(false);
      }
      publishModelPatch(
        {
          selected_asset_key: resolved,
          selected_part_id: null,
          selected_part_name: null,
          isolated: false,
        },
        true
      );
    },
    [modelIsolated, publishModelPatch]
  );

  /** A part picked in the scene: target its asset, and remember the part. */
  const selectPickedPart = useCallback(
    (key: string, partId: string, name: string) => {
      const tools = modelToolsRef.current;
      if (!tools) return;
      tools.select(key);
      const resolved = tools.selectedKey();
      setModelSelectedAssetKey(resolved);
      setModelPartCount(tools.partCount());
      setModelSelectedPartId(partId);
      setModelSelectedPartName(name || null);
      // Already isolating? Follow the new pick rather than leaving the class
      // staring at the part the teacher has just moved on from.
      if (modelIsolated) tools.isolate(partId, true);
      publishModelPatch(
        { selected_asset_key: resolved, selected_part_id: partId, selected_part_name: name || null },
        true
      );
    },
    [modelIsolated, publishModelPatch]
  );

  useEffect(() => {
    selectPickedPartRef.current = selectPickedPart;
  }, [selectPickedPart]);

  /**
   * Publish the glowing set, with each part's tag text.
   *
   * The whole list every time, not a delta: it is at most eight entries, and a
   * late joiner has to be able to rebuild the set from the document alone.
   * Written through the merged publish, so it cannot knock out a concurrent
   * explode or image change. Null, not [], for none — the same "absent" every
   * other cleared field uses.
   */
  const publishHighlights = useCallback(
    (immediate: boolean) => {
      const parts = (modelToolsRef.current?.highlighted() ?? []).map(({ partId, color }) => ({
        part_id: partId,
        label: partLabelTextRef.current.get(partId) ?? null,
        color,
      }));
      setModelHighlightCount(parts.length);
      publishModelPatch({ highlighted_parts: parts.length > 0 ? parts : null }, immediate);
    },
    [publishModelPatch]
  );

  /**
   * A tap on a part in highlight mode: light it up, or put it out if it already
   * glows.
   *
   * Goes through selectPickedPart as well, so the tapped part is also the
   * selection Isolate acts on and selected_part_id reaches the class — the old
   * model mark published only a name, which addressed nothing. The highlight is
   * queued first and the selection publishes immediately, so both leave in one
   * write.
   */
  const toggleHighlight = useCallback(
    (picked: PickedPart) => {
      const tools = modelToolsRef.current;
      if (!tools) return;
      const current = tools.highlighted();
      const lit = current.some((part) => part.partId === picked.partId);
      if (!lit && current.length >= MAX_HIGHLIGHTED_PARTS) {
        // modelTools would silently drop the extra; say so instead.
        toast.info(`Up to ${MAX_HIGHLIGHTED_PARTS} parts can glow at once. Clear one first.`);
        return;
      }
      if (lit) {
        partLabelTextRef.current.delete(picked.partId);
      } else {
        const index = Number(picked.partId.slice(picked.partId.lastIndexOf('#') + 1));
        partLabelTextRef.current.set(picked.partId, resolvePartLabel(picked.mesh, index));
      }
      tools.highlight(
        lit
          ? current.filter((part) => part.partId !== picked.partId)
          : [...current, { partId: picked.partId, color: DEFAULT_HIGHLIGHT_COLOR }]
      );
      syncPartLabels();
      publishHighlights(false);
      selectPickedPart(picked.key, picked.partId, picked.name);
    },
    [publishHighlights, selectPickedPart, syncPartLabels]
  );

  useEffect(() => {
    toggleHighlightRef.current = toggleHighlight;
  }, [toggleHighlight]);

  useEffect(() => {
    highlightModeRef.current = modelHighlightMode;
  }, [modelHighlightMode]);

  /** The toolbar's "Clear (n)": every highlight off, for the whole class. */
  const clearModelHighlights = useCallback(() => {
    modelToolsRef.current?.highlight([]);
    partLabelTextRef.current.clear();
    syncPartLabels();
    publishHighlights(true);
  }, [publishHighlights, syncPartLabels]);

  const applyModelIsolate = useCallback(() => {
    setModelIsolated((wasIsolated) => {
      const next = !wasIsolated;
      modelToolsRef.current?.isolate(modelSelectedPartId, next);
      publishModelPatch(
        {
          isolated: next,
          selected_part_id: modelSelectedPartId ?? null,
          selected_part_name: modelSelectedPartName ?? null,
        },
        true
      );
      return next;
    });
  }, [modelSelectedPartId, modelSelectedPartName, publishModelPatch]);

  const applyModelClip = useCallback(
    (clip: { axis: ClipAxis; offset: number } | null) => {
      setModelClip(clip);
      modelToolsRef.current?.clip(clip?.axis ?? null, clip?.offset ?? 0, rendererRef.current);
      publishModelPatch({ clip });
    },
    [publishModelPatch]
  );

  /**
   * The flat view's long-standing heights.
   *
   * On a screen there is no ergonomics to solve — no neck, no reach, just
   * framing — so the desktop layout is pinned to exactly the numbers it has
   * always used rather than derived. Only the headset gets a fitted room.
   */
  const DESKTOP_LAYOUT: ViewerLayout = {
    eyeHeight: DEFAULT_EYE_HEIGHT,
    dockHeight: 0.9,
    panelHeight: 1.6,
  };

  /**
   * Put the desk, the assets on it and the lesson panel at the given heights.
   *
   * Re-places the assets rather than only moving the dock: raising a surface
   * out from under what is standing on it would leave every model hanging in
   * mid-air, which is the same class of bug as placing them there in the first
   * place.
   */
  const applyWorkspaceLayout = useCallback(
    (view: ViewerLayout, recapture = false) => {
      const scene = sceneRef.current;
      const camera = cameraRef.current;
      if (!scene || !camera) return;

      /*
        One anchor, and everything derived from it.

        Recaptured only when asked: on entering a headset, where the viewer's
        real eye height first becomes known, and on an explicit recentre. A
        workspace that re-derived itself from the live camera would drift every
        time somebody looked away, which is what the three separate layout
        systems were each doing on their own schedule.
      */
      lastViewRef.current = view;
      if (recapture || !workspaceAnchorRef.current) {
        workspaceAnchorRef.current = captureAnchor(camera, GROUND_LEVEL);
      }
      const anchor = { ...workspaceAnchorRef.current!, eyeHeight: view.eyeHeight };
      workspaceAnchorRef.current = anchor;

      const assets = (assetsGroupRef.current?.children ?? []) as THREE.Object3D[];
      const frame = frameFor(anchor);

      // The panel: same anchor, deliberate bearing, sized by the angle it
      // subtends rather than a fixed width that happened to span 62 degrees —
      // or wherever this viewer last put it.
      const panel = lessonPanelRef.current?.mesh;
      if (panel) placeLessonPanel(panel, frame);

      const layout = sceneLayoutRef.current;
      if (!layout) return;
      // The room follows the lesson: its subject and grade pick the theme (stone
      // desk and rocks for Geography, pottery and ruins for History…), and the
      // lesson seeds which pieces stand where, so the whole class sees one room.
      const room = roomContextRef.current;
      const theme = themeFor({
        subject: room.subject,
        classNumber: room.classNumber,
        preset: frame.layout.preset,
        override: frame.layout.roomTheme === 'auto' ? null : frame.layout.roomTheme,
      });
      layout.createAssetDockFromFrame(scene, frame, theme.finish);
      placeRoomProps(
        scene,
        frame.layout.showProps
          ? planLessonRoom(frame, theme, room, frame.layout.propSeed, {
              bearingDeg: frame.panelBearingDeg,
              widthDeg: frame.panelWidthDeg,
            })
          : null,
        frame.anchor.groundY,
        frame.anchor.position
      );
      placeScaleNote(scene, frame);

      // Never yank a model out of somebody's hand mid-lesson.
      if (assets.length === 0 || stableLayoutRef.current?.isGrabbing()) return;

      const placements = layout.placementsFromFrame(frame);
      assetPlacementsRef.current = placements;
      assets.forEach((asset, index) => {
        const placement = placements[index];
        if (!placement) return;
        // placeAssetOnDock measures the model's real bounding box, which is the
        // part that already works; it only needed telling where to put it.
        layout.placeAssetOnDock(asset, placement, camera, GROUND_LEVEL, assets.length);
        asset.userData.dockSurfaceY = placement.dockSurfaceY;
        // How far this model may come apart before it would reach its neighbour.
        asset.userData.explodeReach = frame.slots[index]?.explodeReach ?? 1;
        /*
          What the dock gave it, so this viewer's own size and turn stay
          relative to it — and are put back on top here, so entering the
          headset or recentring re-places the model without undoing them.
        */
        asset.userData.dockScale = asset.scale.x;
        asset.userData.dockYaw = asset.rotation.y;
        const key = modelToolsRef.current?.keyOf(asset);
        const overrides = localModelRef.current;
        if (key && (key in overrides.scale || key in overrides.yaw)) applyAssetOverrides(asset, overrides, key);
      });

      // A cut follows the model it was made on, so re-derive it where the model
      // has just been moved to.
      modelToolsRef.current?.update();
      requestShadowRefresh(rendererRef.current);
      addDebug(
        `Workspace: eye ${frame.anchor.eyeHeight.toFixed(2)}m, desk ${frame.deskY.toFixed(2)}m, ` +
          `${frame.slots.length} slot(s), panel ${frame.panel.width.toFixed(2)}m`
      );
    },
    [addDebug, frameFor, placeLessonPanel]
  );

  // The lesson's layout, and a re-layout when it (or the global default) arrives
  // after the room was built. Same anchor and heights: only the arrangement changes.
  const topicLayout = (lessonData?.topic as { ui_layout?: unknown } | undefined)?.ui_layout;
  const resolvedLayout = useMemo(
    () => resolveLessonLayout(topicLayout, globalLayoutDefault),
    [topicLayout, globalLayoutDefault]
  );
  const roomSubject = lessonData?.chapter?.subject ?? '';
  const roomClass = lessonData?.chapter?.class_name ?? '';
  const roomChapterId = lessonData?.chapter?.chapter_id ?? '';
  const roomTopicId = lessonData?.topic?.topic_id ?? '';
  // The topic's place in its chapter, so each topic's room gets its own pieces:
  // topic_index where the launch path knows it, else the 1-based topic_priority.
  const roomTopicMeta = lessonData?.topic as { topic_index?: unknown; topic_priority?: unknown } | undefined;
  const roomTopicIndex =
    typeof roomTopicMeta?.topic_index === 'number' && roomTopicMeta.topic_index >= 0
      ? roomTopicMeta.topic_index
      : Math.max(0, (Number(roomTopicMeta?.topic_priority) || 1) - 1);
  useEffect(() => {
    lessonLayoutRef.current = resolvedLayout;
    roomContextRef.current = {
      subject: roomSubject,
      classNumber: classNumberOf(roomClass),
      chapterId: roomChapterId,
      topicId: roomTopicId,
      topicIndex: roomTopicIndex,
    };
    if (lastViewRef.current) applyWorkspaceLayout(lastViewRef.current);
  }, [resolvedLayout, roomSubject, roomClass, roomChapterId, roomTopicId, roomTopicIndex, applyWorkspaceLayout]);

  useEffect(() => {
    // The desktop layout, used when assets finish loading before any headset
    // session exists. Recaptures, because that is the first moment there is a
    // scene to anchor to.
    applyWorkspaceLayoutRef.current = () => applyWorkspaceLayout(DESKTOP_LAYOUT, true);
    applyViewerErgonomicsRef.current = () =>
      // Recapture: entering a headset is the first moment the viewer's real eye
      // height and facing are known, and it is where they are actually sitting.
      applyWorkspaceLayout(layoutForViewer(cameraRef.current, GROUND_LEVEL), true);
    restoreDeskHeightRef.current = () => applyWorkspaceLayout(DESKTOP_LAYOUT, true);
  }, [applyWorkspaceLayout]);

  /*
    This viewer's own model gestures: shrink, expand, turn and explode.

    Size and turn are local for everyone, the teacher included — the class
    shares one idea of what a model IS, not of how big each person likes it,
    and the player has never synced model_transform. Explode is local for a
    student (modelPrecedence decides when a teacher publish overrides it), but a
    teacher's explode goes through the published path like the toolbar slider:
    a teacher pulling a model apart is showing the class something.
  */
  const localModelUiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Gestures change the overrides every frame; the dock only needs to catch up. */
  const refreshLocalModelUi = useCallback(() => {
    if (localModelUiTimerRef.current) return;
    localModelUiTimerRef.current = setTimeout(() => {
      localModelUiTimerRef.current = null;
      setLocalModelVersion((version) => version + 1);
      if (classroomRef.current.isClassHost) setModelExplode(modelExplodeRef.current);
    }, 120);
  }, []);

  const assetForKey = useCallback((key: string): THREE.Object3D | null => {
    const tools = modelToolsRef.current;
    if (!tools) return null;
    for (const child of assetsGroupRef.current?.children ?? []) {
      if (tools.keyOf(child) === key) return child;
    }
    return null;
  }, []);

  const setLocalModelScale = useCallback(
    (key: string, multiplier: number) => {
      if (!Number.isFinite(multiplier)) return;
      const next = Math.min(STUDENT_SCALE_LIMITS.max, Math.max(STUDENT_SCALE_LIMITS.min, multiplier));
      const overrides = localModelRef.current;
      if ((overrides.scale[key] ?? 1) === next) return;
      overrides.scale[key] = next;
      const asset = assetForKey(key);
      // A held model scales about the hand holding it, not about its base.
      if (asset) applyAssetLocalScale(asset, next, stableLayoutRef.current?.getGrabbedModel() !== asset);
      refreshLocalModelUi();
    },
    [assetForKey, refreshLocalModelUi]
  );

  const setLocalModelYaw = useCallback(
    (key: string, yaw: number) => {
      if (!Number.isFinite(yaw)) return;
      const overrides = localModelRef.current;
      const previous = overrides.yaw[key] ?? 0;
      if (previous === yaw) return;
      overrides.yaw[key] = yaw;
      const asset = assetForKey(key);
      if (!asset) return;
      // The grab eases a held model back toward its grab rotation every frame,
      // so a held model is turned through the grab instead.
      if (stableLayoutRef.current?.getGrabbedModel() === asset) {
        stableLayoutRef.current.turnGrabbed(yaw - previous);
      } else {
        applyAssetLocalYaw(asset, yaw);
      }
    },
    [assetForKey]
  );

  /* A teacher's explode gesture, through the same path as the toolbar slider.
     A ref, because it closes over applyModelSelectAsset's current state. */
  const hostGestureExplodeRef = useRef<(key: string, amount: number) => void>(() => {});
  useEffect(() => {
    hostGestureExplodeRef.current = (key, amount) => {
      const tools = modelToolsRef.current;
      if (!tools) return;
      // The teacher's explode acts on the selected asset, so the model being
      // pulled apart becomes the selection first — once, not every frame.
      if (tools.selectedKey() !== key) applyModelSelectAsset(key);
      modelExplodeRef.current = amount;
      tools.explode(amount);
      publishModelPatch({ exploded: amount });
      refreshLocalModelUi();
    };
  }, [applyModelSelectAsset, publishModelPatch, refreshLocalModelUi]);

  const setLocalModelExplode = useCallback(
    (key: string, amount: number) => {
      const tools = modelToolsRef.current;
      if (!tools || !Number.isFinite(amount)) return;
      const next = Math.min(1, Math.max(0, amount));
      if (classroomRef.current.isClassHost) {
        hostGestureExplodeRef.current(key, next);
        return;
      }
      localModelRef.current.explode[key] = next;
      tools.explodeAsset(key, next);
      refreshLocalModelUi();
    },
    [refreshLocalModelUi]
  );

  useEffect(() => {
    modelGesturesRef.current = {
      keyOf: (object) => modelToolsRef.current?.keyOf(object) ?? null,
      keyAlongRay: (ray) => {
        const raycaster = raycasterRef.current;
        const tools = modelToolsRef.current;
        if (!raycaster || !tools) return null;
        raycaster.set(ray.origin, ray.direction);
        return tools.pick(raycaster)?.key ?? null;
      },
      nearestKey: (point, maxDistance) => {
        const tools = modelToolsRef.current;
        if (!tools) return null;
        const { box } = gestureScratch;
        let nearest: string | null = null;
        let nearestDistance = maxDistance;
        for (const child of assetsGroupRef.current?.children ?? []) {
          const key = tools.keyOf(child);
          if (!key || box.setFromObject(child).isEmpty()) continue;
          // Distance to the model's box, not its origin: an origin can sit
          // anywhere, and a hand inside a large model is 0 from it.
          const distance = box.distanceToPoint(point);
          if (distance <= nearestDistance) {
            nearest = key;
            nearestDistance = distance;
          }
        }
        return nearest;
      },
      scaleOf: (key) => localModelRef.current.scale[key] ?? 1,
      yawOf: (key) => localModelRef.current.yaw[key] ?? 0,
      explodeOf: (key) => modelToolsRef.current?.explodeAmount(key) ?? 0,
      setScale: setLocalModelScale,
      setYaw: setLocalModelYaw,
      setExplode: setLocalModelExplode,
    };
  }, [setLocalModelScale, setLocalModelYaw, setLocalModelExplode]);

  const applyModelReset = useCallback(() => {
    setModelExplode(0);
    setModelIsolated(false);
    setModelClip(null);
    setModelSelectedPartId(null);
    setModelSelectedPartName(null);
    modelToolsRef.current?.reset(rendererRef.current);
    // reset() put every part out; the tags go with the glow.
    partLabelTextRef.current.clear();
    syncPartLabels();
    setModelHighlightCount(0);
    // The teacher's own sizes and turns go too: Reset means as the lesson set it up.
    localModelRef.current = emptyLocalOverrides();
    for (const child of assetsGroupRef.current?.children ?? []) {
      const key = modelToolsRef.current?.keyOf(child);
      if (key) applyAssetOverrides(child, localModelRef.current, key);
    }
    refreshLocalModelUi();
    publishModelPatch(
      {
        exploded: 0,
        isolated: false,
        clip: null,
        selected_part_id: null,
        selected_part_name: null,
        highlighted_parts: null,
        // What tells students to drop their own explodes, sizes and turns as
        // well: `exploded: 0` alone cannot say "yours too" (modelPrecedence).
        reset_at: Date.now(),
      },
      true
    );
  }, [publishModelPatch, refreshLocalModelUi, syncPartLabels]);

  /**
   * Follow the teacher's model state.
   *
   * Applied by everyone who is not the host, so an explode, isolate or section
   * the teacher performs happens on every student's copy of the model. Gated on
   * the scene key: a student on a different lesson must not inherit it.
   */
  const teacherModelState = classroom.teacherContentState;

  /**
   * Lesson images, and which one the room is looking at.
   *
   * In a class the teacher's choice is the only one that counts, so it is read
   * straight off the synced content state — for THIS scene only. The state
   * outlives a topic change until the host's first publish in the new topic
   * replaces it, and without the scene check everyone would open the next
   * topic onto the last one's image. Outside a class — a teacher previewing, a
   * self-study viewer — there is nobody to follow, so the choice is local.
   */
  const lessonImages = useMemo<ActiveLessonImage[]>(
    () => (Array.isArray((lessonData as any)?.images) ? (lessonData as any).images : []),
    [lessonData]
  );
  const [localImageId, setLocalImageId] = useState<string | null>(null);
  const inClassSession = classroom.isClassHost || classroom.isStudentInSession;
  const activeImageId = inClassSession
    ? scopedActiveImageId(teacherModelState, modelSceneKey)
    : localImageId;

  const selectLessonImage = useCallback(
    (imageId: string | null) => {
      setLocalImageId(imageId);
      if (classroom.isClassHost && modelSceneKey) {
        classroom.publishModelState({ active_image_id: imageId }, modelSceneKey);
      }
    },
    [classroom, modelSceneKey]
  );

  /** Move within the image set, for the in-world panel's prev/next. */
  const stepLessonImage = useCallback(
    (delta: number) => {
      if (lessonImages.length === 0) return;
      const current = lessonImages.findIndex((image) => image.id === activeImageId);
      const from = current >= 0 ? current : 0;
      const next = (from + delta + lessonImages.length) % lessonImages.length;
      selectLessonImage(lessonImages[next].id);
    },
    [lessonImages, activeImageId, selectLessonImage]
  );

  const activeImage = useMemo(
    () => lessonImages.find((image) => image.id === activeImageId) ?? null,
    [lessonImages, activeImageId]
  );

  /*
    An image stays up until the teacher closes it, or the topic changes (the
    swap below clears it).

    It used to be taken down on every phase change, because the panel gave the
    whole board to the image and a lesson moving on underneath it looked like it
    had stopped loading. The board no longer gives itself up: on a flat screen
    the image is a card beside the scene and the panel keeps the script and
    quiz, and in a headset the quiz takes the panel back (see lessonUiState).
    Clearing it would now only undo a teacher who wanted the diagram up while
    the class answered questions about it.
  */

  /*
    Follow the teacher's model state — only what the teacher actually changed.

    This used to re-apply the whole state on every publish, including one that
    only changed the image on screen, so a student's own explode snapped back
    the moment the teacher showed a picture. lib/classroom/modelPrecedence
    decides which fields changed and which of the student's overrides that
    clears (a teacher explode clears only the assets it targets; a Reset clears
    them all); the student's surviving overrides are then put back on top.

    Also runs when the model tools re-collect (modelToolsVersion): models that
    finished loading after the last publish need every teacher value, and the
    student's overrides, applied to them.
  */
  useEffect(() => {
    if (classroom.isClassHost || !teacherModelState || !modelToolsRef.current) return;
    if (teacherModelState.scene_key && teacherModelState.scene_key !== modelSceneKey) return;
    const previous = appliedTeacherStateRef.current;
    const recollected = appliedToolsVersionRef.current !== modelToolsVersion;
    // Every snapshot is a new object; sync_id says whether it is a new publish.
    if (
      !recollected &&
      previous &&
      previous.sync_id === teacherModelState.sync_id &&
      previous.scene_key === teacherModelState.scene_key
    ) {
      return;
    }
    appliedTeacherStateRef.current = teacherModelState;
    appliedToolsVersionRef.current = modelToolsVersion;

    const tools = modelToolsRef.current;
    const exploded =
      typeof teacherModelState.exploded === 'number'
        ? teacherModelState.exploded
        : teacherModelState.exploded
          ? 1
          : 0;
    const changed = changedTeacherFields(previous, teacherModelState);
    // Freshly collected models need everything. Clearing still goes by what the
    // teacher changed, or every late-loading asset would wipe the student's
    // overrides (see changedTeacherFields).
    const toApply = recollected ? changedTeacherFields(null, teacherModelState) : changed;
    const selectedKey = teacherModelState.selected_asset_key ?? null;
    // The target first: explode and section act on the selected asset, so
    // applying them before the selection would work on the wrong model.
    if (toApply.has('selected_asset_key')) tools.select(selectedKey);
    // What the teacher's explode moves is the tools' actual selection, not
    // selected_asset_key: until the teacher picks an asset that field is
    // absent and collect() leaves both sides on the first model. Clearing by
    // the field would drop overrides on assets the teacher never touched and
    // leave them apart with nothing left for Reset my view to reassemble.
    const target = tools.selectedKey();
    const teacherTargets = target ? [target] : tools.list().map((entry) => entry.key);
    const overrides = clearForTeacher(localModelRef.current, changed, teacherTargets);
    const overridesCleared = overrides !== localModelRef.current;
    localModelRef.current = overrides;
    // A gesture in progress holds values anchored on the old state; left alone
    // it would write them straight back over what the teacher just set.
    if (overridesCleared) modelGestureRef.current = { twoGrip: null, twoHand: null, fists: null };

    // A Reset puts every model back together, not just the teacher's target:
    // a student may have pulled apart one the teacher never touched.
    if (changed.has('reset_at')) {
      for (const entry of tools.list()) tools.explodeAsset(entry.key, 0);
    }
    if (toApply.has('exploded')) tools.explode(exploded);
    if (toApply.has('isolated') || toApply.has('selected_part_id')) {
      tools.isolate(teacherModelState.selected_part_id ?? null, teacherModelState.isolated === true);
    }
    if (toApply.has('clip')) {
      tools.clip(
        teacherModelState.clip?.axis ?? null,
        teacherModelState.clip?.offset ?? 0,
        rendererRef.current
      );
    }
    if (toApply.has('highlighted_parts')) {
      const parts = Array.isArray(teacherModelState.highlighted_parts)
        ? teacherModelState.highlighted_parts
        : [];
      partLabelTextRef.current = new Map(
        parts
          .filter((part) => part?.label)
          .map((part): [string, string] => [part.part_id, part.label as string])
      );
      tools.highlight(parts.map((part) => ({ partId: part?.part_id, color: part?.color })));
      setModelHighlightCount(tools.highlighted().length);
    }
    // After a re-collect too: the tags hold meshes, and newly loaded models
    // bring parts a waiting highlight can now hang its tag on.
    if (toApply.has('highlighted_parts') || recollected) syncPartLabels();
    // The student's own changes that survived go back on top.
    for (const child of assetsGroupRef.current?.children ?? []) {
      const key = tools.keyOf(child);
      if (!key) continue;
      const amount = overrides.explode[key];
      if (amount !== undefined) tools.explodeAsset(key, amount);
      if (overridesCleared) applyAssetOverrides(child, overrides, key);
    }
    if (overridesCleared) refreshLocalModelUi();
    // Mirror into local state so the student's own UI reflects reality.
    setModelExplode(exploded);
    setModelIsolated(teacherModelState.isolated === true);
    setModelClip(teacherModelState.clip ?? null);
    setModelSelectedAssetKey(tools.selectedKey());
    setModelPartCount(tools.partCount());
    setModelSelectedPartId(teacherModelState.selected_part_id ?? null);
    setModelSelectedPartName(teacherModelState.selected_part_name ?? null);
  }, [classroom.isClassHost, teacherModelState, modelSceneKey, modelToolsVersion, refreshLocalModelUi, syncPartLabels]);

  /*
    My view: the dock version of the gestures, for anyone not driving a class —
    a student in one, or a viewer on their own. Reads the overrides afresh on
    each render; the setters bump localModelVersion so there is one.
  */
  const resetMyView = useCallback(() => {
    const tools = modelToolsRef.current;
    const previous = localModelRef.current;
    localModelRef.current = emptyLocalOverrides();
    for (const child of assetsGroupRef.current?.children ?? []) {
      const key = tools?.keyOf(child);
      if (!key) continue;
      if (key in previous.explode) tools!.explodeAsset(key, 0);
      applyAssetOverrides(child, localModelRef.current, key);
    }
    // In a class the models go back to the teacher's state, not to assembled:
    // re-running the student-apply effect in full puts it back.
    setModelToolsVersion((version) => version + 1);
    setLocalModelVersion((version) => version + 1);
  }, []);

  const myViewKey = modelAssets.some((asset) => asset.key === myViewAssetKey)
    ? myViewAssetKey
    : (modelAssets[0]?.key ?? null);
  const studentModel = useMemo(() => {
    if (classroom.isClassHost || !myViewKey) return undefined;
    const scale = localModelRef.current.scale[myViewKey] ?? 1;
    return {
      assets: modelAssets,
      selectedKey: myViewKey,
      onSelect: setMyViewAssetKey,
      explode: modelToolsRef.current?.explodeAmount(myViewKey) ?? 0,
      canExplode: (modelAssets.find((asset) => asset.key === myViewKey)?.partCount ?? 0) >= 2,
      onExplodeChange: (t: number) => {
        modelGesturesRef.current.setExplode(myViewKey, t);
        // A slider has to follow the thumb now, not after the gesture throttle.
        setLocalModelVersion((version) => version + 1);
      },
      scale,
      canShrink: scale > STUDENT_SCALE_LIMITS.min,
      canGrow: scale < STUDENT_SCALE_LIMITS.max,
      onScaleStep: (direction: 1 | -1) => {
        modelGesturesRef.current.setScale(myViewKey, scale * (direction > 0 ? 1.25 : 0.8));
        setLocalModelVersion((version) => version + 1);
      },
      onReset: resetMyView,
    };
    // localModelVersion and modelToolsVersion stand in for the refs read above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroom.isClassHost, myViewKey, modelAssets, localModelVersion, modelToolsVersion, modelExplode, resetMyView]);

  /*
    Swapping topics without leaving immersive mode.

    A chapter is several topics, and launching one used to navigate — which
    remounts the player, disposes the renderer and ends the WebXR session. The
    class left the headset between every topic.

    `launch_id` already changes on every launch and is already what
    ClassLaunchRouter dedupes on, so it doubles as the swap signal with no new
    session field. The first value is recorded rather than acted on: that is the
    lesson already loaded, and swapping to it would reload the topic the student
    is in the middle of.
  */
  const launchedLesson =
    (classroom.isClassHost ? classroom.activeSession : classroom.joinedSession)?.launched_lesson ??
    null;
  const launchSignal = launchedLesson?.launch_id ?? null;
  const appliedLaunchRef = useRef<string | null>(null);
  const swappingRef = useRef(false);

  useEffect(() => {
    if (!launchSignal) return;
    if (appliedLaunchRef.current === null) {
      // First sighting: this is the lesson already on screen.
      appliedLaunchRef.current = launchSignal;
      return;
    }
    if (appliedLaunchRef.current === launchSignal || swappingRef.current) return;
    /*
      "Bring everyone in" (forceStudentsToLesson) re-issues the SAME launch
      under a fresh `force_` id so ClassLaunchRouter pulls in anyone who has
      wandered off. A player already on that topic has nothing to fetch — but it
      used to reload it anyway, handing in every half-finished quiz and starting
      the class over. Someone on a different topic still swaps.
    */
    if (launchSignal.startsWith('force_') && launchedLesson?.topic_id === currentTopicIdRef.current) {
      appliedLaunchRef.current = launchSignal;
      return;
    }
    appliedLaunchRef.current = launchSignal;
    swappingRef.current = true;
    // The scene being left, for taking its image down once the swap commits.
    const outgoingSceneKey = modelSceneKey;

    (async () => {
      try {
        addDebug(`Swapping to a new topic (launch ${launchSignal})...`);

        /*
          Build the new lesson BEFORE tearing the old one down.

          This used to read sessionStorage, which two different subscribers were
          racing to write: the player reacted to the session snapshot
          synchronously while ClassLaunchRouter only wrote after fetching a
          bundle, so the player reliably read the topic it was leaving. And the
          router serves students only, so a teacher was never written to at all
          and simply reloaded the topic they had just finished.

          Fetching here removes both problems, and doing it first means a lesson
          that fails to load leaves the current one standing rather than an empty
          room.
        */
        const nextLesson = await buildActiveLesson(launchedLesson!);
        if (!nextLesson) {
          addDebug('The next topic could not be loaded; staying on the current one.');
          toast.error('The next topic could not be loaded.');
          return;
        }

        // The finished topic's result goes to the dashboards BEFORE its state is
        // cleared. A student mid-quiz is pulled forward with the class, so their
        // partial answers are submitted here rather than lost — see
        // submitOutgoingTopic for how an unfinished attempt is marked.
        await submitOutgoingTopicRef.current();

        const released = disposeLessonContent({
          scene: sceneRef.current,
          assetsGroup: assetsGroupRef.current,
          assetRefs: assetRefs.current,
          mixers: animationMixersRef.current,
          modelTools: modelToolsRef.current,
          partLabels: partLabelsRef.current,
          inkLayer: inkLayerRef.current,
          narration: narrationRef.current,
        });
        modelToolsRef.current = null;
        // This viewer's own explodes, sizes and turns belonged to those models,
        // and the teacher state last applied belonged to that scene.
        localModelRef.current = emptyLocalOverrides();
        appliedTeacherStateRef.current = null;
        modelGestureRef.current = { twoGrip: null, twoHand: null, fists: null };
        addDebug(`Released ${released} asset group(s) from the previous topic`);

        // Back to the state a freshly opened lesson starts in. Anything left
        // behind here shows up as the previous topic bleeding into the next one.
        setMeshyAssets([]);
        setModelAssets([]);
        setModelSelectedAssetKey(null);
        setModelSelectedPartId(null);
        setModelSelectedPartName(null);
        setModelPartCount(0);
        setModelExplode(0);
        setModelIsolated(false);
        setModelClip(null);
        partLabelTextRef.current = new Map();
        setModelHighlightCount(0);
        /*
          The image goes with the topic it was shown for. The scene check in
          scopedActiveImageId hides it as soon as the new topic's key is in
          place, but the stored id would come back on a relaunch of this same
          topic (the key does not change), or on a return to it before the
          teacher had published anything in between. Taking it down in the
          state itself leaves nothing to come back.

          Only when one is actually up in that scene, though. A stored state
          from an older scene makes the publish a whole-object replace: an extra
          write per swap, and a new sync_id that sends students still on the
          outgoing topic through the model-apply effect again for nothing.
        */
        setLocalImageId(null);
        if (
          classroomRef.current.isClassHost &&
          outgoingSceneKey &&
          scopedActiveImageId(classroomRef.current.teacherContentState, outgoingSceneKey)
        ) {
          classroomRef.current.publishModelState({ active_image_id: null }, outgoingSceneKey);
        }
        setAssetsLoaded(0);
        setMcqData([]);
        // Not known until the new topic's questions load, even when it is the
        // same topic being relaunched.
        setMcqLoadedTopicId(null);
        setTtsData([]);
        setCurrentMcqIndex(0);
        setCurrentTtsIndex(0);
        setSelectedMcqOption(null);
        setMcqAnswered(false);
        setMcqScore(0);
        mcqAnswerHistoryRef.current = [];
        quizHandedInRef.current = false;
        setLessonPhase('idle');
        setLessonStarted(false);
        setSkyboxUrl(null);
        setLoadingState('loading');
        setLoadingMessage('Loading the next topic...');

        // A new sitting: a new lesson_launches record and a new clock.
        launchIdRef.current = null;
        lessonStartTimeRef.current = null;
        launchClosedRef.current = false;

        // Kept in sessionStorage so a reload mid-lesson recovers the topic the
        // class is actually on, but nothing here depends on reading it back.
        try {
          sessionStorage.setItem('activeLesson', JSON.stringify(nextLesson));
        } catch {
          /* a full quota must not stop the lesson */
        }
        setLessonData(nextLesson as any);
        addDebug(`Now teaching: ${nextLesson.topic.topic_name}`);
      } catch (err: any) {
        console.error('[XRLessonPlayerV3] Topic swap failed:', err);
        addDebug(`Topic swap failed: ${err?.message || err}`);
      } finally {
        swappingRef.current = false;
      }
    })();
  }, [launchSignal, launchedLesson, addDebug, modelSceneKey]);

  /*
    Teaching a chapter, not a topic.

    The lesson payload in sessionStorage carries the one topic being taught, so
    the chapter's running order has to be read separately. Host only: a student
    has no use for it and no reason to spend the read.
  */
  const [chapterTopics, setChapterTopics] = useState<Array<{ id: string; name: string; chapterId: string }>>([]);
  const chapterIdForSequence = (lessonData as any)?.chapter?.chapter_id ?? '';
  /** Language the lesson is being taught in; the next topic inherits it. */
  const lessonLanguage: string =
    (lessonData as any)?.topic?.language || (lessonData as any)?.language || 'en';

  useEffect(() => {
    if (!classroom.isClassHost || !chapterIdForSequence) {
      setChapterTopics([]);
      // Absence of the control is a decision, so record which one it was.
      addDebug(
        !classroom.isClassHost
          ? 'Not hosting a live class — no next-topic control'
          : 'Lesson has no chapter id — no next-topic control'
      );
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Raw documents, not fetchChapterById: the normalizer drops the approval
        // fields, and the order has to match what /lessons shows.
        const snap = await getDoc(doc(db, 'curriculum_chapters', chapterIdForSequence));
        if (cancelled) return;
        if (!snap.exists()) {
          addDebug(`No chapter found for ${chapterIdForSequence}; cannot offer the next topic`);
          return;
        }
        // A chapter is stored across several documents — usually one topic
        // each — so read every document of it, as /lessons groups them.
        // Reading only this one found a single topic and hid Next.
        let docs = [{ id: snap.id, data: snap.data() }];
        const group = chapterGroupOf(snap.data());
        if (group) {
          const all = await getDocs(
            query(
              collection(db, 'curriculum_chapters'),
              where('curriculum', '==', group.curriculum),
              where('class', '==', group.class),
              where('subject', '==', group.subject),
              where('chapter_number', '==', group.chapter_number)
            )
          );
          if (cancelled) return;
          if (!all.empty) docs = all.docs.map((d) => ({ id: d.id, data: d.data() }));
        }
        const topics = sequenceTopics(docs).map(({ id, name, chapterId }) => ({ id, name, chapterId }));
        setChapterTopics(topics);
        addDebug(`Chapter running order: ${topics.length} topic(s)`);
      } catch (err) {
        console.error('[XRLessonPlayerV3] Could not read the chapter running order:', err);
        addDebug(`Could not read the chapter running order: ${err}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classroom.isClassHost, chapterIdForSequence, addDebug]);

  /**
   * May the class move on from this topic yet?
   *
   * The class moves on together: Next waits until every student has handed in
   * this topic's quiz. It is a gate, not a lock — going early asks the teacher
   * to confirm and names who is still working (ForceAdvanceDialog on a screen,
   * two presses in a headset), and those students' answers so far are handed in.
   * What counts as "handed in", and why `phase === 'completed'` no longer does,
   * is in lib/classroom/quizGate.
   *
   * Whether the topic has a quiz is only known once its questions have loaded;
   * until then it counts as having one, so a quick second press of Next cannot
   * skip a topic nobody has seen yet.
   */
  const topicHasQuiz: boolean | 'unknown' =
    mcqLoadedTopicId === currentTopicId ? mcqData.length > 0 : 'unknown';
  const quizGate = useMemo(
    () =>
      evaluateQuizGate({
        progress: classroom.progressList,
        currentTopicId,
        topicHasQuiz,
        // The roster's naming, so the dialog lists students as the roster does.
        nameOf: (p) =>
          resolveStudentDisplayName(null, null, {
            uid: p.student_uid,
            displayName: p.display_name,
            email: p.email,
          }),
      }),
    [classroom.progressList, currentTopicId, topicHasQuiz]
  );

  /**
   * Phases the dock may send the class to.
   *
   * This player always narrates the intro, lesson and summary — with a stock
   * line when a script is missing — so those stay. Quiz is offered only when
   * the topic has questions: sending the class to a quiz with none left every
   * student on an empty board with no way to finish it.
   */
  const availablePhases = useMemo(
    () => ['intro', 'explanation', 'outro', ...(mcqData.length > 0 ? ['quiz'] : [])],
    [mcqData.length]
  );

  /** The topic after the one being taught, or null at the end of the chapter. */
  const nextTopic = useMemo(() => {
    const currentId = String((lessonData as any)?.topic?.topic_id ?? '');
    if (!currentId || chapterTopics.length === 0) return null;
    const index = chapterTopics.findIndex((t) => t.id === currentId);
    if (index === -1 || index + 1 >= chapterTopics.length) return null;
    return chapterTopics[index + 1];
  }, [chapterTopics, lessonData]);

  /** Topics taught in this player instance, for the picker's "Done" marks. */
  const [taughtTopicIds, setTaughtTopicIds] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!classroom.isClassHost || !currentTopicId) return;
    setTaughtTopicIds((prev) => (prev.has(currentTopicId) ? prev : new Set(prev).add(currentTopicId)));
  }, [classroom.isClassHost, currentTopicId]);

  /** Topics with graded quiz results in this session, whoever launched them. */
  const [scoredTopicIds, setScoredTopicIds] = useState<Set<string>>(() => new Set());
  const hostSessionForResults = classroom.isClassHost ? classroom.hostSessionId : null;
  const hostSchoolId = classroom.activeSession?.school_id ?? null;
  const hostClassId = classroom.activeSession?.class_id ?? null;
  useEffect(() => {
    if (!hostSessionForResults || !hostSchoolId || !hostClassId) {
      setScoredTopicIds(new Set());
      return;
    }
    return subscribeSessionResults(
      { id: hostSessionForResults, school_id: hostSchoolId, class_id: hostClassId },
      (rows) => setScoredTopicIds(new Set(rows.map((r) => r.topicId).filter(Boolean))),
      // Only the "Done" marks depend on this; the picker still works without it.
      (err) => addDebug(`Could not read this session's results: ${err.message}`)
    );
  }, [hostSessionForResults, hostSchoolId, hostClassId, addDebug]);

  const topicMenu = useMemo(
    () =>
      chapterTopics.map((t) => ({
        ...t,
        current: t.id === currentTopicId,
        done: t.id !== currentTopicId && (taughtTopicIds.has(t.id) || scoredTopicIds.has(t.id)),
      })),
    [chapterTopics, currentTopicId, taughtTopicIds, scoredTopicIds]
  );

  /**
   * Move the class on to a topic in this chapter.
   *
   * Writes a new `launched_lesson`, which every player in the class — including
   * this one — is watching. Nobody navigates and nobody leaves the headset; each
   * player swaps its own content in place.
   *
   * Every way a teacher moves the class (dock Next and topic list, the
   * completion screen, the in-headset panel) comes through here, so this is
   * where the quiz gate holds. While it is closed a launch goes ahead only when
   * `force` says the teacher has confirmed; otherwise it returns 'blocked' — and
   * from the screen (`from: 'dom'`) opens the confirmation. The panel asks in
   * its own way, so from there it only reports the block.
   */
  const [topicLaunchPending, setTopicLaunchPending] = useState(false);
  /** A move the gate held, waiting on the teacher's answer in ForceAdvanceDialog. */
  const [pendingAdvance, setPendingAdvance] = useState<{
    topic: { id: string; name: string; chapterId: string };
  } | null>(null);
  const launchTopic = useCallback(async (
    topic: { id: string; name: string; chapterId: string } | null,
    opts: { force?: boolean; from?: 'dom' | 'panel' } = {}
  ): Promise<boolean | 'blocked'> => {
    const sessionId = classroom.hostSessionId;
    const chapter = (lessonData as any)?.chapter;
    if (!sessionId || !topic || !chapter?.chapter_id || !user?.uid) return false;
    if (topicLaunchPending) return false;

    const overridingGate = !quizGate.open;
    if (overridingGate && !opts.force) {
      addDebug(`Holding "${topic.name}": ${quizGate.done}/${quizGate.total} have finished the quiz`);
      if ((opts.from ?? 'dom') === 'dom') setPendingAdvance({ topic });
      return 'blocked';
    }

    addDebug(`Launching topic: ${topic.name}${overridingGate ? ' (forced past the quiz gate)' : ''}`);
    // Exactly the fields LaunchedLesson defines. Extras were being sent behind an
    // `as any`, which is the sort of thing that passes a build and then fails a
    // Firestore rule in front of a class.
    const payload: LaunchedLesson = {
      // The topic's own document: a chapter's topics usually live in separate ones.
      chapter_id: topic.chapterId,
      topic_id: topic.id,
      curriculum: chapter.curriculum ?? '',
      class_name: String(chapter.class_name ?? ''),
      subject: chapter.subject ?? '',
      lesson_type: 'curriculum',
      lang: lessonLanguage,
      title: topic.name,
      // Keep the class in the player it is already sitting in.
      player: 'xr_v3',
    };
    // Recorded only when the gate was actually closed, so the session shows
    // which moves left students behind rather than every confirmed press.
    if (overridingGate) payload.forced = true;
    setTopicLaunchPending(true);
    try {
      const ok = await launchLesson(sessionId, user.uid, payload);
      if (!ok) {
        addDebug('The topic could not be launched.');
        toast.error('Could not launch the topic.');
      }
      return ok;
    } finally {
      setTopicLaunchPending(false);
    }
  }, [
    classroom.hostSessionId,
    lessonData,
    user?.uid,
    lessonLanguage,
    addDebug,
    topicLaunchPending,
    quizGate.open,
    quizGate.done,
    quizGate.total,
  ]);

  const advanceToNextTopic = useCallback(() => launchTopic(nextTopic), [launchTopic, nextTopic]);

  // The confirmation belongs to the screen it was asked on and the topic it
  // was about: entering VR, or the class moving on some other way, drops it.
  useEffect(() => {
    setPendingAdvance(null);
  }, [isPresentingXR, currentTopicId]);

  /**
   * In a headset there is no dialog to confirm in, so while the gate is closed
   * the completed panel's Next takes two presses, as End class does: the first
   * arms it for 4 s and shows how many have finished, the second moves on.
   */
  const [nextTopicArmed, setNextTopicArmed] = useState(false);
  const nextTopicArmTimerRef = useRef<number | null>(null);
  /** When the arming press landed; the 'nextTopic' case uses it to spot a double-fire. */
  const nextTopicArmedAtRef = useRef(0);
  const armNextTopic = useCallback((armed: boolean) => {
    if (nextTopicArmTimerRef.current !== null) window.clearTimeout(nextTopicArmTimerRef.current);
    nextTopicArmTimerRef.current = null;
    setNextTopicArmed(armed);
    if (!armed) return;
    nextTopicArmedAtRef.current = performance.now();
    // Reset through the ref so an old timer cannot disarm a newer press early.
    nextTopicArmTimerRef.current = window.setTimeout(() => {
      nextTopicArmTimerRef.current = null;
      setNextTopicArmed(false);
    }, 4000);
  }, []);

  /** True only when the current topic is known to be the chapter's last. */
  const isLastTopicInChapter =
    chapterTopics.length > 1 && chapterTopics[chapterTopics.length - 1].id === currentTopicId;

  const annotationSessionId = classroom.hostSessionId;

  const publishStroke = useCallback(
    (stroke: AnnotationStroke) => {
      if (!annotationSessionId || !classroom.isClassHost) return;
      const next = appendStroke(
        classroom.activeSession?.teacher_annotations ?? null,
        stroke,
        MAX_INK_STROKES
      );
      void publishAnnotations(annotationSessionId, next);
    },
    [annotationSessionId, classroom.isClassHost, classroom.activeSession?.teacher_annotations]
  );

  /**
   * Mark a point on a 3D model.
   *
   * Stored in the MODEL's local space, not the world's, so the mark stays on the
   * part it was placed on when the model is moved, rotated or exploded. That is
   * the whole reason model marks exist separately from the sphere-anchored ink:
   * ink is painted on the sky, and would slide off a model the moment it moved.
   */
  const addModelMark = useCallback(
    (model: THREE.Object3D, mesh: THREE.Mesh, raycaster: THREE.Raycaster) => {
      if (!annotationSessionId || !classroom.isClassHost) return;
      const hit = raycaster.intersectObject(mesh, false)[0];
      if (!hit) return;

      const local = model.worldToLocal(hit.point.clone());
      const current = (classroom.activeSession?.teacher_annotations ?? null) as TeacherAnnotations | null;
      const mark: AnnotationModelMark = {
        id: `m_${Date.now()}_${Math.round(performance.now() % 1000)}`,
        asset_id: String(model.name || model.uuid),
        x: local.x,
        y: local.y,
        z: local.z,
        color: markerColorRef.current,
        created_ms: annotationNow(),
        ttl_ms: STROKE_TTL_MS,
      };

      void publishAnnotations(annotationSessionId, {
        strokes: current?.strokes ?? [],
        laser: current?.laser ?? null,
        // Capped for the same reason strokes are: an unbounded array in a
        // document every student reads gets expensive quickly.
        model_marks: [...(current?.model_marks ?? []), mark].slice(-MAX_INK_STROKES),
        sync_id: Date.now(),
        cleared_at: current?.cleared_at ?? 0,
      });

      // The tap also selects the part it landed on, which is what Isolate acts on.
      if (mesh.name) {
        setModelSelectedPartName(mesh.name);
        publishModelPatch({ selected_part_name: mesh.name }, true);
      }
    },
    [annotationSessionId, classroom.isClassHost, classroom.activeSession?.teacher_annotations, publishModelPatch]
  );

  useEffect(() => {
    addModelMarkRef.current = addModelMark;
  }, [addModelMark]);

  // Pointer drawing. Bound to the canvas so it never competes with the bars.
  useEffect(() => {
    const canvas = rendererRef.current?.domElement;
    if (!canvas || !markerActive || !classroom.isClassHost) return;

    const toSphere = (event: PointerEvent): AnnotationPoint | null => {
      const camera = cameraRef.current;
      const ink = inkLayerRef.current;
      if (!camera || !ink) return null;
      const rect = canvas.getBoundingClientRect();
      const point = ink.pointerToSphere(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
        camera
      );
      return point ? { a: point.a, v: point.v } : null;
    };

    const onDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      const point = toSphere(event);
      if (!point) return;
      canvas.setPointerCapture(event.pointerId);
      activeStrokeRef.current = {
        id: `s_${Date.now()}_${Math.round(performance.now() % 1000)}`,
        mode: 'laser',
        color: markerColorRef.current,
        width: 6,
        points: [point],
        created_ms: annotationNow(),
        ttl_ms: STROKE_TTL_MS,
      };
      inkLayerRef.current?.setLocalStroke(activeStrokeRef.current);
    };

    const onMove = (event: PointerEvent) => {
      const stroke = activeStrokeRef.current;
      if (!stroke) return;
      const point = toSphere(event);
      if (!point) return;
      stroke.points = capPoints([...stroke.points, point]);
      inkLayerRef.current?.setLocalStroke({ ...stroke });
    };

    const onUp = (event: PointerEvent) => {
      const stroke = activeStrokeRef.current;
      activeStrokeRef.current = null;
      try {
        canvas.releasePointerCapture(event.pointerId);
      } catch {
        /* capture is best-effort */
      }
      if (!stroke || stroke.points.length < 2) {
        inkLayerRef.current?.setLocalStroke(null);
        return;
      }
      const finished: AnnotationStroke = {
        ...stroke,
        points: simplifyStroke(stroke.points),
        created_ms: annotationNow(),
      };
      inkLayerRef.current?.setLocalStroke(null);
      publishStroke(finished);
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
    };
  }, [markerActive, classroom.isClassHost, isSceneReady, publishStroke]);

  // Everyone renders whatever the session holds — the teacher included, so their
  // own ink is confirmed to have reached the class rather than only drawn locally.
  const liveAnnotations =
    (classroom.isStudentInSession
      ? classroom.joinedSession?.teacher_annotations
      : classroom.activeSession?.teacher_annotations) ?? null;

  useEffect(() => {
    inkLayerRef.current?.setAnnotations(liveAnnotations);
  }, [liveAnnotations]);

  /** Captured so the End-session closure below sees a defined function. */
  const endClassSession = classroom.endSession;

  /** End the class for everyone. Used by the top bar and the topic-complete screen. */
  const handleEndClass =
    classroom.isClassHost && endClassSession
      ? async () => {
          // Two-step, in-app. window.confirm() returns false outright
          // in browsers that suppress dialogs, which would make End
          // silently do nothing.
          if (!endSessionConfirming) {
            setEndSessionConfirming(true);
            window.setTimeout(() => setEndSessionConfirming(false), 4000);
            return;
          }
          setEndSessionConfirming(false);
          const endedSessionId = classroom.hostSessionId;
          const ok = await endClassSession(endedSessionId ?? undefined);
          if (ok) {
            if (endedSessionId) navigate(`/class-session/${endedSessionId}/results`);
            else navigate('/lessons');
          } else {
            toast.error('Could not end the session, it is still live.');
          }
        }
      : undefined;

  // Bridge for effects declared above this point (the render loop, the raycast
  // UI handler) which are created before the hook runs.
  classroomRef.current = {
    blockStudentPhaseControl: classroom.blockStudentPhaseControl,
    markStudentLooking: classroom.markStudentLooking,
    showImmersiveUiForThisViewer: classroom.showImmersiveUiForThisViewer,
    directClassToCurrentView: classroom.directClassToCurrentView,
    classId: classroom.joinedSession?.class_id ?? classroom.activeSession?.class_id ?? null,
    sessionId: classroom.joinedSession?.id ?? null,
    isClassHost: classroom.isClassHost,
    publishModelState: classroom.publishModelState,
    teacherContentState: classroom.teacherContentState,
  };

  // ============================================================================
  // Haptic Feedback Helper
  // ============================================================================
  
  const triggerHapticFeedback = useCallback((controller: THREE.Group) => {
    try {
      // Find which controller this is (0 or 1)
      let controllerIndex = -1;
      if (controller === controller1Ref.current) {
        controllerIndex = 0;
      } else if (controller === controller2Ref.current) {
        controllerIndex = 1;
      }
      
      if (controllerIndex >= 0 && inputSourcesRef.current[controllerIndex]) {
        const inputSource = inputSourcesRef.current[controllerIndex];
        const gamepad = inputSource.gamepad;
        
        if (gamepad?.hapticActuators?.[0]) {
          // Trigger haptic pulse: intensity 0.5, duration 50ms
          gamepad.hapticActuators[0].pulse(0.5, 50);
          console.log(`[HAPTIC] Vibration triggered on controller ${controllerIndex}`);
          addDebug(`[HAPTIC] Controller ${controllerIndex} vibrated`);
        } else {
          console.log(`[HAPTIC] No haptic actuators available on controller ${controllerIndex}`);
        }
      } else {
        // Fallback: try to get from XR session
        if (rendererRef.current?.xr?.getSession()) {
          const session = rendererRef.current.xr.getSession();
          if (session.inputSources && session.inputSources.length > 0) {
            const inputSource = session.inputSources[controllerIndex >= 0 ? controllerIndex : 0];
            if (inputSource?.gamepad?.hapticActuators?.[0]) {
              inputSource.gamepad.hapticActuators[0].pulse(0.5, 50);
              console.log(`[HAPTIC] Vibration triggered via session fallback`);
            }
          }
        }
      }
    } catch (err: any) {
      console.error('[HAPTIC] Error triggering haptic feedback:', err);
    }
  }, [addDebug]);
  
  // ============================================================================
  // START Button Panel (Gate before lesson begins)
  // ============================================================================
  
  const handleLessonStart = useCallback(() => {
    if (lessonStartTimeRef.current === null) lessonStartTimeRef.current = Date.now();
    // Students only — trackLessonLaunch returns null for teachers and guests,
    // so a teacher previewing a lesson still creates no record.
    if (!launchIdRef.current) {
      const chapter = (lessonData as any)?.chapter;
      const topic = (lessonData as any)?.topic;
      if (chapter?.chapter_id && topic?.topic_id) {
        void trackLessonLaunch(
          profile,
          chapter.chapter_id,
          topic.topic_id,
          chapter.curriculum || 'CBSE',
          String(chapter.class_name ?? ''),
          chapter.subject || '',
          'web',
          classroomRef.current.classId
        ).then((id) => {
          if (id) launchIdRef.current = id;
        });
      }
    }
    console.log('[LESSON START] ========================================');
    console.log('[LESSON START] User pressed START button');
    console.log('[LESSON START] Current state before start:', {
      lessonPhase,
      lessonStarted,
      ttsDataCount: ttsData.length,
      mcqDataCount: mcqData.length,
      layoutEngineReady: layoutEngineRef.current?.isReady() || false,
    });
    
    debugXR('Lesson START button pressed');
    addDebug('🚀 Lesson started by user');
    
    // The panel stays; it simply stops drawing the start state.
    
    
    // Mark lesson as started and transition to intro phase
    setLessonStarted(true);
    setLessonPhase('intro');
    
    console.log('[LESSON START] Transitioning to INTRO phase');
    console.log('[LESSON START] TTS data available:', ttsData.map(t => ({ id: t.id, section: t.section })));
    console.log('[LESSON START] MCQ data available:', mcqData.length, 'questions');
    console.log('[LESSON START] ========================================');
    
    // Recompute layout anchor at start
    if (layoutEngineRef.current && cameraRef.current) {
      layoutEngineRef.current.computeAnchor(cameraRef.current);
      debugLayout('Layout anchor recomputed at lesson start');
    }
  }, [addDebug, lessonPhase, lessonStarted, ttsData, mcqData, debugXR, debugUI, debugLayout, loadingState]);
  
  // ============================================================================
  // Change Placement Strategy - MUST be defined BEFORE createStartPanel
  // ============================================================================
  
  
  
  // Auto-play TTS when phase changes (intro/content/outro) - ONLY if lesson has started
  useEffect(() => {
    // Don't auto-play if lesson hasn't started yet
    if (!lessonStarted) return;
    // Under lockstep the teacher owns playback; their Play command clears
    // lastPlayedPhaseRef and re-enables autoplay.
    if (!autoplayEnabled) return;

    if (['intro', 'explanation', 'outro'].includes(lessonPhase) && ttsData.length > 0) {
      // Fire once per phase. mcqData / currentMcqIndex / mcqScore are in this
      // effect's deps, so without the marker a quiz state change restarts the
      // narration from the top mid-phase.
      if (lastPlayedPhaseRef.current === lessonPhase) return;
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        lastPlayedPhaseRef.current = lessonPhase;
        playTTSForPhase(lessonPhase);
      }, 500);
      return () => clearTimeout(timer);
    } else if (lessonPhase === 'quiz') {
      // Auto-pause audio when entering quiz
      narrationRef.current?.pause();
      
      // Comprehensive quiz phase transition logging
      console.log('[QUIZ PHASE] ========================================');
      console.log('[QUIZ PHASE] Entered MCQ phase');
      console.log('[QUIZ PHASE] Total questions:', mcqData.length);
      console.log('[QUIZ PHASE] Current question index:', currentMcqIndex);
      console.log('[QUIZ PHASE] Current score:', mcqScore);
      mcqData.forEach((mcq, idx) => {
        console.log(`[QUIZ PHASE] Q${idx + 1}: correctAnswer=${mcq.correctAnswer}, correctText="${mcq.options?.[mcq.correctAnswer]}"`);
      });
      console.log('[QUIZ PHASE] ========================================');
      
      debugQuiz(`Entered quiz phase with ${mcqData.length} questions`);
    }
  }, [lessonPhase, ttsData, playTTSForPhase, lessonStarted, autoplayEnabled, mcqData, currentMcqIndex, mcqScore, debugQuiz]);
  
  // Mute applies to the narration element, which is recreated per phase.
  useEffect(() => {
    narrationRef.current?.setMuted(isMuted);
  }, [isMuted, ttsState, lessonPhase]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      narrationRef.current?.dispose();
      narrationRef.current = null;
    };
  }, []);
  
  // One narration controller for the life of the player.
  useEffect(() => {
    narrationRef.current = createNarrationController({
      onStateChange: (next) => {
        setTtsState(next);
        setIsAudioPlaying(next === 'playing');
      },
    });
    return () => {
      narrationRef.current?.dispose();
      narrationRef.current = null;
    };
  }, []);

  useEffect(() => {
    mcqDataRef.current = mcqData;
  }, [mcqData]);

  // NOTE: the VR-entry auto-start effect that used to sit here has gone. It
  // raced the main autoplay effect below for the intro phase; both are keyed on
  // lastPlayedPhaseRef, so whichever timer fired second was a no-op at best and
  // a second clip at worst.
  
  
  // ============================================================================
  // Create VR Script Panel UI (3D Billboard)
  // ============================================================================
  
  /** Narration text for a phase, falling back to a friendly default. */
  const getScriptForPhase = useCallback(
    (phase: LessonPhase): string => {
      const topic = (lessonData as any)?.topic;
      if (phase === 'intro') return topic?.avatar_intro || 'Welcome to this lesson. Let us begin!';
      if (phase === 'explanation') return topic?.avatar_explanation || 'This is the explanation phase.';
      if (phase === 'outro') return topic?.avatar_outro || 'Thank you for completing this lesson!';
      return '';
    },
    [lessonData]
  );

  // ============================================================================
  // Lesson panel
  //
  // One 2048x1280 canvas plane rendered by the SHARED renderer in
  // src/lib/lessonUi — the same code that draws the krpano player's immersive
  // panel. This replaces four separate hand-drawn panels (start, script, MCQ,
  // class status) that were much plainer than krpano's and drifted from it.
  //
  // The mesh is built once and only its texture is redrawn, so the throttles
  // the old rebuild-per-change panels needed are gone.
  // ============================================================================

  /** Rebuild the panel texture from the current state and store its hit regions. */
  const redrawLessonPanel = useCallback(() => {
    const panel = lessonPanelRef.current;
    if (!panel) return;
    try {
      lessonPanelRegionsRef.current = drawLessonPanel(panel.ctx, lessonUiStateRef.current, {
        font: lessonPanelFontRef.current,
        animTime: (performance.now() - panel.bornAt) / 1000,
        hoverAction: lessonPanelHoverRef.current,
        // A notch larger than krpano's: this panel is read at 40 degrees across
        // from two metres, where the 28px body text sits at the edge of legible.
        textScale: 1.2,
      });
      panel.texture.needsUpdate = true;
    } catch (err) {
      console.error('[XRLessonPlayerV3] Lesson panel draw failed:', err);
    }
  }, []);

  /** Create the panel mesh once the scene exists. Safe to call repeatedly. */
  const ensureLessonPanel = useCallback(() => {
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    if (!scene || !camera) return null;
    if (lessonPanelRef.current) return lessonPanelRef.current;

    const canvas = document.createElement('canvas');
    canvas.width = PANEL_W;
    canvas.height = PANEL_H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const texture = new THREE.CanvasTexture(canvas);
    // The canvas is drawn in sRGB. Left as the default linear colour space, the
    // renderer's sRGB output encoded it a second time and washed every colour out.
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.generateMipmaps = true;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    try {
      const maxAniso = rendererRef.current?.capabilities?.getMaxAnisotropy?.();
      if (maxAniso) texture.anisotropy = maxAniso;
    } catch {
      /* anisotropy is a nicety */
    }

    /*
      Placed from the one workspace frame, not from a second layout engine.

      LayoutEngine computed its own anchor at its own moment, and the dock's
      system computed another at a different one — so the panel and the models
      could sit on different bearings and the room never read as arranged. The
      panel still sits to the left, as it always has; the difference is that the
      offset is now measured from the same forward the dock uses.
    */
    const panelAnchor = workspaceAnchorRef.current ?? captureAnchor(camera, GROUND_LEVEL);
    workspaceAnchorRef.current = panelAnchor;
    const panelFrame = frameFor(panelAnchor);

    // Sized from the angle it should subtend at its distance — a fixed 2.4m
    // panel spans 62 degrees two metres away, wider than can be read without
    // scanning across it. The 16:10 ratio matches the canvas, so nothing is ever
    // stretched. toneMapped off: this is UI, and ACES filmic curve on it dimmed
    // the whites and muddied the text edges.
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(panelFrame.panel.width, panelFrame.panel.height),
      new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        side: THREE.DoubleSide,
        toneMapped: false,
      })
    );
    mesh.name = 'lessonPanel';
    mesh.userData.isInteractable = true;
    mesh.userData.layer = 'ui';
    mesh.userData.panelType = 'lesson';

    placeLessonPanel(mesh, panelFrame);
    scene.add(mesh);

    lessonPanelRef.current = { mesh, canvas, ctx, texture, bornAt: performance.now() };
    lessonPanelFontRef.current = ensureLessonPanelFont();
    redrawLessonPanel();
    return lessonPanelRef.current;
  }, [redrawLessonPanel, frameFor, placeLessonPanel]);

  /*
    An image finishes decoding long after the state change that asked for it.
    The panel is only redrawn on state changes, so without this the board keeps
    showing "Loading image…" over a picture that is sitting ready in memory.
  */
  useEffect(() => onLessonImageReady(() => redrawLessonPanel()), [redrawLessonPanel]);


  // Ink layer lives as long as the scene does.
  useEffect(() => {
    if (!isSceneReady || !sceneRef.current) return;
    inkLayerRef.current = createInkLayer({
      scene: sceneRef.current,
      getFov: () => cameraRef.current?.fov ?? 90,
    });
    return () => {
      inkLayerRef.current?.dispose();
      inkLayerRef.current = null;
    };
  }, [isSceneReady]);

  // Part tags belong to the room, like the ink: made with the scene, freed with it.
  useEffect(() => {
    if (!isSceneReady || !sceneRef.current) return;
    partLabelsRef.current = createPartLabelLayer({ scene: sceneRef.current });
    syncPartLabels();
    return () => {
      partLabelsRef.current?.dispose();
      partLabelsRef.current = null;
    };
  }, [isSceneReady, syncPartLabels]);

  // Build the panel as soon as the scene is up, and tear it down with the scene.
  useEffect(() => {
    if (!isSceneReady) return;
    ensureLessonPanel();
    return () => {
      const panel = lessonPanelRef.current;
      if (panel && sceneRef.current) sceneRef.current.remove(panel.mesh);
      panel?.texture.dispose();
      lessonPanelRef.current = null;
      lessonPanelRegionsRef.current = [];
    };
  }, [isSceneReady, ensureLessonPanel]);

  /**
   * Describe the lesson to the shared renderer.
   *
   * The panel speaks the session vocabulary directly now, so there is no
   * translation step. The state stays truthful whatever the class is doing;
   * whether a held student SEES it is a visibility question, handled below.
   */
  const currentMcq = lessonPhase === 'quiz' ? mcqData[currentMcqIndex] : undefined;
  const lessonUiState = useMemo<LessonUiState>(() => {
    return {
      ...EMPTY_LESSON_UI_STATE,
      phase: lessonPhase,
      // Deliberately NOT rewritten for a held student. This used to blank the
      // script and force showQuiz off, which meant the quiz was never drawn at
      // all — there were no regions to press, so it read as "the buttons do not
      // work". krpano keeps this state truthful and hides the panel instead;
      // visibility is handled where the mesh is, below.
      script: getScriptForPhase(lessonPhase),
      ttsStatus: ttsState,
      question: currentMcq?.question ?? '',
      options: currentMcq?.options ?? [],
      showQuiz: lessonPhase === 'quiz' && !!currentMcq,
      showResult: mcqAnswered,
      scoreLabel:
        lessonPhase === 'completed' && mcqData.length > 0
          ? `${mcqScore} / ${mcqData.length} correct`
          : '',
      selectedAnswer: selectedMcqOption ?? -1,
      // In krpano this means "waiting for the user to press Continue", and it
      // drives a pulsing prompt. This player auto-advances when narration ends,
      // so it never waits — mapping it to `!lessonStarted` left a student under
      // teacher control staring at that pulse for the whole lesson.
      waitingForUser: false,
      isPlayingAudio: ttsState === 'playing',
      currentMcqIndex,
      totalMcqs: mcqData.length,
      correctAnswer: mcqAnswered ? currentMcq?.correctAnswer ?? -1 : -1,
      explanation: mcqAnswered ? currentMcq?.explanation ?? '' : '',
      controlStudentsEnabled: classroom.controlStudentsEnabled,
      isStudent: classroom.isStudentInSession,
      isHost: classroom.isClassHost,
      /*
        Where the image is drawn depends on where this viewer is.

        On a flat screen the image is the docked card beside the scene
        (LessonImageViewer), so the panel keeps the script and the quiz — drawn
        here too, it would take the whole board for a picture already on screen.
        In a headset the panel is the only place to show it, but the panel is
        also the only place to answer the quiz: while THIS viewer is in the quiz
        the quiz wins, and the image comes back when they leave it. The
        teacher's choice is untouched either way; this is a per-viewer drawing
        decision.
      */
      imageUrl: isPresentingXR && lessonPhase !== 'quiz' ? activeImage?.url ?? '' : '',
      imageCaption: activeImage?.name || activeImage?.description || '',
      imageIndex: activeImage ? lessonImages.findIndex((i) => i.id === activeImage.id) + 1 : 0,
      imageCount: lessonImages.length,
      completedNote: classroom.isStudentInSession
        ? 'Waiting for your teacher to start the next topic…'
        : classroom.isClassHost && isLastTopicInChapter
          ? 'That was the last topic in this chapter.'
          : '',
      nextTopicLabel: classroom.isClassHost ? nextTopic?.name ?? '' : '',
      // The in-headset side of the quiz gate: see armNextTopic.
      nextTopicWaiting:
        classroom.isClassHost && nextTopic && !quizGate.open
          ? `${quizGate.done}/${quizGate.total} finished`
          : '',
      nextTopicArmed: classroom.isClassHost && nextTopicArmed,
    };
  }, [
    lessonPhase,
    lessonStarted,
    activeImage,
    isPresentingXR,
    lessonImages,
    ttsState,
    currentMcq,
    currentMcqIndex,
    mcqAnswered,
    mcqScore,
    mcqData.length,
    selectedMcqOption,
    getScriptForPhase,
    classroom.controlStudentsEnabled,
    classroom.isStudentInSession,
    classroom.isClassHost,
    classroom.showImmersiveUiForThisViewer,
    isLastTopicInChapter,
    nextTopic,
    quizGate.open,
    quizGate.done,
    quizGate.total,
    nextTopicArmed,
  ]);

  useEffect(() => {
    lessonUiStateRef.current = lessonUiState;
    redrawLessonPanel();
  }, [lessonUiState, redrawLessonPanel]);

  // Close the launch record once, when the lesson reaches its end.
  const launchClosedRef = useRef(false);
  useEffect(() => {
    if (lessonPhase !== 'completed' || launchClosedRef.current) return;
    const id = launchIdRef.current;
    if (!id) return;
    launchClosedRef.current = true;
    const durationSeconds = lessonStartTimeRef.current
      ? Math.round((Date.now() - lessonStartTimeRef.current) / 1000)
      : undefined;
    void updateLessonLaunch(id, 'completed', durationSeconds);
  }, [lessonPhase]);

  /**
   * Panel visibility, matching krpano.
   *
   * There, `__showImmersiveUI` is ANDed with `webvr.isenabled`, so a held
   * student loses the in-headset HUD and nothing else. The equivalent here has
   * to keep the flat panel: it is this player's only lesson UI, and hiding it
   * on a laptop would leave a student looking at an empty room. On a flat
   * screen the DOM waiting card already explains the hold.
   */
  useEffect(() => {
    const mesh = lessonPanelRef.current?.mesh;
    if (!mesh) return;
    mesh.visible = classroom.showImmersiveUiForThisViewer || !isPresentingXR;
  }, [classroom.showImmersiveUiForThisViewer, isPresentingXR, lessonUiState]);

  
  
  
  
  // ============================================================================
  // MCQ Interaction Handlers
  // ============================================================================
  
  /**
   * Record a finished quiz in the durable score collection.
   *
   * This player reported quiz results to the live class session but never wrote
   * them to student_scores, so a student's result vanished when the class ended
   * and never reached the reports. The krpano player has always written both;
   * this brings V3 level with it.
   */
  const persistQuizScore = useCallback(
    async (
      correct: number,
      total: number,
      answers: Record<string, number>,
      /** Fewer than `total` when the class moved on before the student finished. */
      attempted?: number
    ) => {
      if (!user || !profile || total <= 0) return;
      const chapter = (lessonData as any)?.chapter;
      const topic = (lessonData as any)?.topic;
      const chapterId = chapter?.chapter_id;
      const topicId = topic?.topic_id;
      if (!chapterId || !topicId) {
        console.warn('[XRLessonPlayerV3] Cannot save quiz score: missing chapter or topic id');
        return;
      }

      try {
        const durationSeconds = lessonStartTimeRef.current
          ? Math.round((Date.now() - lessonStartTimeRef.current) / 1000)
          : undefined;

        const scoreId = await saveQuizScore(
          profile,
          chapterId,
          topicId,
          chapter?.curriculum || 'CBSE',
          String(chapter?.class_name ?? ''),
          chapter?.subject || '',
          { correct, total, percentage: Math.round((correct / total) * 100) },
          answers,
          // Counted by the server, which grades the attempt.
          1,
          durationSeconds,
          launchIdRef.current ?? undefined,
          topic?.learning_objective,
          'web',
          // Attribute to the class the lesson was actually taught in, not the
          // student's first enrolment.
          classroomRef.current.classId,
          attempted,
          {
            questionIds: mcqData.map((m) => m.id).filter(Boolean),
            sessionId: classroomRef.current.sessionId,
            startedAt: lessonStartTimeRef.current ?? undefined,
          }
        );
        if (scoreId) {
          addDebug(`Quiz score saved: ${correct}/${total} (class ${classroomRef.current.classId ?? 'none'})`);
        } else {
          /*
            A result that was not recorded must not look like one that was.

            saveQuizScore declines for four reasons — no profile, not a student,
            no school_id, or a guest account — and used to return a bare null
            that every caller ignored. The marks then failed to reach any
            dashboard with nothing anywhere to explain it.
          */
          const reason = lastQuizScoreRefusal ?? 'unknown reason';
          console.warn('[XRLessonPlayerV3] Quiz score NOT saved:', reason);
          addDebug(`⚠️ Quiz score NOT saved: ${reason}`);
          // Only tell a student their own work went missing. A teacher
          // previewing a lesson is not scored by design and needs no warning.
          if (profile?.role === 'student') {
            toast.error('Your result could not be saved. Please tell your teacher.');
          }
        }
      } catch (error) {
        console.error('[XRLessonPlayerV3] Failed to save quiz score:', error);
      }
    },
    [user, profile, lessonData, mcqData, addDebug]
  );

  /**
   * Hand in whatever the student has done on the topic that is ending.
   *
   * Called just before a swap, and the reason a student is never punished for
   * the class moving on: a finished quiz has already been recorded and is left
   * alone, an untouched one has nothing to record, and a half-answered one is
   * submitted with the answers actually given and marked as cut short.
   */
  const submitOutgoingTopic = useCallback(async () => {
    // Already handed in by handleMCQNext when the last question was answered.
    if (quizHandedInRef.current) return;

    const history = mcqAnswerHistoryRef.current;
    const total = mcqData.length;
    if (history.length === 0 || total === 0) return;

    const correct = history.filter((a) => a.correct).length;
    const answersByQuestion: Record<string, number> = {};
    history.forEach((a) => {
      const id = mcqData[a.questionIndex]?.id ?? String(a.questionIndex);
      answersByQuestion[id] = a.selectedOptionIndex;
    });

    // The live class report as well, so the teacher's roster reflects it.
    // `lessonData` is still the topic being left, so the id is the right one;
    // `complete: false` keeps a cut-short attempt from counting as finished.
    pendingQuizReportRef.current = {
      score: correct,
      total,
      answers: history.map((a) => ({
        question_index: a.questionIndex,
        correct: a.correct,
        selected_option_index: a.selectedOptionIndex,
      })),
      topicId: currentTopicId || null,
      complete: false,
    };

    addDebug(`Handing in a partial attempt: ${correct}/${history.length} of ${total} answered`);
    await persistQuizScore(correct, total, answersByQuestion, history.length);
  }, [currentTopicId, mcqData, persistQuizScore, addDebug]);

  const submitOutgoingTopicRef = useRef<() => Promise<void>>(async () => {});
  useEffect(() => {
    submitOutgoingTopicRef.current = submitOutgoingTopic;
  }, [submitOutgoingTopic]);

  const handleMCQOptionSelect = useCallback((optionIndex: number) => {
    if (mcqAnswered || lessonPhase !== 'quiz' || currentMcqIndex >= mcqData.length) {
      console.log('[MCQ INTERACTION] Selection blocked:', { mcqAnswered, lessonPhase, currentMcqIndex, mcqDataLength: mcqData.length });
      return;
    }
    
    const currentMcq = mcqData[currentMcqIndex];
    // correctAnswer is now already 0-based (converted in fetchMCQData)
    const correctIndex = currentMcq.correctAnswer;
    const isCorrect = optionIndex === correctIndex;
    
    // Comprehensive MCQ interaction debug logging
    console.log('[MCQ INTERACTION] ========================================');
    console.log('[MCQ INTERACTION] Question:', currentMcqIndex + 1, '/', mcqData.length);
    console.log('[MCQ INTERACTION] MCQ ID:', currentMcq.id);
    console.log('[MCQ INTERACTION] Question text:', currentMcq.question?.substring(0, 60) + '...');
    console.log('[MCQ INTERACTION] Options:', currentMcq.options);
    console.log('[MCQ INTERACTION] correct_option_index (0-based):', correctIndex);
    console.log('[MCQ INTERACTION] Correct option text:', currentMcq.options?.[correctIndex] || 'N/A');
    console.log('[MCQ INTERACTION] User selected index:', optionIndex);
    console.log('[MCQ INTERACTION] User selected text:', currentMcq.options?.[optionIndex] || 'N/A');
    console.log('[MCQ INTERACTION] Is Correct:', isCorrect);
    console.log('[MCQ INTERACTION] Current Score before:', mcqScore, '/', mcqData.length);
    console.log('[MCQ INTERACTION] ========================================');
    
    setSelectedMcqOption(optionIndex);
    setMcqAnswered(true);
    mcqAnswerHistoryRef.current = [
      ...mcqAnswerHistoryRef.current,
      { questionIndex: currentMcqIndex, correct: isCorrect, selectedOptionIndex: optionIndex },
    ];

    if (isCorrect) {
      setMcqScore(prev => prev + 1);
      debugQuiz(`✅ CORRECT! Selected: ${optionIndex} (${currentMcq.options?.[optionIndex]}), Correct: ${correctIndex} (${currentMcq.options?.[correctIndex]})`);
    } else {
      debugQuiz(`❌ WRONG! Selected: ${optionIndex} (${currentMcq.options?.[optionIndex]}), Correct: ${correctIndex} (${currentMcq.options?.[correctIndex]})`);
    }
  }, [mcqAnswered, lessonPhase, mcqData, currentMcqIndex, mcqScore, debugQuiz]);
  
  const handleMCQNext = useCallback(() => {
    if (!mcqAnswered) return;

    if (currentMcqIndex < mcqData.length - 1) {
      setCurrentMcqIndex(prev => prev + 1);
      setSelectedMcqOption(null);
      setMcqAnswered(false);
      addDebug(`Moving to question ${currentMcqIndex + 2}`);
    } else {
      // Quiz complete: build quiz payload for teacher analytics (ref already has all answers from handleMCQOptionSelect)
      const history = mcqAnswerHistoryRef.current;
      const score = history.filter((a) => a.correct).length;
      const total = mcqData.length;
      if (total > 0) {
        pendingQuizReportRef.current = {
          score,
          total,
          answers: history.map((a) => ({
            question_index: a.questionIndex,
            correct: a.correct,
            selected_option_index: a.selectedOptionIndex,
          })),
          // What the teacher's quiz gate counts as finished with this topic.
          topicId: currentTopicId || null,
          complete: true,
        };
        // Durable record, separate from the live class report above.
        const answersByQuestion: Record<string, number> = {};
        history.forEach((a) => {
          const id = mcqData[a.questionIndex]?.id ?? String(a.questionIndex);
          answersByQuestion[id] = a.selectedOptionIndex;
        });
        void persistQuizScore(score, total, answersByQuestion);
      }
      quizHandedInRef.current = true;
      setLessonPhase('completed');
      debugQuiz(`Quiz complete! Score: ${score}/${total}`);
    }
  }, [mcqAnswered, currentMcqIndex, mcqData, addDebug, debugQuiz, persistQuizScore, currentTopicId]);

  /**
   * Handle a click on the panel, given the raycast UV.
   *
   * The action vocabulary is shared with krpano, so both players answer the same
   * button names and neither can quietly grow a control the other lacks.
   */
  const handleLessonPanelUv = useCallback(
    (u: number, v: number): boolean => {
      const raw = actionAtUv(lessonPanelRegionsRef.current, u, v);
      const action = parseLessonUiAction(raw);
      if (!action) return false;

      switch (action.kind) {
        case 'phaseGo': {
          if (classroomRef.current.blockStudentPhaseControl('Jumping ahead')) return true;
          const local = action.phase as LessonPhase;
          if (local) {
            stopAudio();
            lastPlayedPhaseRef.current = null;
            setLessonStarted(true);
            setLessonPhase(local);
          }
          return true;
        }
        case 'ttsPlay':
        case 'ttsPause':
          toggleAudio();
          return true;
        case 'replay':
          if (classroomRef.current.blockStudentPhaseControl('Replay')) return true;
          stopAudio();
          lastPlayedPhaseRef.current = null;
          setLessonStarted(true);
          setAutoplayEnabled(true);
          return true;
        case 'skipToQuiz':
          skipToQuiz();
          return true;
        case 'continue':
          // Before the lesson starts, Continue is the START button.
          if (!lessonStarted) {
            handleLessonStart();
            return true;
          }
          skipNext();
          return true;
        case 'mcqSelect':
          handleMCQOptionSelect(action.index);
          return true;
        case 'mcqSubmit':
        case 'mcqNext':
          handleMCQNext();
          return true;
        case 'directClassView':
          void classroomRef.current.directClassToCurrentView();
          return true;
        case 'nextTopic':
          // First press on a closed gate only arms; see armNextTopic.
          if (!quizGate.open && !nextTopicArmed) {
            armNextTopic(true);
            return true;
          }
          /*
            One pinch reaches this handler twice: once from updateHands'
            justPinched, then ~50 ms later from the hand input source's
            selectstart (debounced into handleControllerSelect), and the two
            share no dedupe. By the second, a re-render has swapped in a closure
            where Next is already armed — so without this a single pinch would
            arm AND confirm, forcing the class on and handing in unfinished
            quizzes. A confirm that soon after the arm is that same press.
          */
          if (nextTopicArmed && performance.now() - nextTopicArmedAtRef.current < 400) return true;
          armNextTopic(false);
          void launchTopic(nextTopic, { force: nextTopicArmed, from: 'panel' });
          return true;
        case 'imageNext':
          stepLessonImage(1);
          return true;
        case 'imagePrev':
          stepLessonImage(-1);
          return true;
        case 'imageClose':
          selectLessonImage(null);
          return true;
        case 'model':
          // Model tools are krpano-only for now; the renderer only draws them
          // when modelPartCount says there is separable geometry, which this
          // player reports as 0.
          return true;
        case 'panelGrab':
        case 'panelResize':
          // Handled on press, where a drag can start (pressPanelHandle). A click
          // that still arrives here is the tail of that press: swallow it.
          return true;
        default:
          return false;
      }
    },
    [
      stopAudio,
      toggleAudio,
      skipToQuiz,
      skipNext,
      lessonStarted,
      handleLessonStart,
      handleMCQOptionSelect,
      handleMCQNext,
      stepLessonImage,
      selectLessonImage,
      quizGate.open,
      nextTopicArmed,
      armNextTopic,
      launchTopic,
      nextTopic,
    ]
  );

  // Same reason as classroomRef: the canvas pointer handler and the VR
  // controller ray are wired up in the scene-init effect, long before the
  // lesson data arrives. They read this ref so they always call the CURRENT
  // handler rather than the one that existed when they were created.
  lessonPanelUvRef.current = handleLessonPanelUv;
  
  // ============================================================================
  // Create VR MCQ Quiz Panel UI
  // ============================================================================
  


  
  
  // ============================================================================
  // 3D Model Reset & Focus Functions
  // ============================================================================
  
  
  
  
  // ============================================================================
  // Render
  // ============================================================================
  
  // Error state
  if (loadingState === 'error') {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card rounded-2xl border border-destructive/30 p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/20 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Unable to Load VR Lesson</h2>
          <p className="text-slate-400 text-sm mb-4">{errorMessage}</p>
          <button
            onClick={() => navigate('/lessons')}
            className="flex items-center justify-center gap-2 px-6 py-3 mx-auto
                     text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Lessons
          </button>
        </div>
      </div>
    );
  }
  
  // NOTE: there is deliberately no "VR required" dead end here any more.
  //
  // This used to return early whenever immersive-vr was unsupported, which
  // unmounted the canvas container — so containerRef stayed null, the scene
  // never initialised, and the log filled with "Waiting for container ref...".
  // It also made it impossible for a teacher to drive a V3 class from a desktop,
  // which is where teachers actually are. The scene renders perfectly well flat,
  // and drag-to-look now gives a mouse and touch user a real camera, so a
  // headset is an upgrade rather than a requirement. The banner below says so.

  // PlayerChrome reads these from the player ROOT (inline custom properties do
  // not reach siblings). Without them both bars collapse to zero height and
  // every control on them becomes unclickable.
  const hudMetrics = {
    '--hud-top': `calc(${playerViewport.isCompact ? '3.25rem' : '3.5rem'} + env(safe-area-inset-top, 0px))`,
    '--hud-bottom': `calc(${playerViewport.isCompact ? '3.75rem' : '4rem'} + env(safe-area-inset-bottom, 0px))`,
  } as React.CSSProperties;

  return (
    <div className="fixed inset-0 bg-black" style={hudMetrics}>
      {/* Three.js Canvas Container */}
      <div ref={containerRef} className="w-full h-full" />
      
      {/* Comfort break. Dismissible, never blocking: a student can carry on, but
          the prompt is there, which is the point of the published guidance. */}
      {comfortBreak.due && (
        <div className="pointer-events-none absolute inset-x-0 top-[calc(var(--hud-top)+1rem)] z-gate flex justify-center px-4">
          <div className="pointer-events-auto w-full max-w-sm rounded-2xl border border-amber-300/30 bg-slate-950/90 p-4 text-white shadow-2xl backdrop-blur-xl">
            <p className="text-sm font-semibold">Time for a short break</p>
            <p className="mt-1 text-xs leading-snug text-white/60">
              You have been in the headset for about {comfortBreak.immersedMinutes} minutes.
              Take it off for a moment, look at something far away, then carry on.
            </p>
            <button
              type="button"
              onClick={comfortBreak.acknowledge}
              className="mt-3 w-full rounded-lg border border-white/12 bg-white/[0.06] px-3 py-2 text-xs font-semibold text-white/80 transition hover:bg-white/10"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Flat-mode notice. Informational only: the lesson is fully usable here. */}
      {isVRSupported === false && !classroom.isClassHost && !classroom.isStudentInSession && (
        <div className="pointer-events-none absolute inset-x-0 bottom-6 z-40 flex justify-center px-4">
          <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-border bg-card/85 px-4 py-2 text-xs text-muted-foreground backdrop-blur-sm">
            <Glasses className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span>Drag to look around. Open on a Meta Quest headset for the immersive version.</span>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loadingState === 'loading' && (
        <div className="absolute inset-0 bg-background/90 flex items-center justify-center z-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <p className="text-foreground font-medium">{loadingMessage}</p>
            {lessonData && (
              <p className="text-slate-400 text-sm mt-2">
                {lessonData.topic.topic_name}
              </p>
            )}
          </div>
        </div>
      )}
      
      
      {/* In VR Indicator (shown on 2D screen while in VR) */}
      {loadingState === 'in-vr' && (
        <div className="absolute inset-0 bg-background flex items-center justify-center z-50">
          <div className="text-center">
            <Glasses className="w-16 h-16 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">In VR Mode</h2>
            <p className="text-slate-400">
              Look around in your headset to explore the lesson
            </p>
          </div>
        </div>
      )}
      
      
      
      {/* Waiting for START indicator in VR */}
      {loadingState === 'in-vr' && lessonPhase === 'idle' && !lessonStarted && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-cyan-600/90 backdrop-blur-sm rounded-lg px-6 py-3 border border-cyan-400/50 shadow-lg">
            <div className="flex items-center gap-3">
              <Play className="w-5 h-5 text-foreground animate-pulse" />
              <p className="text-foreground font-medium">
                Point at the START button in VR to begin
              </p>
            </div>
          </div>
        </div>
      )}
      
      
      {/* Lesson Completion Screen */}
      {lessonPhase === 'completed' && (
        <div className="absolute inset-0 bg-background/95 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="max-w-md w-full mx-4 bg-card rounded-2xl 
                        border border-emerald-500/30 p-8 text-center shadow-2xl">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 
                          flex items-center justify-center border border-emerald-500/30">
              <Award className="w-10 h-10 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {classroom.isClassHost && isLastTopicInChapter
                ? 'Chapter Complete!'
                : classroom.isClassHost || classroom.isStudentInSession
                  ? 'Topic Complete!'
                  : 'Lesson Complete!'}
            </h2>
            <p className="text-slate-400 text-sm mb-6">
              {lessonData?.topic.topic_name}
            </p>
            
            {/* Quiz Score Display */}
            {mcqData.length > 0 && (
              <div className="mb-6 p-4 bg-card/50 rounded-xl inline-block">
                <p className="text-xs text-slate-400 mb-1">Quiz Score</p>
                <p className="text-4xl font-bold text-emerald-400">
                  {mcqScore}/{mcqData.length}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {Math.round((mcqScore / mcqData.length) * 100)}% correct
                </p>
              </div>
            )}
            
            {classroom.isClassHost ? (
              /* The teacher moves the class on from here: same session, same
                 player, and every student's headset swaps in place. */
              <div className="flex flex-col items-center gap-3">
                {nextTopic && (
                  <button
                    onClick={() => void advanceToNextTopic()}
                    disabled={topicLaunchPending}
                    className="flex w-full items-center justify-center gap-2 px-6 py-3
                             text-primary-foreground bg-primary rounded-lg font-medium
                             shadow-lg transition-all disabled:opacity-60"
                  >
                    {topicLaunchPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <SkipForward className="w-4 h-4" />
                    )}
                    <span className="truncate">Next: {nextTopic.name}</span>
                    {quizGate.total > 0 && topicHasQuiz !== false && (
                      <span
                        className={`rounded px-1.5 text-xs tabular-nums ${
                          quizGate.open ? 'bg-black/20' : 'bg-amber-400/40 text-amber-50'
                        }`}
                        title={`${quizGate.done} of ${quizGate.total} students have finished the quiz`}
                      >
                        {quizGate.done}/{quizGate.total}
                      </span>
                    )}
                  </button>
                )}
                {topicMenu.length > 1 && (
                  <details className="w-full rounded-lg border border-white/10 bg-black/20 p-2 text-left" open={!nextTopic}>
                    <summary className="cursor-pointer text-xs font-semibold text-white/70">
                      Choose another topic
                    </summary>
                    <div className="mt-2 flex justify-center">
                      <TopicPicker
                        topics={topicMenu}
                        disabled={topicLaunchPending}
                        onPick={(t) => void launchTopic(chapterTopics.find((c) => c.id === t.id) ?? null)}
                      />
                    </div>
                  </details>
                )}
                <div className="flex w-full gap-2">
                  <button
                    onClick={() => navigate('/lessons')}
                    title="The class stays live; you can launch another lesson from the list"
                    className="flex flex-1 items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                             border border-white/15 text-sm text-white/80 hover:bg-white/10"
                  >
                    <Home className="w-4 h-4" />
                    Back to Lessons
                  </button>
                  {handleEndClass && (
                    <button
                      onClick={() => void handleEndClass()}
                      className="flex flex-1 items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                               border border-red-400/40 text-sm text-red-200 hover:bg-red-500/15"
                    >
                      <LogOut className="w-4 h-4" />
                      {endSessionConfirming ? 'Tap again to end' : 'End class'}
                    </button>
                  )}
                </div>
              </div>
            ) : classroom.isStudentInSession ? (
              <div className="flex flex-col items-center gap-3">
                <p className="flex items-center gap-2 text-sm text-slate-300">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Waiting for your teacher to start the next topic…
                </p>
                <button
                  onClick={() => {
                    classroom.leaveSessionAsStudent?.();
                    navigate('/dashboard/student');
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs text-white/60 hover:bg-white/10"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Leave class
                </button>
              </div>
            ) : (
              <button
                onClick={() => navigate('/lessons')}
                className="flex items-center justify-center gap-2 px-6 py-3 mx-auto
                         text-primary-foreground bg-primary 
                         hover:from-cyan-500 hover:to-blue-500 rounded-lg font-medium 
                         shadow-lg transition-all"
              >
                <Home className="w-4 h-4" />
                Back to Lessons
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Audio Controls (shown when ready or in VR) - Now in VR panel */}
      {/* Audio controls are now in the 3D VR script panel */}
      
      
      

      {/* ------------------------------------------------------------------
          Live class. Every one of these components is player-agnostic and is
          already used by the krpano player; only the wiring is new here.
          Hidden while presenting in a headset, where DOM overlays cannot be
          seen and the in-scene panels take over.
          ------------------------------------------------------------------ */}
      {classroom.isClassHost && (
        <LiveClassHostOverlay
          session={classroom.activeSession}
          sessionId={classroom.hostSessionId}
          hostUid={user?.uid ?? null}
          progressList={classroom.progressList}
          sessionCode={classroom.hostSessionCode}
          skyboxUrlOverride={skyboxUrl}
          openDrawer={hostDrawer}
          onDrawerChange={setHostDrawer}
        />
      )}

      {/* Waiting room: the scene is loaded and silent until the teacher starts. */}
      {classroom.isStudentInSession &&
        classroom.controlStudentsEnabled &&
        isSceneReady &&
        !classroom.classStarted && (
          <div className="pointer-events-none absolute inset-x-0 top-4 z-[45] flex justify-center px-4">
            <div className="pointer-events-auto w-full max-w-sm rounded-2xl border border-cyan-400/25 bg-slate-950/85 p-4 text-white shadow-2xl backdrop-blur-xl">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-cyan-400" />
                <p className="text-sm font-semibold">Waiting for your teacher</p>
              </div>
              <p className="mt-1 text-xs leading-snug text-white/55">
                The lesson will begin when your teacher starts the class.
              </p>

              {Array.isArray(classroom.joinedSession?.lobby_roster) &&
                classroom.joinedSession.lobby_roster.length > 0 && (
                  <div className="mt-3 border-t border-white/10 pt-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">
                      In the class ({classroom.joinedSession.lobby_roster.length})
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {classroom.joinedSession.lobby_roster.slice(0, 12).map((member) => (
                        <span
                          key={member.uid}
                          className={`rounded-full border px-2 py-0.5 text-[10px] ${
                            member.ready
                              ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-100'
                              : 'border-white/10 bg-white/[0.05] text-white/50'
                          }`}
                        >
                          {member.name}
                        </span>
                      ))}
                      {classroom.joinedSession.lobby_roster.length > 12 && (
                        <span className="rounded-full border border-white/10 bg-white/[0.05] px-2 py-0.5 text-[10px] text-white/50">
                          +{classroom.joinedSession.lobby_roster.length - 12} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

              <button
                type="button"
                onClick={classroom.toggleHandRaised}
                className={`mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                  classroom.handRaised
                    ? 'border-amber-300/40 bg-amber-400/20 text-amber-100'
                    : 'border-white/12 bg-white/[0.05] text-white/70 hover:bg-white/10'
                }`}
              >
                <Hand className="h-3.5 w-3.5" />
                {classroom.handRaised ? 'Lower hand' : 'Raise hand'}
              </button>
            </div>
          </div>
        )}

      {/* The only chrome. Exit, title, progress, narration controls and the class
          controls all live in these two bars, which is why the seven overlays
          that used to sit on top of them are gone. */}
      {loadingState !== 'in-vr' && (
        <PlayerChrome
          overlays={
            <LessonImageViewer
              images={lessonImages}
              activeImageId={activeImageId}
              onSelect={selectLessonImage}
              isHost={!inClassSession || classroom.isClassHost}
              // Opposite the 3D panel, so the card never covers it.
              side={panelSideOf(resolvedLayout) === 'right' ? 'left' : 'right'}
            />
          }
          topBar={
            <PlayerTopBar
              onExit={() => navigate('/lessons')}
              title={lessonData?.topic?.topic_name || 'Lesson'}
              subtitle={`${lessonData?.chapter?.subject || ''} · ${lessonPhase}`}
              isMuted={isMuted}
              onToggleMute={() => setIsMuted((m) => !m)}
              showChat={false}
              onToggleChat={() => {}}
              chatAvailable={false}
              compact={playerViewport.isCompact}
              isHost={classroom.isClassHost}
              sessionCode={classroom.hostSessionCode}
              liveCount={classroom.rosterCounts.inLesson}
              joinedCount={classroom.rosterCounts.joined}
              classCount={classroom.enrolledCount}
              pendingCount={classroom.pendingJoinCount}
              onCopyCode={async () => {
                if (!classroom.hostSessionCode) return;
                try {
                  await navigator.clipboard.writeText(classroom.hostSessionCode);
                  toast.success('Class code copied');
                } catch {
                  toast.error('Could not copy class code');
                }
              }}
              onOpenApprovals={() =>
                setHostDrawer((d) => (d === 'approvals' ? null : 'approvals'))
              }
              endSessionConfirming={endSessionConfirming}
              onEndSession={handleEndClass}
            />
          }
          bottomBar={
            <PlayerBottomBar
              isHost={classroom.isClassHost}
              compact={playerViewport.isCompact}
              playbackState={classroom.teacherPlayback?.state ?? 'idle'}
              currentPhase={lessonPhase}
              onPlaybackCommand={classroom.handleTeacherPlaybackCommand}
              onLocalPlayToggle={toggleAudio}
              isPlayingAudio={ttsState === 'playing'}
              playbackLocked={classroom.isStudentInSession && classroom.controlStudentsEnabled}
              controlStudentsEnabled={classroom.controlStudentsEnabled}
              onToggleControl={(next) => void classroom.toggleControl(next)}
              classStarted={classroom.classStarted}
              studentUiVisible={classroom.studentUiVisible}
              onToggleStudentUi={(visible) => void classroom.toggleStudentUi(visible)}
              onForceStudentsIn={() => void classroom.forceStudentsIn()}
              canForce={Boolean(
                classroom.activeSession?.launched_lesson || classroom.activeSession?.launched_scene
              )}
              onDirectView={() => void classroom.directClassToCurrentView()}
              liveCount={classroom.rosterCounts.joined}
              onOpenRoster={() => setHostDrawer((d) => (d === 'roster' ? null : 'roster'))}
              raisedHands={classroom.raisedHandCount}
              nextTopicName={nextTopic?.name ?? null}
              chapterHasSequence={chapterTopics.length > 1}
              availablePhases={availablePhases}
              // No count on a topic with no questions: nobody hands anything in
              // there, and "0/2" would read as the class being behind.
              quizProgress={topicHasQuiz === false ? undefined : quizGate}
              onAdvanceTopic={() => void advanceToNextTopic()}
              topics={topicMenu}
              onPickTopic={(t) => void launchTopic(chapterTopics.find((c) => c.id === t.id) ?? null)}
              topicLaunchPending={topicLaunchPending}
              modelPartCount={modelPartCount}
              modelAssets={modelAssets}
              modelSelectedAssetKey={modelSelectedAssetKey}
              onModelSelectAsset={applyModelSelectAsset}
              modelExplode={modelExplode}
              onModelExplodeChange={applyModelExplode}
              modelIsolated={modelIsolated}
              modelSelectedPartName={modelSelectedPartName}
              onToggleModelIsolate={applyModelIsolate}
              modelClip={modelClip}
              onModelClipChange={applyModelClip}
              onModelReset={applyModelReset}
              // The marker (Tools) draws on the scene and marks models; Highlight
              // (3D model) makes a named part glow. One pick mode at a time.
              markerActive={markerActive}
              markerColor={markerColor}
              onToggleMarker={() => {
                setMarkerActive((on) => !on);
                setModelHighlightMode(false);
              }}
              onMarkerColorChange={setMarkerColor}
              modelHighlightMode={modelHighlightMode}
              onToggleModelHighlight={() => {
                setModelHighlightMode((on) => !on);
                setMarkerActive(false);
              }}
              modelHighlightCount={modelHighlightCount}
              onClearModelHighlights={clearModelHighlights}
              lessonImages={lessonImages}
              activeImageId={activeImageId}
              // Only whoever is driving gets a picker; students follow the class.
              onSelectImage={!inClassSession || classroom.isClassHost ? selectLessonImage : undefined}
              studentModel={studentModel}
            />
          }
        />
      )}

      {/* Moving on before the whole class has handed in the quiz asks first.
          A screen only: in a headset the completed panel's Next arms instead. */}
      {classroom.isClassHost && (
        <ForceAdvanceDialog
          open={!!pendingAdvance && !isPresentingXR}
          topicName={pendingAdvance?.topic.name ?? ''}
          gate={quizGate}
          pending={topicLaunchPending}
          onCancel={() => setPendingAdvance(null)}
          onConfirm={() => {
            const topic = pendingAdvance?.topic ?? null;
            setPendingAdvance(null);
            void launchTopic(topic, { force: true });
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// Export with Error Boundary
// ============================================================================

const XRLessonPlayerV3WithBoundary: React.FC = () => {
  return (
    <XRPlayerErrorBoundary>
      <XRLessonPlayerV3 />
    </XRPlayerErrorBoundary>
  );
};

export default XRLessonPlayerV3WithBoundary;
